import React, { useState, useEffect, useMemo, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  onAuthStateChanged, 
  signOut,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  doc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot,
  query,
  writeBatch,
  setDoc,
  getDocs,
  getDoc
} from 'firebase/firestore';
import { 
  LayoutDashboard, 
  UserPlus, 
  Table as TableIcon, 
  FileText, 
  DatabaseBackup, 
  Settings, 
  LogOut, 
  Menu, 
  X, 
  Eye, 
  EyeOff,
  Search,
  Activity,
  Download,
  Upload,
  CheckCircle2, 
  AlertCircle,
  CalendarDays,
  ArrowDownCircle,
  ArrowUpCircle,
  UserCheck,
  UserMinus,
  Trash2,
  Save,
  Clock,
  Edit3,
  FileSpreadsheet,
  Lock,
  Unlock,
  PanelLeftClose,
  PanelLeftOpen,
  UserCog,
  History,
  ShieldCheck,
  Loader2,
  Stethoscope,
  Bed,
  Plus,
  ChevronRight,
  SortAsc,
  SortDesc,
  ShieldAlert,
  CalendarPlus,
  ListChecks,
  CalendarSearch,
  MessageSquareQuote,
  Pin,
  Trash,
  ClipboardList,
  User,
  FileDown,
  ArrowUp,
  ArrowDown,
  UserSearch,
  ChevronLeft,
  Filter,
  MapPin,
  Phone,
  Syringe,
  Info,
  ChevronLeftCircle,
  ChevronRightCircle,
  BookOpen,
  Shuffle,
  Users,
  Briefcase,
  ExternalLink,
  RotateCcw,
  DoorOpen,
  MessageSquarePlus,
  PlusCircle,
  MinusCircle,
  TrendingUp,
  BarChart3,
  Share2,
  Copy,
  RotateCw,
  AlertTriangle,
  Wind,
  KeyRound,
  Percent,
  FileBadge
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// --- 1. CONFIGURATION & CONSTANTS ---
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = typeof __app_id !== 'undefined' ? __app_id : 'icu-bedah-fix';

const DEFAULT_BEDS = [
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15',
  'Isolasi 16', 'Isolasi 17', '18'
];
const DEFAULT_UNIT_CATEGORIES = ['Neuro', 'Bedah', 'Metabolik/Paru', 'HCU', 'Code Stroke'];
const DEFAULT_LOCATIONS = ['IGD', 'OK', 'ICU1', 'HCU', 'Ranap lt 4', 'Ranap lt 5', 'Ranap lt 6', 'Ranap lt 7', 'Ranap lt 8', 'Ranap lt 9', 'Ranap lt 10', 'Ranap lt 11'];
const DEFAULT_EXIT_STATUS = [...DEFAULT_LOCATIONS, 'Meninggal', 'Rujuk', 'Pulpak'];
const DEFAULT_OXYGEN = ['Ventilator', 'NRM', 'RM', 'SM', 'NK', 'T-Piece', 'Room Air'];
const DEFAULT_DPJP = ['dr Sri SpAn', 'dr Erwin SpAn'];
const DEFAULT_RABER = ['dr Bismo SpBs', 'Dr arief dito SpBs'];
const DEFAULT_NURSES = ['Gilang', 'Ozi', 'Hari'];

const REGISTER_COL_DEFS = [
  { id: 'no', label: 'NO', width: '40px' },
  { id: 'patient', label: 'NAMA PASIEN &\nNOMOR MR', width: '150px' },
  { id: 'identity', label: 'TGL LAHIR, USIA\n& JENIS KELAMIN', width: '110px' },
  { id: 'agama', label: 'AGAMA', width: '80px' },
  { id: 'bed', label: 'BED', width: '80px' },
  { id: 'entry', label: 'TANGGAL & JAM\nMASUK ICU', width: '100px' },
  { id: 'origin', label: 'ASAL PASIEN &\nJAMINAN', width: '110px' },
  { id: 'diagnosis', label: 'DIAGNOSA\nMEDIS', width: '180px' },
  { id: 'doctors', label: 'DPJP &\nDOKTER RABER', width: '160px' },
  { id: 'oxygen', label: 'OKSIGEN\n(AWAL / SEKARANG)', width: '130px' },
  { id: 'trach', label: 'TGL\nTRAKHEO', width: '90px' },
  { id: 'procs', label: 'RIWAYAT INTUBASI &\nRIWAYAT EKSTUBASI', width: '160px' },
  { id: 'exit', label: 'TGL & JAM KELUAR\nICU', width: '110px' },
  { id: 'status', label: 'STATUS\nKELUAR', width: '90px' },
  { id: 'action', label: 'AKSI', width: '80px' }
];

const BOOKING_COL_DEFS = [
  { id: 'no', label: 'NO', width: '40px' },
  { id: 'patient', label: 'NAMA PASIEN &\nNOMOR MR', width: '130px' },
  { id: 'plan', label: 'TGL & JAM\nBOOKING', width: '110px' },
  { id: 'origin', label: 'ASAL PASIEN', width: '90px' },
  { id: 'unit', label: 'UNIT', width: '100px' },
  { id: 'diagnosis', label: 'DIAGNOSA', width: '180px' },
  { id: 'doctor', label: 'DOKTER MERAWAT', width: '130px' },
  { id: 'status', label: 'STATUS', width: '110px' },
  { id: 'note', label: 'KETERANGAN', width: '150px' }, 
  { id: 'action', label: 'AKSI', width: '80px' }
];

// --- 2. HELPERS ---
const getCurrentShift = () => {
  const hour = new Date().getHours();
  if (hour >= 7 && hour < 14) return { id: 'pagi', name: 'Pagi', range: '07:00 - 14:00' };
  if (hour >= 14 && hour < 21) return { id: 'siang', name: 'Siang', range: '14:00 - 21:00' };
  return { id: 'malam', name: 'Malam', range: '21:00 - 07:00' };
};

const getShiftTimeRange = (dateStr, shiftId) => {
  const start = new Date(dateStr);
  const end = new Date(dateStr);
  if (shiftId === 'pagi') {
    start.setHours(7, 0, 0, 0);
    end.setHours(14, 0, 0, 0);
  } else if (shiftId === 'siang') {
    start.setHours(14, 0, 0, 0);
    end.setHours(21, 0, 0, 0);
  } else {
    start.setHours(21, 0, 0, 0);
    end.setDate(end.getDate() + 1);
    end.setHours(7, 0, 0, 0);
  }
  return { start, end };
};

const naturalSortBeds = (a, b) => {
  const extract = (s) => {
    const match = s.match(/\d+/);
    return match ? parseInt(match[0], 10) : Infinity;
  };
  const numA = extract(a);
  const numB = extract(b);
  if (numA !== numB) return numA - numB;
  return a.localeCompare(b);
};

const Field = ({ label, type="text", required, value, onChange, disabled, placeholder }) => (
  <div className="space-y-1 text-left">
    <label className="text-[9px] font-black text-slate-500 uppercase ml-1 tracking-widest">{label} {required && "*"}</label>
    <input type={type} required={required} disabled={disabled} placeholder={placeholder} className={`w-full px-4 py-2.5 rounded-xl border border-slate-200 outline-none focus:ring-4 focus:ring-blue-100 transition-all font-bold text-xs ${disabled ? 'bg-slate-100 text-slate-500 shadow-inner' : 'bg-slate-50 text-slate-800'} text-center`} value={value} onChange={e => onChange(e.target.value)} />
  </div>
);

const Select = ({ label, required, value, onChange, options }) => (
  <div className="space-y-1 text-left">
    <label className="text-[9px] font-black text-slate-500 uppercase ml-1 tracking-widest">{label} {required && "*"}</label>
    <select required={required} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-xs text-slate-800 transition-all shadow-sm text-center" value={value} onChange={e => onChange(e.target.value)}>
      <option value="">Pilih...</option>
      {options && options.map(o => <option key={String(o)} value={String(o)} className="text-center">{String(o)}</option>)}
    </select>
  </div>
);

const Pagination = ({ total, perPage, current, onChange }) => {
  const totalPages = Math.ceil(total / perPage);
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 mt-4 pb-4">
      <button disabled={current === 1} onClick={() => onChange(current - 1)} className="p-2 bg-white border border-slate-200 rounded-lg text-slate-400 hover:bg-slate-50 disabled:opacity-30 transition-all"><ChevronLeft size={16} /></button>
      <div className="px-4 py-2 bg-white border border-slate-200 rounded-lg shadow-sm"><span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Hal {current} / {totalPages}</span></div>
      <button disabled={current === totalPages} onClick={() => onChange(current + 1)} className="p-2 bg-white border border-slate-200 rounded-lg text-slate-400 hover:bg-slate-50 disabled:opacity-30 transition-all"><ChevronRight size={16} /></button>
    </div>
  );
};

const SearchableSelect = ({ label, value, onChange, options = [], required, placeholder = "Cari nama..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const filtered = useMemo(() => options.filter(o => String(o).toLowerCase().includes(search.toLowerCase())), [options, search]);
  return (
    <div className="space-y-1 text-left relative" ref={containerRef}>
      <label className="text-[9px] font-black text-slate-500 uppercase ml-1 tracking-widest">{label} {required && "*"}</label>
      <div onClick={() => setIsOpen(true)} className={`w-full px-4 py-2.5 rounded-xl border border-slate-200 outline-none focus-within:ring-4 focus-within:ring-blue-100 transition-all font-bold text-xs bg-slate-50 flex items-center justify-between cursor-pointer ${isOpen ? 'ring-4 ring-blue-100 border-blue-300' : ''}`}><span className={value ? 'text-slate-800' : 'text-slate-400'}>{value || placeholder}</span><Search size={14} className="text-slate-400" /></div>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute z-[100] top-full left-0 w-full mt-2 bg-white border border-slate-100 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            <div className="p-3 border-b border-slate-50 bg-slate-50/50"><input autoFocus type="text" placeholder="Ketik untuk mencari..." className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold uppercase outline-none focus:border-blue-400" value={search} onChange={e => setSearch(e.target.value)} /></div>
            <div className="max-h-48 overflow-y-auto custom-scrollbar p-1 text-left">
              {filtered.length > 0 ? filtered.map((o, idx) => (
                <button key={idx} type="button" onClick={() => { onChange(o); setIsOpen(false); setSearch(''); }} className={`w-full text-left px-4 py-2.5 rounded-xl text-[10px] font-black uppercase transition-all ${value === o ? 'bg-blue-600 text-white shadow-md' : 'hover:bg-slate-50 text-slate-600'}`}>{o}</button>
              )) : <div className="px-4 py-6 text-center text-[9px] font-bold text-slate-400 uppercase italic">Tidak ditemukan</div>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SearchableMultiSelect = ({ label, selected = [], onChange, options = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const filtered = useMemo(() => options.filter(o => String(o).toLowerCase().includes(search.toLowerCase()) && !selected.includes(o)), [options, search, selected]);
  const toggle = (o) => { const next = selected.includes(o) ? selected.filter(x => x !== o) : [...selected, o]; onChange(next); };
  return (
    <div className="space-y-1 text-left relative" ref={containerRef}>
      <label className="text-[9px] font-black text-slate-500 uppercase ml-1 tracking-widest">{label}</label>
      <div className="flex flex-wrap gap-2 mb-2">{selected.map(s => (<span key={s} className="px-3 py-1 bg-blue-600 text-white rounded-lg text-[9px] font-black uppercase flex items-center gap-2 shadow-sm">{s}<button onClick={() => toggle(s)} className="hover:text-red-200"><X size={10} /></button></span>))}</div>
      <div onClick={() => setIsOpen(true)} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between cursor-pointer font-bold text-xs"><span className="text-slate-400">PILIH DOKTER RABER...</span><Search size={14} className="text-slate-400" /></div>
      <AnimatePresence>{isOpen && (<motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="absolute z-[100] top-full left-0 w-full mt-2 bg-white border border-slate-100 rounded-2xl shadow-2xl p-2"><input autoFocus className="w-full px-3 py-2 bg-slate-50 rounded-lg text-xs font-bold uppercase mb-2 outline-none border border-slate-100" placeholder="Cari Dokter..." value={search} onChange={e => setSearch(e.target.value)} /><div className="max-h-40 overflow-y-auto custom-scrollbar">{filtered.map(o => (<button key={o} type="button" onClick={() => { toggle(o); setSearch(''); }} className="w-full text-left px-3 py-2 rounded-lg text-[9px] font-bold uppercase hover:bg-blue-50 text-slate-700">{o}</button>))}</div></motion.div>)}</AnimatePresence>
    </div>
  );
};

// --- SMART EXCEL HELPERS ---
const applySmartExcelFormatting = (ws, data) => {
  if (!data || data.length === 0) return;
  const colWidths = Object.keys(data[0] || {}).map(key => {
    let maxLen = String(key).length;
    data.forEach(row => {
      const cellValue = row[key] ? String(row[key]) : "";
      const multiLineMax = cellValue.split('\n').reduce((max, line) => Math.max(max, line.length), 0);
      if (multiLineMax > maxLen) maxLen = multiLineMax;
    });
    return { wch: Math.min(maxLen + 6, 60) }; 
  });
  ws['!cols'] = colWidths;
  const rowHeights = data.map(row => {
    let maxLines = 1;
    Object.values(row).forEach(val => {
      const lines = String(val || '').split('\n').length;
      if (lines > maxLines) maxLines = lines;
    });
    return { hpt: maxLines * 14 + 10 }; 
  });
  ws['!rows'] = [{ hpt: 30 }, { hpt: 10 }, { hpt: 25 }, ...rowHeights];
};

// --- 3. MODALS ---

function SmartDeleteModal({ onConfirm, onCancel, title, description }) { 
  return ( 
    <div className="fixed inset-0 z-[700] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm text-center">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-sm rounded-[2rem] p-8 shadow-2xl text-center">
        <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-red-100 shadow-lg text-center"><AlertTriangle size={32} /></div>
        <h5 className="text-lg font-black text-slate-800 mb-1 uppercase tracking-tight text-center">{title || 'Hapus Data Permanen?'}</h5>
        <p className="text-slate-500 text-[10px] mb-8 uppercase tracking-tighter leading-relaxed text-center">{description || 'Konfirmasi penghapusan data utama secara permanen.'}</p>
        <div className="flex gap-3 text-center justify-center">
          <button onClick={onCancel} className="flex-1 py-3.5 bg-slate-100 text-slate-600 font-black rounded-xl text-xs uppercase leading-none">Batal</button>
          <button onClick={onConfirm} className="flex-1 py-3.5 bg-red-600 text-white font-black rounded-xl text-xs uppercase shadow-lg text-center leading-none">Konfirmasi</button>
        </div>
      </motion.div>
    </div> 
  ); 
}

function SmartDuplicateModal({ patient, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-[800] flex items-center justify-center p-6 bg-slate-900/70 backdrop-blur-md">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-sm rounded-[2.5rem] p-8 shadow-2xl text-center">
        <div className="w-20 h-20 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-white shadow-xl"><UserSearch size={40} /></div>
        <h4 className="text-xl font-black text-slate-800 uppercase tracking-tight mb-2 text-center">Pasien Terdeteksi!</h4>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-6 text-center">Nomor MR <span className="text-blue-600">[{patient.mr}]</span> terdaftar atas nama:</p>
        <div className="bg-slate-50 p-4 rounded-2xl mb-8 border border-slate-100 text-center"><h5 className="text-lg font-black text-slate-800 uppercase">{patient.nama}</h5><p className="text-[9px] font-bold text-slate-400 mt-1 uppercase tracking-tighter">Gunakan identitas ini agar tidak mengetik ulang?</p></div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-4 bg-slate-100 text-slate-500 font-black rounded-2xl text-[10px] uppercase">Tidak, Ketik Baru</button>
          <button onClick={() => onConfirm(patient)} className="flex-[2] py-4 bg-blue-600 text-white font-black rounded-2xl text-[10px] uppercase shadow-lg shadow-blue-100">Iya, Gunakan Data</button>
        </div>
      </motion.div>
    </div>
  );
}

function SmartReceiverModal({ nurses, onConfirm, onCancel }) {
  const [selected, setSelected] = useState('');
  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center p-6 bg-slate-900/70 backdrop-blur-md text-center">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white w-full max-w-sm rounded-[2.5rem] p-8 shadow-2xl text-center">
        <div className="w-20 h-20 bg-blue-100 text-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-inner text-center"><UserCheck size={40} /></div>
        <h4 className="text-xl font-black text-slate-800 uppercase tracking-tight mb-2 text-center">Perawat Penerima</h4>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-8 text-center">Pilih perawat pelaksana yang menerima pasien di unit.</p>
        <div className="mb-8"><SearchableSelect label="Pilih Perawat" options={nurses} value={selected} onChange={setSelected} placeholder="Cari nama perawat..." /></div>
        <div className="flex gap-3 text-center">
          <button onClick={onCancel} className="flex-1 py-4 bg-slate-100 text-slate-500 font-black rounded-2xl text-[10px] uppercase">Batal</button>
          <button disabled={!selected} onClick={() => onConfirm(selected)} className="flex-[2] py-4 bg-blue-600 text-white font-black rounded-2xl text-[10px] uppercase shadow-lg shadow-blue-100 disabled:opacity-50 text-center">Lanjut Ke Registrasi</button>
        </div>
      </motion.div>
    </div>
  );
}

function SmartViewModal({ p, onClose }) { 
  return ( 
    <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md">
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} className="bg-white w-full max-w-4xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="bg-gradient-to-r from-blue-700 via-indigo-600 to-indigo-800 p-8 text-white relative">
          <div className="absolute top-0 right-0 w-64 h-full bg-white/5 skew-x-[-20deg] translate-x-20"></div>
          <div className="flex justify-between items-start relative z-10 text-left">
            <div className="flex items-center gap-5 text-left">
              <div className="w-16 h-16 bg-white/20 rounded-[1.5rem] flex items-center justify-center backdrop-blur-lg border border-white/30 shadow-xl text-center">
                <Activity size={32} />
              </div>
              <div className="text-left">
                <h5 className="text-2xl font-black uppercase tracking-tight leading-none mb-2">{p.nama}</h5>
                <div className="flex flex-wrap gap-2 text-left">
                  <span className="bg-white/20 px-3 py-1 rounded-lg font-black text-[10px] uppercase border border-white/20">MR: {p.mr}</span>
                  <span className="bg-white/20 px-3 py-1 rounded-lg font-black text-[10px] uppercase border border-white/20">{p.bed}</span>
                  <span className={`px-3 py-1 rounded-lg font-black text-[10px] uppercase border ${p.statusKeluar ? 'bg-red-50 text-red-500 border-red-100' : 'bg-emerald-50 text-emerald-500 border-emerald-400'}`}>
                    {p.statusKeluar || 'DIRAWAT'}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl transition-all border border-white/10 text-center">
              <X size={24} />
            </button>
          </div>
        </div>
        <div className="p-8 overflow-y-auto custom-scrollbar bg-slate-50 flex-1 text-left">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            <div className="space-y-6 text-left">
              <div className="flex items-center gap-2 border-l-4 border-blue-600 pl-3">
                <User size={18} className="text-blue-600" />
                <h6 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-800">Identitas Pasien</h6>
              </div>
              <div className="grid grid-cols-1 gap-4 text-left">
                <InfoRow label="Tgl Lahir" value={p.tglLahir} icon={CalendarDays} />
                <InfoRow label="Usia / Gender" value={`${p.usia} / ${p.jenisKelamin}`} icon={UserSearch} />
                <InfoRow label="Agama" value={p.agama} icon={CheckCircle2} />
                <InfoRow label="No. Telepon" value={p.noTelp} icon={Phone} />
                <InfoRow label="Riwayat Vaksin" value={p.riwayatVaksin} icon={Syringe} color="indigo" />
                <InfoRow label="Alamat Lengkap" value={p.alamat} icon={MapPin} full />
              </div>
            </div>
            <div className="space-y-6 text-left">
              <div className="flex items-center gap-2 border-l-4 border-emerald-600 pl-3">
                <Bed size={18} className="text-emerald-600" />
                <h6 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-800">Status Rawat</h6>
              </div>
              <div className="grid grid-cols-1 gap-4 text-left">
                <InfoRow label="Masuk ICU" value={p.tglJamMasuk?.replace('T', ' ')} icon={Clock} color="emerald" />
                <InfoRow label="Asal / Jaminan" value={`${p.asalPasien} / ${p.jaminan}`} icon={FileText} color="emerald" />
                <InfoRow label="Perawat Penerima" value={p.perawatPenerima} icon={UserCheck} color="emerald" />
                <InfoRow label="Diagnosa Medis" value={p.diagnosaMedis} icon={Stethoscope} full color="red" />
              </div>
            </div>
            <div className="space-y-6 text-left">
              <div className="flex items-center gap-2 border-l-4 border-indigo-600 pl-3">
                <Activity size={18} className="text-indigo-600" />
                <h6 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-800">Tindakan & Keluar</h6>
              </div>
              <div className="grid grid-cols-1 gap-4 text-left">
                <div className="p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100 grid grid-cols-1 gap-3 text-left">
                  <div className="flex justify-between items-center text-left"><span className="text-[9px] font-black text-indigo-500 uppercase text-left leading-none">Oksigen (Awal):</span><span className="text-[10px] font-bold">{p.oksigenAwal || '-'}</span></div>
                  <div className="flex justify-between items-center text-left"><span className="text-[9px] font-black text-indigo-500 uppercase text-left leading-none">Oksigen (Skrg):</span><span className="text-[10px] font-bold">{p.oksigenSaatIni || '-'}</span></div>
                  <div className="flex flex-col text-left"><span className="text-[9px] font-black text-indigo-500 uppercase">Trakheo:</span><span className="text-[10px] font-bold whitespace-pre-wrap">{(p.tglTrakheostomi || []).join(', ').replace(/T/g, ' ') || 'Tidak'}</span></div>
                  <div className="flex flex-col text-left"><span className="text-[9px] font-black text-indigo-500 uppercase">Intubasi:</span><span className="text-[10px] font-bold whitespace-pre-wrap">{(p.tglIntubasi || []).join(', ').replace(/T/g, ' ') || 'Tidak'}</span></div>
                  <div className="flex flex-col text-left"><span className="text-[9px] font-black text-indigo-500 uppercase">Ekstubasi:</span><span className="text-[10px] font-bold whitespace-pre-wrap">{(p.tglEkstubasi || []).join(', ').replace(/T/g, ' ') || 'Tidak'}</span></div>
                </div>
                <InfoRow label="DPJP" value={p.dpjp} icon={UserCog} color="blue" />
                <InfoRow label="Raber" value={Array.isArray(p.raber) ? p.raber.join(', ') : p.raber} icon={UserSearch} color="blue" />
                <InfoRow label="Keluar ICU" value={p.tglJamKeluar?.replace('T', ' ')} icon={LogOut} color="red" />
                <InfoRow label="Keterangan Tambahan" value={p.keterangan} icon={Info} full color="amber" />
              </div>
            </div>
          </div>
        </div>
        <div className="p-6 bg-white border-t border-slate-100 text-center">
          <button onClick={onClose} className="px-12 py-4 bg-slate-900 text-white font-black rounded-2xl uppercase text-[11px] tracking-widest hover:scale-105 transition-all shadow-xl shadow-slate-200 text-center">Selesai Pratinjau</button>
        </div>
      </motion.div>
    </div> 
  ); 
}

const InfoRow = ({ label, value, icon: Icon, full, color="blue" }) => (
  <div className={`p-4 bg-white rounded-2xl border border-slate-100 shadow-sm flex items-center gap-3 ${full ? 'md:col-span-1' : ''}`}>
    <div className={`p-2 rounded-xl bg-${color}-50 text-${color}-600 text-center`}>
      <Icon size={16} />
    </div>
    <div className="leading-none text-left">
      <p className="text-[8px] font-black text-slate-400 uppercase mb-1">{label}</p>
      <p className="text-[11px] font-bold text-slate-800 uppercase leading-tight">{value || '-'}</p>
    </div>
  </div>
);

function SmartReportViewModal({ p, conds, pj, date, onClose, onNext, onPrev, hasNext, hasPrev }) {
  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md text-center">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-5xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[95vh] relative text-center">
        <div className="absolute top-1/2 left-4 -translate-y-1/2 z-20">
          <button disabled={!hasPrev} onClick={onPrev} className={`p-2 rounded-full bg-white shadow-xl border border-slate-100 transition-all ${hasPrev ? 'text-indigo-600 hover:scale-110 active:scale-95' : 'text-slate-200 opacity-30 cursor-not-allowed'}`}><ChevronLeft size={32} /></button>
        </div>
        <div className="absolute top-1/2 right-4 -translate-y-1/2 z-20">
          <button disabled={!hasNext} onClick={onNext} className={`p-2 rounded-full bg-white shadow-xl border border-slate-100 transition-all ${hasNext ? 'text-indigo-600 hover:scale-110 active:scale-95' : 'text-slate-200 opacity-30 cursor-not-allowed'}`}><ChevronRight size={32} /></button>
        </div>
        <div className="bg-indigo-700 p-8 text-white flex justify-between items-center shadow-lg text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-white/5 skew-y-6 -translate-y-20"></div>
          <div className="flex items-center gap-6 relative z-10 text-left">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md border border-white/30 shadow-lg text-center">
              <ClipboardList size={32} />
            </div>
            <div className="text-left leading-tight">
              <h5 className="text-2xl font-black uppercase tracking-tight">{p.nama}</h5>
              <div className="flex items-center gap-3 mt-2 text-indigo-100 font-bold text-[11px] uppercase text-left">
                <span className="bg-white/20 px-3 py-1 rounded-lg border border-white/10 shadow-sm">MR: {p.mr}</span>
                <span className="bg-white/20 px-3 py-1 rounded-lg border border-white/10 shadow-sm">{p.bed}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl border border-white/10 transition-all relative z-10 text-center">
            <X size={24} />
          </button>
        </div>
        <div className="p-8 overflow-y-auto custom-scrollbar bg-slate-50 flex-1 px-16 text-left">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 text-left">
            <div className="bg-white p-5 rounded-[2rem] border border-slate-200/60 shadow-sm text-left relative overflow-hidden group">
              <div className="absolute -right-4 -bottom-4 opacity-5 text-indigo-600 group-hover:scale-110 transition-transform"><User size={80}/></div>
              <div className="flex items-center gap-2 mb-4 text-indigo-600 font-black uppercase text-[10px] tracking-widest text-left"><User size={16} /> Identitas</div>
              <div className="space-y-3 text-left">
                <div className="text-left"><p className="text-[8px] text-slate-400 font-black uppercase">Usia / Kelamin</p><p className="text-xs font-bold text-slate-800 uppercase">{p.usia || '-'} / {p.jenisKelamin || '-'}</p></div>
                <div className="text-left"><p className="text-[8px] text-slate-400 font-black uppercase">Jaminan</p><p className="text-xs font-bold text-emerald-600 uppercase">{p.jaminan || '-'}</p></div>
              </div>
            </div>
            <div className="md:col-span-2 bg-white p-5 rounded-[2rem] border border-slate-200/60 shadow-sm text-left relative overflow-hidden group">
              <div className="absolute -right-4 -bottom-4 opacity-5 text-red-500 group-hover:scale-110 transition-transform"><Stethoscope size={80}/></div>
              <div className="flex items-center gap-2 mb-4 text-red-500 font-black uppercase text-[10px] tracking-widest text-left"><Stethoscope size={16} /> Diagnosa & DPJP</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-left">
                <div className="text-left"><p className="text-[8px] text-slate-400 font-black uppercase">Diagnosa Medis</p><p className="text-xs font-bold text-slate-800 uppercase leading-relaxed line-clamp-3">{p.diagnosaMedis || '-'}</p></div>
                <div className="text-left">
                  <p className="text-[8px] text-slate-400 font-black uppercase">DPJP</p><p className="text-xs font-bold text-slate-800 uppercase mb-3">{p.dpjp || '-'}</p>
                  <p className="text-[8px] text-slate-400 font-black uppercase">Raber</p><p className="text-xs font-bold text-slate-600 uppercase italic leading-tight">{Array.isArray(p.raber) ? p.raber.join(', ') : (p.raber || '-')}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px flex-1 bg-slate-200"></div>
            <h6 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.3em]">Monitoring Laporan Shift ({date})</h6>
            <div className="h-px flex-1 bg-slate-200"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            {['pagi', 'siang', 'malam'].map((sKey) => {
              const colors = sKey === 'pagi' ? 'blue' : sKey === 'siang' ? 'indigo' : 'violet';
              return (
                <div key={sKey} className={`bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full hover:shadow-xl transition-all text-left`}>
                  <div className={`p-5 border-b border-slate-100 flex items-center justify-between bg-${colors}-50 text-left`}>
                    <div className="flex flex-col text-left">
                      <span className={`text-[11px] font-black text-${colors}-700 uppercase tracking-widest text-left`}>SHIFT {sKey.toUpperCase()}</span>
                      <span className="text-[8px] font-bold text-slate-400 uppercase text-left">PJ: {pj[sKey] || '-'}</span>
                    </div>
                    <div className={`p-2 bg-white rounded-xl text-${colors}-600 shadow-sm text-center`}><Clock size={16} /></div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col gap-6 text-left">
                    <div className="flex-1 text-left">
                      <p className="text-[8px] font-black text-slate-400 uppercase mb-2 tracking-widest text-left">Catatan Kondisi</p>
                      <div className="bg-slate-50/80 p-4 rounded-2xl border border-slate-100 min-h-[120px] shadow-inner text-left">
                        <p className="text-[11px] font-bold text-slate-700 uppercase leading-relaxed text-left">{conds[sKey]?.txt || 'TIDAK ADA CATATAN'}</p>
                      </div>
                    </div>
                    <div className="text-left">
                      <p className="text-[8px] font-black text-slate-400 uppercase mb-2 tracking-widest text-left">Perawat Pelaksana</p>
                      <div className={`flex items-center gap-3 bg-${colors}-50/50 px-4 py-3 rounded-2xl border border-${colors}-100 shadow-sm text-left`}>
                        <UserCheck size={14} className={`text-${colors}-600`} />
                        <span className={`text-[10px] font-black text-${colors}-800 uppercase`}>{conds[sKey]?.nrs || '-'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="p-8 bg-white border-t border-slate-100 text-center flex justify-center items-center gap-6">
          <div className="flex gap-2">
            <button disabled={!hasPrev} onClick={onPrev} className={`px-5 py-3 rounded-xl font-black text-[10px] uppercase flex items-center gap-2 border transition-all ${hasPrev ? 'border-slate-200 text-slate-600 hover:bg-slate-50' : 'border-slate-100 text-slate-200 cursor-not-allowed'}`}><ChevronLeft size={16}/> Sebelumnya</button>
            <button disabled={!hasNext} onClick={onNext} className={`px-5 py-3 rounded-xl font-black text-[10px] uppercase flex items-center gap-2 border transition-all ${hasNext ? 'border-slate-200 text-slate-600 hover:bg-slate-50' : 'border-slate-100 text-slate-200 cursor-not-allowed'}`}>Selanjutnya <ChevronRight size={16}/></button>
          </div>
          <button onClick={onClose} className="px-12 py-4 bg-slate-900 text-white font-black rounded-[1.5rem] uppercase text-[11px] tracking-[0.2em] shadow-2xl shadow-indigo-200 hover:scale-105 active:scale-95 transition-all text-center">Tutup Laporan</button>
        </div>
      </motion.div>
    </div>
  );
}

function SmartBookingDownloadModal({ onExport, onClose }) {
  const [fileName, setFileName] = useState(`Data_Booking_ICU_${new Date().toISOString().split('T')[0]}`);
  const [dateRange, setDateRange] = useState({ 
    start: new Date().toISOString().split('T')[0], 
    end: new Date().toISOString().split('T')[0] 
  });
  const [loading, setLoading] = useState(false);
  return (
    <div className="fixed inset-0 z-[700] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm text-center">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-md rounded-[2.5rem] p-8 shadow-2xl text-center">
        <div className="flex items-center gap-3 mb-6 text-left">
          <div className="p-2.5 bg-emerald-100 rounded-xl text-emerald-600 shadow-inner text-center"><FileSpreadsheet size={24} /></div>
          <div className="text-left leading-none text-left">
            <h4 className="text-lg font-black text-slate-800 uppercase tracking-tight text-left">Export Booking Excel</h4>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1 text-left">Output Berdasarkan Rentang Tanggal</p>
          </div>
        </div>
        <div className="space-y-4">
          <Field label="Nama File" value={fileName} onChange={setFileName} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Tgl Mulai" type="date" value={dateRange.start} onChange={v => setDateRange({...dateRange, start: v})} />
            <Field label="Tgl Akhir" type="date" value={dateRange.end} onChange={v => setDateRange({...dateRange, end: v})} />
          </div>
          <div className="flex gap-3 pt-2 justify-center">
            <button onClick={onClose} className="flex-1 py-4 bg-slate-100 text-slate-600 font-black rounded-2xl text-[10px] uppercase text-center">Batal</button>
            <button disabled={loading} onClick={async () => { setLoading(true); try { await onExport(fileName, dateRange); } finally { setLoading(false); } }} className="flex-[2] py-4 bg-emerald-600 text-white font-black rounded-2xl text-[10px] uppercase shadow-lg flex items-center justify-center gap-2">{loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}{loading ? 'PROSES...' : 'DOWNLOAD EXCEL'}</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function SmartBookingPdfDownloadModal({ onExport, onClose }) {
  const [fileName, setFileName] = useState(`Data_Booking_ICU_${new Date().toISOString().split('T')[0]}`);
  const [dateRange, setDateRange] = useState({ 
    start: new Date().toISOString().split('T')[0], 
    end: new Date().toISOString().split('T')[0] 
  });
  const [loading, setLoading] = useState(false);
  
  return (
    <div className="fixed inset-0 z-[700] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm text-center">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-md rounded-[2.5rem] p-8 shadow-2xl text-center">
        <div className="flex items-center gap-3 mb-6 text-left">
          <div className="p-2.5 bg-red-100 rounded-xl text-red-600 shadow-inner text-center"><FileText size={24} /></div>
          <div className="text-left leading-none text-left">
            <h4 className="text-lg font-black text-slate-800 uppercase tracking-tight text-left">Download PDF Booking</h4>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1 text-left">Format Profesional (Legal Landscape)</p>
          </div>
        </div>
        <div className="space-y-4 text-left">
          <Field label="Nama File" value={fileName} onChange={setFileName} />
          <div className="grid grid-cols-2 gap-4 text-center">
            <Field label="Tgl Mulai" type="date" value={dateRange.start} onChange={v => setDateRange({...dateRange, start: v})} />
            <Field label="Tgl Akhir" type="date" value={dateRange.end} onChange={v => setDateRange({...dateRange, end: v})} />
          </div>
          <div className="p-4 bg-red-50 rounded-2xl border border-red-100 flex items-start gap-3 text-left">
            <CheckCircle2 size={16} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-[10px] font-bold text-red-600 leading-relaxed uppercase text-left">Sistem akan otomatis memisahkan halaman PDF berdasarkan bulan, dan spasi per tanggal.</p>
          </div>
          <div className="flex gap-3 pt-2 justify-center text-center">
            <button onClick={onClose} className="flex-1 py-4 bg-slate-100 text-slate-600 font-black rounded-2xl text-[10px] uppercase text-center">Batal</button>
            <button disabled={loading} onClick={async () => { setLoading(true); await onExport(fileName, dateRange); setLoading(false); }} className="flex-[2] py-4 bg-red-600 text-white font-black rounded-2xl text-[10px] uppercase shadow-lg shadow-red-100 flex items-center justify-center gap-2">{loading ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}{loading ? 'MENYIAPKAN...' : 'DOWNLOAD PDF'}</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function SmartDownloadModal({ onExport, onClose }) {
  const [fileName, setFileName] = useState(`Laporan_ICU_${new Date().toISOString().split('T')[0]}`);
  const [dateRange, setDateRange] = useState({ start: new Date().toISOString().split('T')[0], end: new Date().toISOString().split('T')[0] });
  const [loading, setLoading] = useState(false);
  return (
    <div className="fixed inset-0 z-[700] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm text-center">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-md rounded-[2.5rem] p-8 shadow-2xl text-center">
        <div className="flex items-center gap-3 mb-6 text-left">
          <div className="p-2.5 bg-emerald-100 rounded-xl text-emerald-600 shadow-inner text-center"><FileDown size={24} /></div>
          <div className="text-left leading-none text-left">
            <h4 className="text-lg font-black text-slate-800 uppercase tracking-tight text-left">Eksport Laporan Excel</h4>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1 text-left">Konfigurasi Output Data</p>
          </div>
        </div>
        <div className="space-y-4 text-center">
          <Field label="Nama File" value={fileName} onChange={setFileName} />
          <div className="grid grid-cols-2 gap-4 text-center">
            <Field label="Rentang Mulai" type="date" value={dateRange.start} onChange={v => setDateRange({...dateRange, start: v})} />
            <Field label="Rentang Akhir" type="date" value={dateRange.end} onChange={v => setDateRange({...dateRange, end: v})} />
          </div>
          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-start gap-3 text-left">
            <AlertCircle size={16} className="text-blue-500 shrink-0 mt-0.5" />
            <p className="text-[10px] font-bold text-slate-500 leading-relaxed uppercase text-left">Sistem akan memproses data per tanggal ke dalam sheet yang berbeda.</p>
          </div>
          <div className="flex gap-3 pt-2 justify-center">
            <button onClick={onClose} className="flex-1 py-4 bg-slate-100 text-slate-600 font-black rounded-2xl text-[10px] uppercase text-center">Batal</button>
            <button disabled={loading} onClick={async () => { setLoading(true); await onExport(fileName, dateRange); setLoading(false); }} className="flex-[2] py-4 bg-emerald-600 text-white font-black rounded-2xl text-[10px] uppercase shadow-lg shadow-emerald-100 flex items-center justify-center gap-2">{loading ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}{loading ? 'MEMPROSES...' : 'MULAI DOWNLOAD'}</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function SmartRegDownloadModal({ onExport, onClose }) {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [fileName, setFileName] = useState(`Data_Registrasi_ICU_${new Date().toISOString().split('T')[0]}`);
  const [monthRange, setMonthRange] = useState({ start: currentMonth, end: currentMonth });
  const [loading, setLoading] = useState(false);
  return (
    <div className="fixed inset-0 z-[700] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm text-center">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-md rounded-[2.5rem] p-8 shadow-2xl text-center">
        <div className="flex items-center gap-3 mb-6 text-left">
          <div className="p-2.5 bg-emerald-100 rounded-xl text-emerald-600 shadow-inner text-center"><FileSpreadsheet size={24} /></div>
          <div className="text-left leading-none text-left">
            <h4 className="text-lg font-black text-slate-800 uppercase tracking-tight text-left">Download Register Pasien</h4>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1 text-left">Output Excel Berdasarkan Rentang Bulan</p>
          </div>
        </div>
        <div className="space-y-4 text-left">
          <Field label="Nama File" value={fileName} onChange={setFileName} />
          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="space-y-1 text-left">
              <label className="text-[9px] font-black text-slate-500 uppercase ml-1">Dari Bulan</label>
              <input type="month" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-xs text-center" value={monthRange.start} onChange={e => setMonthRange({...monthRange, start: e.target.value})} />
            </div>
            <div className="space-y-1 text-left">
              <label className="text-[9px] font-black text-slate-500 uppercase ml-1">Hingga Bulan</label>
              <input type="month" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-xs text-center" value={monthRange.end} onChange={e => setMonthRange({...monthRange, end: e.target.value})} />
            </div>
          </div>
          <div className="p-4 bg-indigo-50 rounded-2xl border border-indigo-100 flex items-start gap-3 text-left">
            <CheckCircle2 size={16} className="text-indigo-500 shrink-0 mt-0.5" />
            <p className="text-[10px] font-bold text-indigo-500 leading-relaxed uppercase text-left">Sistem akan memisahkan data ke dalam sheet berbeda sesuai nama bulan.</p>
          </div>
          <div className="flex gap-3 pt-2 justify-center text-center">
            <button onClick={onClose} className="flex-1 py-4 bg-slate-100 text-slate-600 font-black rounded-2xl text-[10px] uppercase text-center">Batal</button>
            <button disabled={loading} onClick={async () => { setLoading(true); await onExport(fileName, monthRange); setLoading(false); }} className="flex-[2] py-4 bg-emerald-600 text-white font-black rounded-2xl text-[10px] uppercase shadow-lg shadow-emerald-100 flex items-center justify-center gap-2">{loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}{loading ? 'MENYIAPKAN...' : 'DOWNLOAD EXCEL'}</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function SmartRegPdfDownloadModal({ onExport, onClose }) {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [fileName, setFileName] = useState(`Data_Registrasi_ICU_${new Date().toISOString().split('T')[0]}`);
  const [monthRange, setMonthRange] = useState({ start: currentMonth, end: currentMonth });
  const [loading, setLoading] = useState(false);
  return (
    <div className="fixed inset-0 z-[700] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm text-center">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-md rounded-[2.5rem] p-8 shadow-2xl text-center">
        <div className="flex items-center gap-3 mb-6 text-left">
          <div className="p-2.5 bg-red-100 rounded-xl text-red-600 shadow-inner text-center"><FileText size={24} /></div>
          <div className="text-left leading-none text-left">
            <h4 className="text-lg font-black text-slate-800 uppercase tracking-tight text-left">Download Register Pasien (PDF)</h4>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1 text-left">Format Legal Landscape Berdasarkan Bulan</p>
          </div>
        </div>
        <div className="space-y-4 text-left">
          <Field label="Nama File" value={fileName} onChange={setFileName} />
          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="space-y-1 text-left">
              <label className="text-[9px] font-black text-slate-500 uppercase ml-1">Dari Bulan</label>
              <input type="month" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-xs text-center" value={monthRange.start} onChange={e => setMonthRange({...monthRange, start: e.target.value})} />
            </div>
            <div className="space-y-1 text-left">
              <label className="text-[9px] font-black text-slate-500 uppercase ml-1">Hingga Bulan</label>
              <input type="month" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-xs text-center" value={monthRange.end} onChange={e => setMonthRange({...monthRange, end: e.target.value})} />
            </div>
          </div>
          <div className="p-4 bg-red-50 rounded-2xl border border-red-100 flex items-start gap-3 text-left">
            <CheckCircle2 size={16} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-[10px] font-bold text-red-600 leading-relaxed uppercase text-left">PDF akan otomatis dipisahkan per halamannya berdasarkan bulan yang Anda pilih.</p>
          </div>
          <div className="flex gap-3 pt-2 justify-center text-center">
            <button onClick={onClose} className="flex-1 py-4 bg-slate-100 text-slate-600 font-black rounded-2xl text-[10px] uppercase text-center">Batal</button>
            <button disabled={loading} onClick={async () => { setLoading(true); await onExport(fileName, monthRange); setLoading(false); }} className="flex-[2] py-4 bg-red-600 text-white font-black rounded-2xl text-[10px] uppercase shadow-lg shadow-red-100 flex items-center justify-center gap-2">{loading ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}{loading ? 'MENYIAPKAN...' : 'DOWNLOAD PDF'}</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function SmartBukuTugasPdfDownloadModal({ onExport, onClose }) {
  const [fileName, setFileName] = useState(`Buku_Tugas_ICU_${new Date().toISOString().split('T')[0]}`);
  const [dateRange, setDateRange] = useState({ start: new Date().toISOString().split('T')[0], end: new Date().toISOString().split('T')[0] });
  const [loading, setLoading] = useState(false);
  return (
    <div className="fixed inset-0 z-[700] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm text-center">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-md rounded-[2.5rem] p-8 shadow-2xl text-center">
        <div className="flex items-center gap-3 mb-6 text-left">
          <div className="p-2.5 bg-red-100 rounded-xl text-red-600 shadow-inner text-center"><FileText size={24} /></div>
          <div className="text-left leading-none text-left">
            <h4 className="text-lg font-black text-slate-800 uppercase tracking-tight text-left">Download PDF Buku Tugas</h4>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1 text-left">Format Profesional (Legal Landscape)</p>
          </div>
        </div>
        <div className="space-y-4 text-left">
          <Field label="Nama File" value={fileName} onChange={setFileName} />
          <div className="grid grid-cols-2 gap-4 text-center">
            <Field label="Tgl Mulai" type="date" value={dateRange.start} onChange={v => setDateRange({...dateRange, start: v})} />
            <Field label="Tgl Akhir" type="date" value={dateRange.end} onChange={v => setDateRange({...dateRange, end: v})} />
          </div>
          <div className="p-4 bg-red-50 rounded-2xl border border-red-100 flex items-start gap-3 text-left">
            <CheckCircle2 size={16} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-[10px] font-bold text-red-600 leading-relaxed uppercase text-left">Sistem akan memisahkan halaman secara otomatis setiap pergantian tanggal secara rapi.</p>
          </div>
          <div className="flex gap-3 pt-2 justify-center text-center">
            <button onClick={onClose} className="flex-1 py-4 bg-slate-100 text-slate-600 font-black rounded-2xl text-[10px] uppercase text-center">Batal</button>
            <button disabled={loading} onClick={async () => { setLoading(true); await onExport(fileName, dateRange); setLoading(false); }} className="flex-[2] py-4 bg-red-600 text-white font-black rounded-2xl text-[10px] uppercase shadow-lg shadow-red-100 flex items-center justify-center gap-2">{loading ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}{loading ? 'MENYIAPKAN...' : 'DOWNLOAD PDF'}</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function SmartReportPdfDownloadModal({ onExport, onClose }) {
  const [fileName, setFileName] = useState(`Laporan_Shift_ICU_${new Date().toISOString().split('T')[0]}`);
  const [dateRange, setDateRange] = useState({ start: new Date().toISOString().split('T')[0], end: new Date().toISOString().split('T')[0] });
  const [loading, setLoading] = useState(false);
  return (
    <div className="fixed inset-0 z-[700] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm text-center">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-md rounded-[2.5rem] p-8 shadow-2xl text-center">
        <div className="flex items-center gap-3 mb-6 text-left">
          <div className="p-2.5 bg-red-100 rounded-xl text-red-600 shadow-inner text-center"><FileText size={24} /></div>
          <div className="text-left leading-none text-left">
            <h4 className="text-lg font-black text-slate-800 uppercase tracking-tight text-left">Download PDF Laporan Pasien</h4>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1 text-left">Format Profesional (Legal Landscape)</p>
          </div>
        </div>
        <div className="space-y-4 text-left">
          <Field label="Nama File" value={fileName} onChange={setFileName} />
          <div className="grid grid-cols-2 gap-4 text-center">
            <Field label="Tgl Mulai" type="date" value={dateRange.start} onChange={v => setDateRange({...dateRange, start: v})} />
            <Field label="Tgl Akhir" type="date" value={dateRange.end} onChange={v => setDateRange({...dateRange, end: v})} />
          </div>
          <div className="p-4 bg-red-50 rounded-2xl border border-red-100 flex items-start gap-3 text-left">
            <CheckCircle2 size={16} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-[10px] font-bold text-red-600 leading-relaxed uppercase text-left">Sistem akan memisahkan halaman secara otomatis setiap pergantian tanggal.</p>
          </div>
          <div className="flex gap-3 pt-2 justify-center text-center">
            <button onClick={onClose} className="flex-1 py-4 bg-slate-100 text-slate-600 font-black rounded-2xl text-[10px] uppercase text-center">Batal</button>
            <button disabled={loading} onClick={async () => { setLoading(true); await onExport(fileName, dateRange); setLoading(false); }} className="flex-[2] py-4 bg-red-600 text-white font-black rounded-2xl text-[10px] uppercase shadow-lg shadow-red-100 flex items-center justify-center gap-2">{loading ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}{loading ? 'MENYIAPKAN...' : 'DOWNLOAD PDF'}</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// --- 4. FEATURE COMPONENTS ---

const LoginBackgroundAnimation = () => {
  const containerRef = useRef(null);
  const itemsRef = useRef([]);

  useEffect(() => {
    const numItems = 15;
    const items = [];
    const container = containerRef.current;
    if (!container) return;

    let width = container.clientWidth;
    let height = container.clientHeight;

    for (let i = 0; i < numItems; i++) {
      const size = 60; 
      let speed = Math.random() * 1.5 + 1.2; 
      let angle = Math.random() * Math.PI * 2;
      items.push({
        x: Math.random() * (width - size),
        y: Math.random() * (height - size),
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: size,
        el: itemsRef.current[i]
      });
    }

    let animationFrameId;

    const update = () => {
      width = container.clientWidth;
      height = container.clientHeight;

      items.forEach(item => {
        item.x += item.vx;
        item.y += item.vy;

        if (item.x <= 0) { item.x = 0; item.vx *= -1; }
        if (item.x + item.size >= width) { item.x = width - item.size; item.vx *= -1; }
        if (item.y <= 0) { item.y = 0; item.vy *= -1; }
        if (item.y + item.size >= height) { item.y = height - item.size; item.vy *= -1; }

        if (item.el) {
          item.el.style.transform = `translate(${item.x}px, ${item.y}px)`;
        }
      });

      animationFrameId = requestAnimationFrame(update);
    };

    update();

    const handleResize = () => {
      width = container.clientWidth;
      height = container.clientHeight;
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const icons = [Activity, Stethoscope, Syringe, Bed, Plus];

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {Array.from({ length: 15 }).map((_, i) => {
        const Icon = icons[i % icons.length];
        return (
          <div
            key={i}
            ref={el => itemsRef.current[i] = el}
            className="absolute text-white/10 drop-shadow-lg"
            style={{ top: 0, left: 0, willChange: 'transform' }}
          >
            <Icon size={60} strokeWidth={1.5} />
          </div>
        );
      })}
    </div>
  );
};

function LoginUI({ auth, showNotify }) {
  const [email, setEmail] = useState(localStorage.getItem('icu_rem_email') || '');
  const [password, setPassword] = useState(localStorage.getItem('icu_rem_pass') || '');
  const [rem, setRem] = useState(localStorage.getItem('icu_rem_check') === 'true');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      if (rem) { localStorage.setItem('icu_rem_email', email); localStorage.setItem('icu_rem_pass', password); localStorage.setItem('icu_rem_check', 'true'); } 
      else { localStorage.removeItem('icu_rem_email'); localStorage.removeItem('icu_rem_pass'); localStorage.setItem('icu_rem_check', 'false'); }
      showNotify("Berhasil Masuk");
    } catch (err) { showNotify("Email atau Password Salah / Tidak Terdaftar", "error"); } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center p-4 text-center relative overflow-hidden">
      <LoginBackgroundAnimation />
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl p-10 text-center relative z-10">
        <div className="text-center mb-8"><div className="w-16 h-16 bg-blue-100 rounded-[1.5rem] flex items-center justify-center mx-auto mb-3 text-blue-600 shadow-inner"><Activity size={32} /></div><h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight">ICU RSUD PM</h2><p className="text-slate-500 text-[10px] font-bold italic mt-2 uppercase tracking-widest leading-none">Pusat Manajemen Pasien & Laporan</p></div>
        <form onSubmit={handleLogin} className="space-y-4 text-left">
          <div className="space-y-1"><label className="text-[10px] font-black text-slate-500 uppercase ml-2 tracking-widest">Email Akses</label><input type="email" required placeholder="admin@rsudpm.com" className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-sm" value={email} onChange={e => setEmail(e.target.value)} /></div>
          <div className="space-y-1 relative"><label className="text-[10px] font-black text-slate-500 uppercase ml-2 tracking-widest">Kata Sandi</label><div className="relative"><input type={showPass ? "text" : "password"} required placeholder="••••••••" className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-4 focus:ring-blue-100 font-bold text-sm pr-12" value={password} onChange={e => setPassword(e.target.value)} /><button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600">{showPass ? <EyeOff size={18} /> : <Eye size={18} />}</button></div></div>
          <div className="flex items-center gap-2 justify-center"><input type="checkbox" id="remCheck" checked={rem} onChange={e => setRem(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-blue-600" /><label htmlFor="remCheck" className="text-[10px] font-black text-slate-500 cursor-pointer">Ingat Saya</label></div>
          <button type="submit" disabled={loading} className="w-full py-4 bg-blue-600 text-white font-black rounded-xl shadow-lg flex items-center justify-center gap-2 uppercase text-xs">{loading ? <Loader2 className="animate-spin" size={16} /> : 'MASUK KE SISTEM'}</button>
          <div className="pt-4 border-t border-slate-100 text-center"><p className="text-[9px] font-bold text-slate-400 uppercase leading-relaxed">Gunakan akun resmi untuk masuk. <br/> Hak akses terbatas bagi staf rumah sakit.</p></div>
        </form>
      </motion.div>
    </div>
  );
}

function SidebarUI({ isOpen, setOpen, isCollapsed, setCollapsed, activeTab, setActiveTab }) {
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard }, 
    { id: 'bookingPasien', label: 'Booking Pasien', icon: CalendarPlus }, 
    { id: 'dataBookingPasien', label: 'Data Booking Pasien', icon: ListChecks }, 
    { id: 'registrasiPasien', label: 'Registrasi Pasien', icon: UserPlus }, 
    { id: 'dataRegisterPasien', label: 'Data Register Pasien', icon: TableIcon }, 
    { id: 'laporanPasien', label: 'Laporan Pasien', icon: FileText }, 
    { id: 'laporanWhatsApp', label: 'Laporan WhatsApp', icon: Share2 },
    { id: 'pembagianTugas', label: 'Pembagian Tugas', icon: BookOpen }, 
    { id: 'bukuTugas', label: 'Buku Tugas', icon: ClipboardList }, 
    { id: 'smartBackupRestore', label: 'Smart Backup & Restore', icon: DatabaseBackup }, 
    { id: 'pengaturanMaster', label: 'Pengaturan Master', icon: Settings },
  ];
  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.aside initial={{ x: -280 }} animate={{ x: 0, width: isCollapsed ? 80 : 256 }} exit={{ x: -280 }} transition={{ type: 'spring', damping: 25, stiffness: 200 }} className="fixed lg:relative z-[150] h-full bg-white border-r border-slate-200 flex flex-col shadow-2xl lg:shadow-none overflow-hidden text-center">
            <div className={`p-5 border-b border-slate-50 flex items-center justify-between ${isCollapsed ? 'flex-col gap-4' : ''}`}><div className="flex items-center gap-2 overflow-hidden text-center"><div className="bg-blue-600 p-2 rounded-xl text-white shadow-md flex-shrink-0"><Activity size={20} /></div>{!isCollapsed && (<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col leading-none whitespace-nowrap text-left"><span className="text-md font-black text-slate-800 uppercase">ICU Care</span><span className="text-[8px] text-slate-400 font-black uppercase tracking-tighter">RSUD PASAR MINGGU</span></motion.div>)}</div><div className="flex items-center gap-1 text-center"><button onClick={() => setCollapsed(!isCollapsed)} className="hidden lg:flex p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg transition-all">{isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}</button><button onClick={() => setOpen(false)} className="lg:hidden p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg transition-all"><X size={18} /></button></div></div>
            <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto no-scrollbar">{menuItems.map(item => (<button key={item.id} onClick={() => { setActiveTab(item.id); if (window.innerWidth < 1024) setOpen(false); }} className={`w-full flex items-center rounded-xl transition-all group relative ${isCollapsed ? 'justify-center p-3' : 'gap-3 px-4 py-3.5'} ${activeTab === item.id ? 'bg-blue-600 text-white shadow-lg font-bold' : 'text-slate-500 hover:bg-slate-50 hover:text-blue-600 font-medium'}`} title={isCollapsed ? item.label : ''}><item.icon size={18} className="flex-shrink-0" />{!isCollapsed && <span className="text-xs tracking-wide uppercase whitespace-nowrap">{item.label}</span>}</button>))}</nav>
            <div className="p-4 border-t border-slate-50 text-center">{!isCollapsed ? (<p className="text-[10px] font-bold text-emerald-500 flex items-center justify-center gap-1.5 leading-none uppercase"><span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>CONNECTED</p>) : (<div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse mx-auto"></div>)}</div>
          </motion.aside>
        )}
      </AnimatePresence>
      {isOpen && <div className="fixed inset-0 bg-slate-900/30 z-[140] lg:hidden backdrop-blur-[2px]" onClick={() => setOpen(false)} />}
    </>
  );
}

function LaporanWhatsAppUI({ patients, bookingPatients, masters, showNotify }) {
  const currentShift = getCurrentShift();
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [shift, setShift] = useState(currentShift.id);
  const [staff, setStaff] = useState({ dr: '', nurses: '' });
  const [equipment, setEquipment] = useState({ monitor: 0, infus: 0, syringe: 0 });
  const [loading, setLoading] = useState(false);
  const [conditions, setConditions] = useState({});
  const [customText, setCustomText] = useState(null);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [editableText, setEditableText] = useState('');

  useEffect(() => {
    const reportRef = doc(db, 'artifacts', appId, 'public', 'data', 'wa_reports', `${date}_${shift}`);
    const unsub = onSnapshot(reportRef, (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setStaff(prev => ({ 
          dr: d.staff?.dr || prev.dr, 
          nurses: d.staff?.nurses || prev.nurses 
        }));
        setEquipment(d.equipment || { monitor: 0, infus: 0, syringe: 0 });
        setCustomText(d.customText !== undefined ? d.customText : null);
      } else {
        setStaff(prev => ({ ...prev, dr: '' }));
        setEquipment({ monitor: 0, infus: 0, syringe: 0 });
        setCustomText(null);
        const taskBookRef = doc(db, 'artifacts', appId, 'public', 'data', 'task_books', `${date}_${shift}`);
        getDoc(taskBookRef).then(tSnap => {
          if (tSnap.exists()) {
            const nursesArray = tSnap.data().workingNurses || [];
            setStaff(prev => ({ ...prev, nurses: nursesArray.join(', ') }));
          } else {
            setStaff(prev => ({ ...prev, nurses: '' }));
          }
        });
      }
    });

    const unsubConds = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', `conds_${date}`), (s) => {
      const c = {}; s.docs.forEach(d => { c[d.id] = d.data(); });
      setConditions(c);
    });
    return () => { unsub(); unsubConds(); };
  }, [date, shift]);

  const stats = useMemo(() => {
    const { start, end } = getShiftTimeRange(date, shift);

    const awal = patients.filter(p => {
      const masuk = new Date(p.tglJamMasuk);
      const keluar = p.tglJamKeluar ? new Date(p.tglJamKeluar) : null;
      return masuk < start && (!keluar || keluar >= start);
    });

    const baru = patients.filter(p => {
      const masuk = new Date(p.tglJamMasuk);
      return masuk >= start && masuk < end;
    });

    const pindah = patients.filter(p => {
      const keluar = p.tglJamKeluar ? new Date(p.tglJamKeluar) : null;
      return keluar && keluar >= start && keluar < end && p.statusKeluar !== 'Meninggal';
    });

    const meninggal = patients.filter(p => {
      const keluar = p.tglJamKeluar ? new Date(p.tglJamKeluar) : null;
      return keluar && keluar >= start && keluar < end && p.statusKeluar === 'Meninggal';
    });

    const treated = patients.filter(p => {
      const masuk = new Date(p.tglJamMasuk);
      const keluar = p.tglJamKeluar ? new Date(p.tglJamKeluar) : null;
      return masuk < end && (!keluar || keluar >= start);
    });

    const getCountByUnit = (list, unit) => list.filter(p => (p.bed || '').toLowerCase().includes(unit.toLowerCase())).length;
    
    const bookings = bookingPatients.filter(b => {
      if (b.statusBooking !== 'Tetap Booking') return false;
      const bTime = new Date(b.tglJamBooking);
      return bTime <= end;
    });

    return { awal, baru, pindah, meninggal, treated, getCountByUnit, bookings, shiftRange: { start, end } };
  }, [patients, bookingPatients, date, shift]);

  const generateReport = () => {
    const sName = shift.charAt(0).toUpperCase() + shift.slice(1);
    const dStr = new Date(date).toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    
    const activeTreat = stats.treated.filter(p => !p.statusKeluar || new Date(p.tglJamKeluar) >= stats.shiftRange.end);
    const ventCount = activeTreat.filter(p => p.oksigenSaatIni === 'Ventilator').length;
    const ettCount = activeTreat.filter(p => p.intubasi === 'Iya' || (Array.isArray(p.tglIntubasi) && p.tglIntubasi.length > 0)).length;
    const ttCount = activeTreat.filter(p => p.trakheostomi === 'Iya').length;
    const tPieceCount = activeTreat.filter(p => p.oksigenSaatIni === 'T-Piece').length;
    const nkCount = activeTreat.filter(p => p.oksigenSaatIni === 'NK').length;
    const nrmCount = activeTreat.filter(p => p.oksigenSaatIni === 'NRM').length;

    const namesBaru = stats.baru.length > 0 ? `(${stats.baru.map(p => p.nama?.toUpperCase()).join(', ')})` : '';
    const namesPindah = stats.pindah.length > 0 ? `(${stats.pindah.map(p => p.nama?.toUpperCase()).join(', ')})` : '';
    const namesMeninggal = stats.meninggal.length > 0 ? `(${stats.meninggal.map(p => p.nama?.toUpperCase()).join(', ')})` : '';

    let text = `Assalamu’alaikum, Wr, Wb\n`;
    text += `Selamat ${shift}, Laporan Dinas ${sName} tanggal ${dStr}\n`;
    text += `(ICU NEURO & ICU BEDAH)\n\n`;
    text += `======================\n`;
    text += `Total pasien\t: ${stats.awal.length} orang (awal shift)\n`;
    text += `ICU Neuro\t: ${stats.getCountByUnit(stats.awal, 'Neuro')} orang\n`;
    text += `ICU Metabolik/Paru : ${stats.getCountByUnit(stats.awal, 'Metabolik')} orang\n`;
    text += `ICU Bedah\t: ${stats.getCountByUnit(stats.awal, 'Bedah')} orang\n`;
    text += `HCU\t\t: ${stats.getCountByUnit(stats.awal, 'HCU')} orang\n`;
    text += `Code Stroke\t: ${stats.getCountByUnit(stats.awal, 'Code')} orang\n`;
    text += `Pasien Baru\t: ${stats.baru.length} orang ${namesBaru}\n`;
    text += `Pasien Pindah\t: ${stats.pindah.length} orang ${namesPindah}\n`;
    text += `Pasien Meninggal : ${stats.meninggal.length} orang ${namesMeninggal}\n`;
    text += `======================\n`;
    text += `Total pasien\t: ${activeTreat.length} orang (dirawat)\n`;
    text += `ICU Neuro\t: ${stats.getCountByUnit(activeTreat, 'Neuro')} orang\n`;
    text += `ICU Metabolik/Paru : ${stats.getCountByUnit(activeTreat, 'Metabolik')} orang\n`;
    text += `ICU Bedah\t: ${stats.getCountByUnit(activeTreat, 'Bedah')} orang\n`;
    text += `HCU\t\t: ${stats.getCountByUnit(activeTreat, 'HCU')} orang\n`;
    text += `Code Stroke\t: ${stats.getCountByUnit(activeTreat, 'Code')} orang\n\n`;
    
    text += `Bookingan\t: ${stats.bookings.length} orang\n`;
    const unitsList = ['Neuro', 'Metabolik/Paru', 'Bedah', 'HCU', 'Code Stroke'];
    unitsList.forEach(u => {
      const filtered = stats.bookings.filter(b => (b.unit || '').toLowerCase().includes(u.split('/')[0].toLowerCase()));
      text += `ICU ${u} : ${filtered.length} orang\n`;
      filtered.forEach((b, i) => {
        text += `${i+1}. ${b.nama?.toUpperCase()}, ${b.mr || '-'}, ${b.asalPasien || '-'}, ${b.unit || '-'}, ${b.diagnosa || '-'}, ${b.dokterMerawat || '-'}${b.keterangan ? ', ' + b.keterangan : ''}\n`;
      });
    });

    text += `\n======================\n`;
    text += `Kondisi pasien shift ${shift}\n\n`;
    
    const bedList = masters.beds || DEFAULT_BEDS;
    bedList.forEach((bedName, idx) => {
      const p = stats.treated.find(pt => {
        if (!pt.bed) return false;
        const escapedBedName = bedName.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
        const bedRegex = new RegExp('^' + escapedBedName + '(\\s|$)');
        return bedRegex.test(pt.bed);
      });

      if (p) {
        const exitTime = p.statusKeluar ? new Date(p.tglJamKeluar) : null;
        const exitedThisShift = exitTime && exitTime >= stats.shiftRange.start && exitTime < stats.shiftRange.end;
        
        const unitLabel = p.bed.replace(bedName, '').trim();
        const cond = conditions[p.id]?.[shift]?.txt || 'TIDAK ADA CATATAN';
        
        const diagnosaArray = (p.diagnosaMedis || '').split(/[,;\n]/).map(d => d.trim()).filter(d => d);
        const diagsLimited = diagnosaArray.slice(0, 3).join(', ');

        text += `${idx + 1}. ${bedName}, ${unitLabel || '-'}\n`;
        text += `${p.nama?.toUpperCase()}, ${p.mr}, ${p.dpjp}, ${diagsLimited?.toUpperCase()}\n`;
        text += `${cond}\n`;
        if (exitedThisShift) {
          text += `(STATUS: ${p.statusKeluar.toUpperCase()})\n`;
        }
      } else {
        text += `${idx + 1}. ${bedName}\n`;
        text += `Kosong\n`;
      }
      text += `\n`;
    });

    text += `\n\nPENGGUNAAN ALAT ICU NEURO & BEDAH\n`;
    text += `Monitor\t: ${equipment.monitor} unit\n`;
    text += `Infus Pump\t: ${equipment.infus} unit\n`;
    text += `Syringe Pump : ${equipment.syringe} unit\n`;
    text += `Ventilator\t: ${ventCount} unit\n`;
    text += `ETT\t\t: ${ettCount} orang\n`;
    text += `TT\t\t: ${ttCount} orang\n`;
    text += `T-Piece\t: ${tPieceCount} orang\n`;
    text += `NK\t\t: ${nkCount} orang\n`;
    text += `NRM\t\t: ${nrmCount} orang\n\n`;
    
    text += `Nurse & Dokter on Duty\n`;
    text += `Dokter : dr. ${staff.dr || '-'}\n`;
    text += `Nurse  : ${staff.nurses || '-'}\n`;

    return text;
  };

  const copyToClipboard = () => {
    const el = document.createElement('textarea');
    el.value = customText !== null ? customText : generateReport();
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    showNotify("Laporan Berhasil Disalin");
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'wa_reports', `${date}_${shift}`), {
        date, shift, staff, equipment, updatedAt: new Date().toISOString()
      }, { merge: true });
      showNotify("Laporan WA Tersimpan");
    } catch (e) { showNotify("Gagal Simpan", "error"); } finally { setLoading(false); }
  };

  const handleReset = () => {
    setStaff({ dr: '', nurses: '' });
    setEquipment({ monitor: 0, infus: 0, syringe: 0 });
    showNotify("Form Reset");
  };

  const handleSaveTemplate = async () => {
    setLoading(true);
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'wa_reports', `${date}_${shift}`), {
        customText: editableText,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      setIsEditingMode(false);
      showNotify("Template Laporan Tersimpan");
    } catch (e) { showNotify("Gagal Simpan Template", "error"); } finally { setLoading(false); }
  };

  const handleResetTemplate = async () => {
    setLoading(true);
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'wa_reports', `${date}_${shift}`), {
        customText: null,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      showNotify("Template Dikembalikan ke Default");
    } catch (e) { showNotify("Gagal Reset", "error"); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-20 text-center">
      <div className="bg-white rounded-[2.5rem] p-8 shadow-xl border border-slate-100 text-center">
        <div className="flex items-center gap-4 mb-8 text-left">
          <div className="p-3 bg-green-50 text-green-600 rounded-2xl"><Share2 size={24}/></div>
          <div className="text-left leading-none text-left">
            <h4 className="text-xl font-black text-slate-800 uppercase tracking-tight">Generator Laporan WhatsApp</h4>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Format Resmi ICU Neuro & Bedah</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 text-center">
          <div className="space-y-6 text-center">
            <div className="grid grid-cols-2 gap-4 text-center">
              <Field label="Pilih Tanggal" type="date" value={date} onChange={setDate} />
              <Select label="Pilih Shift" value={shift} options={['pagi', 'siang', 'malam']} onChange={setShift} />
            </div>

            <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-200 space-y-4 text-left">
              <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 mb-4 flex items-center gap-2"><UserCog size={14}/> Petugas On-Duty</h5>
              <Field label="Dokter Jaga" value={staff.dr} onChange={v => setStaff({...staff, dr: v.toUpperCase()})} placeholder="CONTOH: BUDI" />
              <div className="space-y-1 text-left">
                <label className="text-[9px] font-black text-slate-500 uppercase ml-1 tracking-widest">Daftar Perawat</label>
                <textarea className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl font-bold text-xs h-24 uppercase outline-none focus:ring-4 focus:ring-blue-100" value={staff.nurses} onChange={e => setStaff({...staff, nurses: e.target.value.toUpperCase()})} placeholder="OTOMATIS DARI BUKU TUGAS..." />
              </div>
            </div>

            <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-200 space-y-4 text-left">
              <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 mb-4 flex items-center gap-2"><Activity size={14}/> Penggunaan Alat (Unit)</h5>
              <div className="grid grid-cols-3 gap-4 text-center">
                <Field label="Monitor" type="number" value={equipment.monitor} onChange={v => setEquipment({...equipment, monitor: v})} />
                <Field label="Infus Pump" type="number" value={equipment.infus} onChange={v => setEquipment({...equipment, infus: v})} />
                <Field label="Syringe Pump" type="number" value={equipment.syringe} onChange={v => setEquipment({...equipment, syringe: v})} />
              </div>
            </div>

            <div className="flex gap-3 text-center">
              <button onClick={handleReset} className="flex-1 py-4 bg-red-50 text-red-600 font-black rounded-2xl text-[10px] uppercase border border-red-100">Reset Data</button>
              <button onClick={handleSave} disabled={loading} className="flex-1 py-4 bg-blue-600 text-white font-black rounded-2xl text-[10px] uppercase shadow-lg shadow-blue-100">
                {loading ? <Loader2 className="animate-spin mx-auto" size={16}/> : 'Simpan Laporan'}
              </button>
            </div>
          </div>

          <div className="flex flex-col h-full text-center">
            <div className="flex items-center justify-between mb-4 px-2 text-left">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Pratinjau Teks WhatsApp</span>
              <div className="flex items-center gap-2">
                {!isEditingMode ? (
                  <>
                    {customText !== null && (
                      <button onClick={handleResetTemplate} className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-500 rounded-xl text-[9px] font-black uppercase shadow-sm hover:bg-slate-200 transition-all">
                        <RotateCcw size={12}/> Reset Default
                      </button>
                    )}
                    <button onClick={() => { setEditableText(customText !== null ? customText : generateReport()); setIsEditingMode(true); }} className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-xl text-[9px] font-black uppercase shadow-sm shadow-blue-100 hover:bg-blue-700 transition-all">
                      <Edit3 size={12}/> Edit Teks
                    </button>
                    <button onClick={copyToClipboard} className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase shadow-lg shadow-emerald-100 active:scale-95 transition-all">
                      <Copy size={14}/> Salin Teks
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => setIsEditingMode(false)} className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 rounded-xl text-[9px] font-black uppercase shadow-sm border border-red-100 hover:bg-red-100 transition-all">
                      <X size={12}/> Batal
                    </button>
                    <button onClick={handleSaveTemplate} disabled={loading} className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase shadow-lg shadow-indigo-100 active:scale-95 transition-all disabled:opacity-50">
                      {loading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14}/>} Simpan Template
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="flex-1 bg-slate-900 rounded-[2rem] p-8 text-left overflow-y-auto max-h-[600px] custom-scrollbar border-4 border-slate-800 shadow-inner">
              {isEditingMode ? (
                <textarea 
                  className="w-full h-full min-h-[400px] bg-transparent text-emerald-400 font-mono text-[10px] leading-relaxed whitespace-pre-wrap outline-none resize-none custom-scrollbar"
                  value={editableText}
                  onChange={(e) => setEditableText(e.target.value)}
                  spellCheck="false"
                />
              ) : (
                <pre className="text-emerald-400 font-mono text-[10px] leading-relaxed whitespace-pre-wrap">
                  {customText !== null ? customText : generateReport()}
                </pre>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BookingListUI({ bookings, patients, showNotify, onEdit, onRegistrasi }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Semua Status');
  const [sortOrder, setSortOrder] = useState('desc'); 
  const [isLocked, setIsLocked] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [activeRowId, setActiveRowId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 50;

  const filteredBookings = useMemo(() => {
    return bookings.filter(b => {
      const searchMatch = (b.nama || '').toLowerCase().includes(searchTerm.toLowerCase()) || (b.mr || '').includes(searchTerm);
      const statusMatch = statusFilter === 'Semua Status' || b.statusBooking === statusFilter;
      return searchMatch && statusMatch;
    });
  }, [bookings, searchTerm, statusFilter]);

  const rowsToDisplay = useMemo(() => {
    let list = [...filteredBookings];
    list.sort((a, b) => {
      const dtA = a.tglJamBooking || '';
      const dtB = b.tglJamBooking || '';
      const [dA, tA] = dtA.split('T');
      const [dB, tB] = dtB.split('T');

      if (sortOrder === 'desc') {
        if (dA !== dB) return dB.localeCompare(dA); 
        return tA.localeCompare(tB); 
      } else {
        if (dA !== dB) return dA.localeCompare(dB); 
        return tB.localeCompare(tA); 
      }
    });

    const processed = [];
    let lastDate = null;
    let counter = 1;
    list.forEach(item => {
      const currentDate = item.tglJamBooking?.split('T')[0];
      if (currentDate !== lastDate) {
        processed.push({ isDivider: true, date: currentDate });
        lastDate = currentDate;
        counter = 1; 
      }
      processed.push({ ...item, displayNo: counter++ });
    });
    return processed;
  }, [filteredBookings, sortOrder]);

  const pagedRows = useMemo(() => rowsToDisplay.slice((currentPage - 1) * perPage, currentPage * perPage), [rowsToDisplay, currentPage]);

  const handleConfirmDelete = async () => {
    try {
      if (deleteModal.type === 'single') {
        await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'booking_patients', deleteModal.id));
        showNotify("Booking Dihapus");
      } else {
        const batch = writeBatch(db);
        selectedIds.forEach(id => batch.delete(doc(db, 'artifacts', appId, 'public', 'data', 'booking_patients', id)));
        await batch.commit();
        setSelectedIds([]);
        showNotify(`${selectedIds.length} Booking Dihapus Massal`);
      }
    } catch (e) { showNotify("Gagal Hapus", "error"); } finally { setDeleteModal(null); }
  };

  const handleExportBooking = async (name, range) => {
    if (!window.XLSX) { showNotify("Library Excel Belum Siap", "error"); return; }
    const filteredForExport = bookings.filter(b => {
      const bookingDate = b.tglJamBooking?.split('T')[0];
      return bookingDate >= range.start && bookingDate <= range.end;
    });
    if (filteredForExport.length === 0) { showNotify("Tidak ada data pada rentang ini", "error"); return; }
    const wb = window.XLSX.utils.book_new();
    const groupedByDate = {};
    filteredForExport.forEach(item => {
      const d = item.tglJamBooking?.split('T')[0] || 'Unknown';
      if (!groupedByDate[d]) groupedByDate[d] = [];
      groupedByDate[d].push(item);
    });
    Object.keys(groupedByDate).sort().forEach(dateKey => {
      const dataForSheet = groupedByDate[dateKey].map((b, i) => ({
        'NO': i + 1, 'NAMA PASIEN': b.nama?.toUpperCase(), 'NOMOR MR': b.mr, 'TANGGAL & JAM': b.tglJamBooking?.replace('T', ' '), 'ASAL PASIEN': b.asalPasien, 'UNIT': b.unit || '-', 'DIAGNOSA': b.diagnosa, 'DOKTER MERAWAT': b.dokterMerawat, 'STATUS BOOKING': b.statusBooking, 'KETERANGAN': b.keterangan || '-'
      }));
      const ws = window.XLSX.utils.aoa_to_sheet([[name.toUpperCase()]]);
      window.XLSX.utils.sheet_add_json(ws, dataForSheet, { origin: 'A3' });
      applySmartExcelFormatting(ws, dataForSheet);
      window.XLSX.utils.book_append_sheet(wb, ws, dateKey);
    });
    window.XLSX.writeFile(wb, `${name}.xlsx`); showNotify("Booking Excel Terunduh"); setShowDownloadModal(false);
  };

  const handleExportPdfBooking = async (name, range) => {
    if (!window.jspdf || !window.jspdf.jsPDF) { showNotify("Library PDF belum siap", "error"); return; }
    try {
      const filteredForExport = bookings.filter(b => {
        const bookingDate = b.tglJamBooking?.split('T')[0];
        return bookingDate >= range.start && bookingDate <= range.end;
      });
      if (filteredForExport.length === 0) { showNotify("Tidak ada data pada rentang ini", "error"); return; }
      
      filteredForExport.sort((a, b) => {
        const dateA = a.tglJamBooking || '';
        const dateB = b.tglJamBooking || '';
        return dateA.localeCompare(dateB);
      });

      const groupedByMonth = {};
      filteredForExport.forEach(item => {
        const d = item.tglJamBooking?.split('T')[0] || 'Unknown';
        const mKey = d.substring(0, 7); 
        if (!groupedByMonth[mKey]) groupedByMonth[mKey] = {};
        if (!groupedByMonth[mKey][d]) groupedByMonth[mKey][d] = [];
        groupedByMonth[mKey][d].push(item);
      });

      const sortedMonths = Object.keys(groupedByMonth).sort();
      const monthNames = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

      const { jsPDF } = window.jspdf;
      const doc = new jsPDF('landscape', 'pt', 'legal');

      sortedMonths.forEach((mKey, monthIndex) => {
        if (monthIndex > 0) doc.addPage('legal', 'landscape');
        
        const dateObj = new Date(mKey + "-01");
        const titleMonth = `${monthNames[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
        
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 58, 138);
        doc.text(`LAPORAN BOOKING PASIEN ICU - RSUD PASAR MINGGU`, 40, 40);
        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 116, 139);
        doc.text(`PERIODE: ${titleMonth.toUpperCase()}`, 40, 60);
        doc.setFontSize(9);
        doc.text(`Tanggal Cetak: ${new Date().toLocaleString('id-ID')}`, doc.internal.pageSize.getWidth() - 40, 40, { align: 'right' });

        const tableBody = [];
        const datesInMonth = Object.keys(groupedByMonth[mKey]).sort();
        
        let counter = 1;

        datesInMonth.forEach((dKey) => {
          const dateDisplay = new Date(dKey).toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
          
          tableBody.push([
            { content: `BOOKING TANGGAL: ${dateDisplay.toUpperCase()}`, colSpan: 9, styles: { fillColor: [238, 242, 255], textColor: [67, 56, 202], fontStyle: 'bold', halign: 'left' } }
          ]);
          
          counter = 1;

          groupedByMonth[mKey][dKey].forEach((b) => {
            tableBody.push([
              counter++,
              `${b.nama?.toUpperCase() || '-'}\nMR: ${b.mr || '-'}`,
              b.tglJamBooking?.replace('T', '\n') || '-',
              b.asalPasien?.toUpperCase() || '-',
              b.unit?.toUpperCase() || '-',
              b.diagnosa?.toUpperCase() || '-',
              b.dokterMerawat?.toUpperCase() || '-',
              b.statusBooking?.toUpperCase() || '-',
              b.keterangan?.toUpperCase() || '-'
            ]);
          });
        });

        doc.autoTable({
          startY: 80,
          head: [['NO', 'NAMA PASIEN & MR', 'TGL & JAM BOOKING', 'ASAL PASIEN', 'UNIT', 'DIAGNOSA', 'DOKTER MERAWAT', 'STATUS', 'KETERANGAN']],
          body: tableBody,
          theme: 'grid',
          styles: { font: 'helvetica', fontSize: 8, cellPadding: 4, lineColor: [226, 232, 240], lineWidth: 0.5, valign: 'middle' },
          headStyles: { fillColor: [248, 250, 252], textColor: [71, 85, 105], fontStyle: 'bold', halign: 'center' },
          columnStyles: {
            0: { halign: 'center', cellWidth: 30 },
            1: { cellWidth: 150 },
            2: { halign: 'center', cellWidth: 80 },
            3: { halign: 'center', cellWidth: 70 },
            4: { halign: 'center', cellWidth: 70 },
            5: { cellWidth: 200 },
            6: { cellWidth: 100 },
            7: { halign: 'center', cellWidth: 70 },
            8: { cellWidth: 130 }
          },
          margin: { left: 40, right: 40, bottom: 40 },
          didParseCell: function (data) {
            if (data.section === 'body' && data.column.index === 7 && data.row.raw.length > 1) {
              const val = data.cell.raw;
              if (val === 'MASUK') { data.cell.styles.textColor = [16, 185, 129]; data.cell.styles.fontStyle = 'bold'; }
              else if (val === 'BATAL MASUK') { data.cell.styles.textColor = [239, 68, 68]; data.cell.styles.fontStyle = 'bold'; }
            }
          }
        });
      });

      doc.save(`${name}.pdf`);
      showNotify("PDF Booking Berhasil Diunduh");
      setShowPdfModal(false);
    } catch(e) {
      showNotify("Gagal Eksport PDF", "error");
      console.error(e);
    }
  };

  return (
    <div className="space-y-4 text-center">
      <div className="flex flex-col md:flex-row gap-3 items-center justify-between text-left">
        <div className="flex flex-wrap items-center gap-3 w-full md:max-w-7xl text-left">
          <div className="relative flex-1 min-w-[200px] group text-left"><Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><input type="text" placeholder="Cari Nama / MR..." className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-2xl outline-none font-bold text-xs shadow-sm" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} /></div>
          <div className="flex bg-white p-1 rounded-xl border border-slate-100 shadow-sm items-center gap-1 text-left">
            <Filter size={14} className="text-indigo-400 ml-2" />
            <select className="bg-transparent font-black text-[9px] uppercase outline-none text-slate-600 px-2 cursor-pointer h-8" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
              <option value="Semua Status">Semua Status</option>
              <option value="Masuk">Masuk</option>
              <option value="Tetap Booking">Tetap Booking</option>
              <option value="Batal Masuk">Batal Masuk</option>
            </select>
          </div>
          <div className="flex bg-white p-1 rounded-xl border border-slate-100 shadow-sm text-center">
            <button onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')} className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all flex items-center gap-2 bg-indigo-600 text-white shadow-md text-center`}><CalendarSearch size={12}/> Tgl Booking {sortOrder === 'asc' ? <SortAsc size={10}/> : <SortDesc size={10}/>}</button>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowDownloadModal(true)} className="p-3 bg-emerald-600 text-white rounded-2xl shadow-lg hover:bg-emerald-700 transition-all flex items-center gap-2 text-center"><FileSpreadsheet size={18} /><span className="text-[10px] font-black uppercase hidden sm:block">Excel</span></button>
            <button onClick={() => setShowPdfModal(true)} className="p-3 bg-red-600 text-white rounded-2xl shadow-lg hover:bg-red-700 transition-all flex items-center gap-2 text-center"><FileText size={18} /><span className="text-[10px] font-black uppercase hidden sm:block">PDF</span></button>
          </div>
          <button onClick={() => setIsLocked(!isLocked)} className={`p-3 rounded-2xl transition-all shadow-sm flex items-center gap-2 text-center ${isLocked ? 'bg-blue-600 text-white' : 'bg-white text-slate-400 border border-slate-100'}`}>{isLocked ? <Lock size={18} /> : <Unlock size={18} />}<span className="text-[10px] font-black uppercase">{isLocked ? 'Locked' : 'Unlocked'}</span></button>
        </div>
        {selectedIds.length > 0 && (<button onClick={() => setDeleteModal({ type: 'bulk' })} className="px-4 py-3 bg-red-50 text-red-600 font-black rounded-xl flex items-center gap-1.5 text-[10px] uppercase border border-red-100 shadow-sm text-center"><Trash2 size={14} /> HAPUS ({selectedIds.length})</button>)}
      </div>
      <div className="bg-white rounded-[1.5rem] shadow-xl border border-slate-100 overflow-hidden overflow-x-auto custom-scrollbar relative text-center">
        <table className="w-full border-collapse table-auto min-w-[1300px] text-center">
          <thead className="bg-slate-50 border-b border-slate-200 text-[8px] font-black text-slate-400 uppercase sticky top-0 z-10 text-center">
            <tr>
              {BOOKING_COL_DEFS.map(col => (
                <th key={col.id} style={{ width: col.width }} className={`p-3 border-r border-slate-100 ${col.id === 'action' && isLocked ? 'sticky right-0 bg-slate-50 z-20 border-l border-slate-200 shadow-[-5px_0_10_rgba(0,0,0,0.05)]' : ''}`}>
                  {col.id === 'no' ? (
                    <input 
                      type="checkbox" 
                      className="w-3 h-3 rounded"
                      onChange={e => setSelectedIds(e.target.checked ? filteredBookings.map(b => b.id) : [])}
                      checked={selectedIds.length > 0 && selectedIds.length === filteredBookings.length}
                    />
                  ) : col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50 text-[10px] text-center">
            {pagedRows.map((row, i) => {
              if (row.isDivider) {
                return (
                  <tr key={`div-${row.date}`} className="bg-indigo-50/50 text-left">
                    <td colSpan={BOOKING_COL_DEFS.length} className="p-2 text-left pl-6 text-left">
                      <div className="flex items-center gap-2 text-left"><CalendarDays size={14} className="text-indigo-600" /><span className="font-black text-indigo-700 uppercase tracking-[0.2em] text-[10px] text-left">Booking: {new Date(row.date).toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span></div>
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={row.id} onClick={() => setActiveRowId(row.id)} className={`transition-all duration-200 h-14 group cursor-pointer ${activeRowId === row.id ? 'bg-blue-200/90 outline outline-2 outline-blue-500 shadow-md relative z-10 font-semibold' : 'hover:bg-indigo-50/40'}`}>
                  <td className={`p-2 border-r border-slate-100 transition-all duration-200 ${activeRowId === row.id ? 'border-l-[6px] border-l-blue-600' : 'border-l-[6px] border-l-transparent group-hover:border-l-blue-300'}`}>
                    <input 
                      type="checkbox" className="w-3 h-3 rounded cursor-pointer"
                      checked={selectedIds.includes(row.id)}
                      onChange={() => setSelectedIds(prev => prev.includes(row.id) ? prev.filter(x => x !== row.id) : [...prev, row.id])}
                    />
                    <span className="ml-1 font-black text-slate-300">{row.displayNo}</span>
                  </td>
                  <td className="p-2 border-r border-slate-100 font-black text-slate-800 uppercase text-left pl-4">{row.nama}<br/><span className="text-indigo-600 text-[8px]">MR: {row.mr || '-'}</span></td>
                  <td className="p-2 border-r border-slate-100 font-black text-indigo-500 whitespace-pre-wrap text-center">{row.tglJamBooking?.replace('T', ' ')}</td>
                  <td className="p-2 border-r border-slate-100 font-bold uppercase text-center">{row.asalPasien}</td>
                  <td className="p-2 border-r border-slate-100 font-black text-blue-600 uppercase text-center">{row.unit || '-'}</td>
                  <td className="p-2 border-r border-slate-100 uppercase px-4 text-left line-clamp-2 h-14">{row.diagnosa}</td>
                  <td className="p-2 border-r border-slate-100 font-bold text-center">{row.dokterMerawat}</td>
                  <td className="p-2 border-r border-slate-100 text-center"><span className={`px-2 py-0.5 rounded-lg text-[8px] font-black uppercase border ${row.statusBooking === 'Masuk' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : row.statusBooking === 'Batal Masuk' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-slate-50 text-slate-600 border-slate-100'}`}>{row.statusBooking || 'TIDAK SET'}</span></td>
                  <td className="p-2 border-r border-slate-100 text-[9px] font-bold text-slate-500 uppercase italic text-center">{row.keterangan || '-'}</td>
                  <td className={`p-1 text-center bg-white group-hover:bg-slate-50 transition-colors ${isLocked ? 'sticky right-0 z-10 border-l border-slate-100 shadow-[-5px_0_10_rgba(0,0,0,0.03)]' : ''}`}><div className="flex gap-1 justify-center text-center"><button onClick={() => onEdit(row)} className="p-1.5 text-emerald-600 bg-emerald-50 rounded-lg"><Edit3 size={14}/></button><button onClick={() => setDeleteModal({ type: 'single', id: row.id })} className="p-1.5 text-red-600 bg-red-50 rounded-lg"><Trash2 size={14}/></button></div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination total={rowsToDisplay.length} perPage={perPage} current={currentPage} onChange={setCurrentPage} />
      <AnimatePresence>{deleteModal && (<SmartDeleteModal onConfirm={handleConfirmDelete} onCancel={() => setDeleteModal(null)} />)}</AnimatePresence>
      <AnimatePresence>{showDownloadModal && (
        <SmartBookingDownloadModal 
          onClose={() => setShowDownloadModal(false)} 
          onExport={handleExportBooking} 
        />
      )}</AnimatePresence>
      <AnimatePresence>{showPdfModal && (
        <SmartBookingPdfDownloadModal 
          onClose={() => setShowPdfModal(false)} 
          onExport={handleExportPdfBooking} 
        />
      )}</AnimatePresence>
    </div>
  );
}

function DashboardUI({ patients, infoNotes, showNotify, masters }) {
  const currentShiftData = getCurrentShift();
  const todayDate = new Date().toISOString().split('T')[0];
  
  const [selectedDate, setSelectedDate] = useState(todayDate);
  const [selectedShift, setSelectedShift] = useState(currentShiftData.id);
  const [showNoteModal, setShowNoteModal] = useState(null);
  const [noteText, setNoteText] = useState('');

  const isLive = selectedDate === todayDate && selectedShift === currentShiftData.id;

  const stats = useMemo(() => {
    const { start, end } = getShiftTimeRange(selectedDate, selectedShift);
    const currentMonthNum = new Date(selectedDate).getMonth();
    const currentMonthName = new Intl.DateTimeFormat('id-ID', { month: 'long' }).format(new Date(selectedDate));

    const icuMonth = patients.filter(p => p.tglJamMasuk && new Date(p.tglJamMasuk).getMonth() === currentMonthNum).length;
    
    const referenceTimeForActive = isLive ? new Date() : end;
    const activePatientsNow = patients.filter(p => {
      const masuk = p.tglJamMasuk ? new Date(p.tglJamMasuk) : null;
      const keluar = p.tglJamKeluar ? new Date(p.tglJamKeluar) : null;
      if (!masuk || masuk > referenceTimeForActive) return false;
      return !p.statusKeluar || (keluar && keluar > referenceTimeForActive);
    });

    const totalSekarang = activePatientsNow.length;

    const totalSebelumnya = patients.filter(p => {
      const masuk = p.tglJamMasuk ? new Date(p.tglJamMasuk) : null;
      const keluar = p.tglJamKeluar ? new Date(p.tglJamKeluar) : null;
      if (!masuk) return false;
      return masuk < start && (!keluar || keluar >= start);
    }).length;

    const baruShift = patients.filter(p => {
      const masuk = p.tglJamMasuk ? new Date(p.tglJamMasuk) : null;
      return masuk >= start && masuk < end;
    }).length;

    const keluarShift = patients.filter(p => {
      const keluar = p.tglJamKeluar ? new Date(p.tglJamKeluar) : null;
      return keluar >= start && keluar < end;
    }).length;

    // BOR CALCULATION (ICU Kapasitas 17, HCU Kapasitas 1)
    const totalHcuSekarang = activePatientsNow.filter(p => p.bed?.includes('HCU') || p.unit?.includes('HCU')).length;
    const totalIcuSekarang = activePatientsNow.filter(p => !p.bed?.includes('HCU') && !p.unit?.includes('HCU')).length;

    const borIcuPercentage = ((totalIcuSekarang / 17) * 100).toFixed(1);
    const borHcuPercentage = ((totalHcuSekarang / 1) * 100).toFixed(1);

    return [
      { label: `Pasien ICU (${currentMonthName})`, value: icuMonth, gradient: 'from-blue-600 to-indigo-600', icon: CalendarDays }, 
      { label: 'Pasien Aktif (Dirawat)', value: totalSekarang, gradient: 'from-amber-500 to-orange-600', icon: ArrowDownCircle }, 
      { label: 'BOR ICU & HCU', value: 'bor_special', borIcu: borIcuPercentage, borHcu: borHcuPercentage, gradient: 'from-pink-600 to-rose-600', icon: Percent }, 
      { label: 'Data Operan (Awal Shift)', value: totalSebelumnya, gradient: 'from-violet-600 to-purple-700', icon: UserCheck }, 
      { label: 'Pasien Baru (Shift)', value: baruShift, gradient: 'from-emerald-500 to-teal-600', icon: ArrowUpCircle }, 
      { label: 'Pasien Keluar (Shift)', value: keluarShift, gradient: 'from-red-500 to-rose-600', icon: UserMinus }
    ];
  }, [patients, selectedDate, selectedShift, isLive, masters]);

  const trendData = useMemo(() => {
    const counts = {};
    const last30Days = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const ds = d.toISOString().split('T')[0];
      last30Days.push(ds);
      counts[ds] = 0;
    }
    patients.forEach(p => {
      if (p.tglJamMasuk) {
        const ds = p.tglJamMasuk.split('T')[0];
        if (counts.hasOwnProperty(ds)) counts[ds]++;
      }
    });
    const maxValue = Math.max(...Object.values(counts), 5);
    return last30Days.map(date => ({
      date, count: counts[date], height: (counts[date] / maxValue) * 100
    }));
  }, [patients]);

  const handleSaveNote = async () => {
    if (!noteText.trim()) return;
    try {
      const colRef = collection(db, 'artifacts', appId, 'public', 'data', 'info_notes');
      if (showNoteModal && showNoteModal.id) { await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'info_notes', showNoteModal.id), { text: noteText }); } 
      else { await addDoc(colRef, { text: noteText, createdAt: new Date().toISOString() }); }
      showNotify("Informasi Tersimpan"); setShowNoteModal(null); setNoteText('');
    } catch (e) { showNotify("Gagal Simpan Note", "error"); }
  };
  
  const handleDeleteNote = async (id) => { try { await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'info_notes', id)); showNotify("Informasi Dihapus"); } catch (e) { showNotify("Gagal Hapus Note", "error"); } };

  return (
    <div className="space-y-6 pb-10 text-center">
      <div className="bg-slate-900 rounded-[2.5rem] p-6 relative overflow-hidden border border-blue-500/20 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4 relative z-10 text-left w-full md:w-auto">
          <div className="p-3 bg-blue-500/20 rounded-2xl border border-blue-500/50 text-center">
            <Clock size={22} className={`text-blue-400 ${isLive ? 'animate-pulse' : ''} mx-auto`} />
          </div>
          <div className="leading-tight text-left">
            <p className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-1">{isLive ? 'Live Status' : 'History View'}</p>
            <h2 className="text-lg font-black text-white leading-none uppercase tracking-tighter">SHIFT {selectedShift.toUpperCase()} <span className="text-slate-400 text-[9px] font-medium ml-1">({selectedShift === 'pagi' ? '07:00-14:00' : selectedShift === 'siang' ? '14:00-21:00' : '21:00-07:00'})</span></h2>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 relative z-10 w-full md:w-auto justify-end">
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 p-1.5 rounded-xl text-center">
             <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="bg-transparent text-[10px] font-black uppercase text-blue-100 outline-none px-2 cursor-pointer text-center" />
          </div>
          <div className="flex items-center gap-1 bg-white/5 border border-white/10 p-1 rounded-xl text-center">
             {['pagi', 'siang', 'malam'].map(s => (
               <button key={s} onClick={() => setSelectedShift(s)} className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all ${selectedShift === s ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-white/10'}`}>{s}</button>
             ))}
          </div>
          {!isLive && (
             <button onClick={() => { setSelectedDate(todayDate); setSelectedShift(currentShiftData.id); }} className="p-2 bg-red-500 text-white rounded-xl shadow-lg hover:bg-red-600 transition-all flex items-center gap-2 text-center" title="Kembali ke Live">
                <RotateCcw size={14} />
                <span className="text-[8px] font-black uppercase">Live</span>
             </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 text-center">{stats.map((stat, idx) => (
          <div key={idx} className={`bg-gradient-to-br ${stat.gradient} p-4 rounded-3xl shadow-lg flex flex-col justify-between h-36 relative overflow-hidden group shadow-black/5 text-center`}>
              <div className="absolute -right-3 -bottom-3 opacity-10 text-white"><stat.icon size={80} /></div>
              <div className="flex justify-between items-start relative z-10">
                  <div className="p-2 bg-white/20 backdrop-blur-md rounded-xl text-white"><stat.icon size={20} /></div>
                  {stat.value === 'bor_special' ? (
                      <div className="flex flex-col text-right leading-none">
                          <span className="text-sm font-black text-white">ICU: {stat.borIcu}%</span>
                          <span className="text-sm font-black text-white mt-1">HCU: {stat.borHcu}%</span>
                      </div>
                  ) : (
                      <span className="text-2xl font-black text-white">{String(stat.value)}</span>
                  )}
              </div>
              <p className="text-white/90 font-black text-[9px] uppercase tracking-wider leading-tight relative z-10 pr-2 leading-none text-left">{stat.label}</p>
          </div>
      ))}</div>
      <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm text-center">
        <div className="flex items-center justify-between mb-8 text-left">
          <div className="flex items-center gap-3 text-left">
             <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl"><TrendingUp size={20} /></div>
             <div className="text-left leading-none">
                <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Tren Kunjungan Pasien</h3>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">Total Registrasi (30 Hari Terakhir)</p>
             </div>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-lg text-center">
             <BarChart3 size={12} className="text-slate-400" />
             <span className="text-[9px] font-black text-slate-500 uppercase">Interactive Analytics</span>
          </div>
        </div>
        <div className="h-48 w-full flex items-end justify-between gap-1 group/chart pt-4 text-center">
           {trendData.map((day, idx) => (
             <div key={idx} className="flex-1 h-full flex flex-col items-center justify-end group relative text-center">
               <div className="absolute bottom-full mb-2 bg-slate-900 text-white text-[8px] font-black px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20 pointer-events-none shadow-xl border border-white/10">
                  {day.date}: {day.count} Pasien
               </div>
               <motion.div initial={{ height: 0 }} animate={{ height: `${day.height}%` }} className={`w-full rounded-t-lg transition-all duration-500 cursor-help ${day.count > 0 ? 'bg-indigo-500 group-hover:bg-blue-600' : 'bg-slate-100'}`} />
               {idx % 5 === 0 && <span className="absolute -bottom-6 text-[7px] font-bold text-slate-400 uppercase">{day.date.split('-').slice(1).reverse().join('/')}</span>}
             </div>
           ))}
        </div>
      </div>
      <div className="space-y-4 mt-8 text-center">
        <div className="flex items-center justify-between px-2 text-left">
          <div className="flex items-center gap-2 text-left">
            <MessageSquareQuote className="text-blue-600" size={20} />
            <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Papan Informasi ICU</h3>
          </div>
          <button onClick={() => { setShowNoteModal({ id: null }); setNoteText(''); }} className="px-4 py-2 bg-blue-600 text-white text-[10px] font-black rounded-xl uppercase shadow-lg shadow-blue-100 flex items-center gap-2 hover:bg-blue-700 transition-all text-center"><Plus size={14} /> Tambah Info</button>
        </div>
        <div className="space-y-3 text-left">
          <AnimatePresence initial={false}>
            {infoNotes && infoNotes.length > 0 ? infoNotes.map((note) => (
              <motion.div key={note.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-[2rem] p-6 shadow-xl relative overflow-hidden group text-left">
                <div className="absolute -right-10 -top-10 opacity-5 text-white group-hover:rotate-12 transition-transform duration-500"><Pin size={150} /></div>
                <div className="flex justify-between items-start gap-6 relative z-10 text-left">
                  <div className="flex-1 text-left text-left">
                    <div className="flex items-center gap-2 mb-2 text-left">
                      <span className="px-2 py-0.5 bg-white/20 rounded-md text-[8px] font-black text-white uppercase tracking-widest">PENTING</span>
                      <span className="text-[8px] font-bold text-blue-100 uppercase">{note.createdAt ? new Date(note.createdAt).toLocaleString('id-ID') : '-'}</span>
                    </div>
                    <p className="text-sm font-bold text-white uppercase leading-relaxed max-w-4xl whitespace-pre-wrap">{note.text}</p>
                  </div>
                  <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity text-center">
                    <button onClick={() => { setShowNoteModal(note); setNoteText(note.text); }} className="p-2 bg-white/20 hover:bg-white/30 rounded-xl text-white backdrop-blur-md transition-all text-center"><Edit3 size={14} /></button>
                    <button onClick={() => handleDeleteNote(note.id)} className="p-2 bg-white/20 hover:bg-red-50 rounded-xl text-white backdrop-blur-md transition-all text-center"><Trash size={14} /></button>
                  </div>
                </div>
              </motion.div>
            )) : (
              <div className="bg-white border-2 border-dashed border-slate-200 rounded-[2.5rem] p-12 text-center text-center">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                  <Pin size={32} />
                </div>
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Belum Ada Informasi Penting</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <AnimatePresence>{showNoteModal && (<div className="fixed inset-0 z-[600] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm text-center"><motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white w-full max-w-xl rounded-[2.5rem] p-8 shadow-2xl text-center"><div className="flex justify-between items-center mb-6"><div className="flex items-center gap-3 text-left"><div className="p-2 bg-blue-100 rounded-xl text-blue-600"><Pin size={20} /></div><h4 className="text-lg font-black text-slate-800 uppercase tracking-tight">{showNoteModal.id ? 'Edit Informasi' : 'Tambah Informasi Baru'}</h4></div><button onClick={() => setShowNoteModal(null)} className="p-2 hover:bg-slate-100 rounded-xl text-center"><X size={20} /></button></div><div className="space-y-4 text-center"><div className="space-y-1 text-left"><label className="text-[9px] font-black text-slate-500 uppercase ml-2 tracking-widest">Isi Informasi</label><textarea className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-[1.5rem] font-bold text-sm h-40 uppercase outline-none focus:ring-4 focus:ring-blue-100" placeholder="Ketik informasi penting di sini..." value={noteText} onChange={(e) => setNoteText(e.target.value)} /></div><button onClick={handleSaveNote} className="w-full py-4 bg-blue-600 text-white font-black rounded-2xl text-[10px] uppercase shadow-lg shadow-blue-100 hover:bg-blue-700 active:scale-95 transition-all text-center">SIMPAN KE PAPAN INFORMASI</button></div></motion.div></div>)}</AnimatePresence>
    </div>
  );
}

function BookingFormUI({ showNotify, editData, masters, onCancel, onSaved, onRedirectToReg, patients, bookingPatients }) {
  const allDoctors = useMemo(() => [...(masters.dpjp || []), ...(masters.raber || [])].filter((v, i, a) => a.indexOf(v) === i), [masters.dpjp, masters.raber]);
  
  const INITIAL = { nama: '', mr: '', tglJamBooking: '', asalPasien: '', unit: '', diagnosa: '', dokterMerawat: '', statusBooking: '', keterangan: '' };
  const [formData, setFormData] = useState(() => ({ ...INITIAL, ...(editData || {}) }));
  const [unitMain, setUnitMain] = useState(() => {
      if (editData?.unit?.includes('HCU')) return 'HCU';
      if (editData?.unit?.includes('PICU')) return 'PICU';
      if (editData?.unit) return 'ICU';
      return '';
  });
  const [subUnit, setSubUnit] = useState(() => {
      if (editData?.unit?.includes('Neuro')) return 'Neuro';
      if (editData?.unit?.includes('Bedah')) return 'Bedah';
      if (editData?.unit?.includes('Metabolik')) return 'Metabolik / Paru';
      return '';
  });
  const [loading, setLoading] = useState(false);
  const [duplicateModal, setDuplicateModal] = useState(null);
  
  useEffect(() => {
      const finalUnit = unitMain === 'ICU' ? (subUnit ? `ICU ${subUnit}` : 'ICU') : unitMain;
      setFormData(prev => ({ ...prev, unit: finalUnit }));
  }, [unitMain, subUnit]);

  useEffect(() => { if (formData.mr && formData.mr.length >= 3 && !editData) { const existing = patients.find(p => p.mr === formData.mr) || bookingPatients.find(b => b.mr === formData.mr); if (existing && existing.nama !== formData.nama) { setDuplicateModal(existing); } } }, [formData.mr]);
  
  const save = async (e) => {
    e.preventDefault(); 
    if (!formData.statusBooking) { showNotify("Pilih Status Booking Terlebih Dahulu", "error"); return; }
    setLoading(true);
    try {
      const colRef = collection(db, 'artifacts', appId, 'public', 'data', 'booking_patients');
      if (editData && editData.id) { await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'booking_patients', editData.id), formData); } 
      else { await addDoc(colRef, { ...formData, createdAt: new Date().toISOString() }); }
      showNotify("Data Booking Tersimpan"); if (formData.statusBooking === 'Masuk') { onRedirectToReg(formData); } else { onSaved(); }
    } catch (e) { showNotify("Gagal Simpan Cloud", "error"); } finally { setLoading(false); }
  };
  
  return (
    <div className="max-w-4xl mx-auto text-center">
      <div className="bg-white rounded-[2rem] shadow-2xl overflow-hidden border border-slate-100 relative text-left">
        <button onClick={onCancel} className="absolute top-6 right-6 p-2 text-slate-400 hover:text-red-500 transition-all z-20 text-center"><X size={20} /></button>
        <div className="bg-indigo-600 p-8 text-white flex items-center gap-4 text-left"><div className="bg-white/20 p-3 rounded-2xl text-center"><CalendarPlus size={24} /></div><div className="text-left"><h3 className="text-xl font-black uppercase tracking-tight leading-none">{editData ? 'Edit Booking' : 'Booking Pasien'}</h3></div></div>
        <form onSubmit={save} className="p-8 space-y-8 text-left">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-center">
            <Field label="No. MR" required value={formData.mr} onChange={v => setFormData({...formData, mr: v})} />
            <Field label="Nama Pasien" required value={formData.nama} onChange={v => setFormData({...formData, nama: v})} />
            <Field label="Tanggal & Jam Booking" type="datetime-local" required value={formData.tglJamBooking} onChange={v => setFormData({...formData, tglJamBooking: v})} />
            <Select label="Asal Pasien" value={formData.asalPasien} options={masters.locations} onChange={v => setFormData({...formData, asalPasien: v})} />
            <Select label="Unit" required value={unitMain} options={['ICU', 'HCU', 'PICU']} onChange={v => { setUnitMain(v); if(v!=='ICU') setSubUnit(''); }} />
            {unitMain === 'ICU' && (
              <Select label="Sub Unit ICU" required value={subUnit} options={['Neuro', 'Bedah', 'Metabolik / Paru']} onChange={setSubUnit} />
            )}
            <SearchableSelect label="Dokter yang Merawat" value={formData.dokterMerawat} options={allDoctors} onChange={v => setFormData({...formData, dokterMerawat: v})} />
            <div className="md:col-span-2 text-center">
                <Select label="Status Booking" required value={formData.statusBooking} options={['Masuk', 'Batal Masuk', 'Tetap Booking']} onChange={v => setFormData({...formData, statusBooking: v})} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-5 text-left">
            <div className="space-y-1 text-left">
              <label className="text-[9px] font-black text-slate-500 uppercase ml-1 tracking-widest">Diagnosa</label>
              <textarea 
                placeholder="Ketik Diagnosa Lengkap..." 
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-xs h-32 uppercase outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-sm"
                value={formData.diagnosa} 
                onChange={e => setFormData({...formData, diagnosa: e.target.value})} 
              />
            </div>
            <Field label="Keterangan / Pesan Khusus" value={formData.keterangan} onChange={v => setFormData({...formData, keterangan: v})} />
          </div>
          <div className="flex gap-4 pt-4 text-center"><button type="button" onClick={onCancel} className="flex-1 py-4 bg-slate-100 text-slate-500 font-black rounded-2xl uppercase text-[10px] text-center">Batal</button><button type="submit" disabled={loading} className="flex-[2] py-4 bg-indigo-600 text-white font-black rounded-2xl shadow-lg uppercase text-xs text-center">{loading ? "Memproses..." : "Simpan Data Booking"}</button></div>
        </form>
      </div>
      <AnimatePresence>{duplicateModal && (<SmartDuplicateModal patient={duplicateModal} onConfirm={(p) => { setFormData({...formData, nama: p.nama}); setDuplicateModal(null); }} onCancel={() => setDuplicateModal(null)} />)}</AnimatePresence>
    </div>
  );
}

function RegistrationFormUI({ showNotify, patients, editData, masters, onCancel, onSaved, receiverNurse, bookingPatients }) {
  const INITIAL = { nama:'', mr:'', tglLahir:'', usia:'', jenisKelamin:'', agama:'', bed:'', tglJamMasuk:'', asalPasien:'', jaminan:'', diagnosaMedis:'', dpjp:'', raber:[], oksigenAwal:'', oksigenSaatIni:'', trakheostomi:'', tglTrakheostomi:[], intubasi:'', tglIntubasi:[], ekstubasi:'', tglEkstubasi:[], alamat:'', noTelp:'', riwayatVaksin:'', keterangan:'', tglJamKeluar:'', statusKeluar:'', perawatPenerima: receiverNurse || '' };
  const [formData, setFormData] = useState(() => ({ ...INITIAL, ...(editData || {}), perawatPenerima: editData?.perawatPenerima || receiverNurse || '' }));
  
  const [intubDates, setIntubDates] = useState(() => Array.isArray(editData?.tglIntubasi) ? (editData.tglIntubasi.length > 0 ? editData.tglIntubasi : ['']) : (editData?.tglIntubasi ? [editData.tglIntubasi] : ['']));
  const [ekstubDates, setEkstubDates] = useState(() => Array.isArray(editData?.tglEkstubasi) ? (editData.tglEkstubasi.length > 0 ? editData.tglEkstubasi : ['']) : (editData?.tglEkstubasi ? [editData.tglEkstubasi] : ['']));
  const [trachDates, setTrachDates] = useState(() => Array.isArray(editData?.tglTrakheostomi) ? (editData.tglTrakheostomi.length > 0 ? editData.tglTrakheostomi : ['']) : (editData?.tglTrakheostomi ? [editData.tglTrakheostomi] : ['']));

  const [loading, setLoading] = useState(false);
  const [duplicateModal, setDuplicateModal] = useState(null);
  
  const [tempUnitMain, setTempUnitMain] = useState(() => {
      if (editData?.unit?.includes('HCU') || editData?.bed?.includes('HCU')) return 'HCU';
      if (editData?.unit?.includes('PICU') || editData?.bed?.includes('PICU')) return 'PICU';
      if (editData?.unit || editData?.bed) return 'ICU';
      return '';
  });
  const [tempSubUnit, setTempSubUnit] = useState(() => {
      if (editData?.unit?.includes('Neuro') || editData?.bed?.includes('Neuro')) return 'Neuro';
      if (editData?.unit?.includes('Bedah') || editData?.bed?.includes('Bedah')) return 'Bedah';
      if (editData?.unit?.includes('Metabolik') || editData?.bed?.includes('Metabolik')) return 'Metabolik / Paru';
      return '';
  });

  const currentBedsFromMaster = masters.beds || DEFAULT_BEDS;
  const [tempBaseBed, setTempBaseBed] = useState(() => {
      if (editData?.bed) {
          const match = currentBedsFromMaster.find(b => editData.bed.startsWith(b));
          return match || editData.bed;
      }
      return '';
  });

  useEffect(() => { 
    if (editData) { 
      setFormData(prev => ({ ...INITIAL, ...prev, ...editData, raber: Array.isArray(editData.raber) ? editData.raber : [], perawatPenerima: editData.perawatPenerima || prev.perawatPenerima })); 
      if (editData.bed) {
        const matchingBase = currentBedsFromMaster.find(b => {
          const regex = new RegExp('^' + b.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&') + '(\\s|$)');
          return regex.test(editData.bed);
        });
        if (matchingBase) setTempBaseBed(matchingBase);
      }
    } 
  }, [editData, currentBedsFromMaster]);

  useEffect(() => {
      const finalUnit = tempUnitMain === 'ICU' ? (tempSubUnit ? `ICU ${tempSubUnit}` : 'ICU') : tempUnitMain;
      if (tempBaseBed) {
          setFormData(f => ({ ...f, bed: finalUnit ? `${tempBaseBed} ${finalUnit}` : tempBaseBed, unit: finalUnit }));
      }
  }, [tempBaseBed, tempUnitMain, tempSubUnit]);

  useEffect(() => { if (formData.mr && formData.mr.length >= 3 && !editData?.id) { const existing = patients.find(p => p.mr === formData.mr) || bookingPatients.find(b => b.mr === formData.mr); if (existing && existing.nama !== formData.nama) { setDuplicateModal(existing); } } }, [formData.mr]);
  
  const availableBeds = useMemo(() => {
    const occupiedBases = patients
      .filter(p => !p.statusKeluar && (!editData || p.id !== editData.id))
      .map(p => {
        const match = currentBedsFromMaster.find(b => {
          const regex = new RegExp('^' + b.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&') + '(\\s|$)');
          return regex.test(p.bed);
        });
        return match || null;
      }); 
    return currentBedsFromMaster.filter(b => !occupiedBases.includes(b));
  }, [patients, editData, currentBedsFromMaster]);

  useEffect(() => { if (formData.tglLahir) { const birth = new Date(formData.tglLahir); const now = new Date(); let age = now.getFullYear() - birth.getFullYear(); if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) age--; setFormData(f => ({ ...f, usia: age >= 0 ? `${age} Thn` : '' })); } }, [formData.tglLahir]);
  const useDuplicateData = (p) => { setFormData({ ...formData, nama: p.nama || '', tglLahir: p.tglLahir || '', jenisKelamin: p.jenisKelamin || '', agama: p.agama || '', alamat: p.alamat || '', noTelp: p.noTelp || '' }); setDuplicateModal(null); };
  
  const save = async () => { 
    if (!formData.bed) { showNotify("Pilih Bed Terlebih Dahulu", "error"); return; }
    setLoading(true); 
    try { 
      const finalData = { 
        ...formData, 
        tglIntubasi: formData.intubasi === 'Iya' ? intubDates.filter(d => !!d) : [], 
        tglEkstubasi: formData.ekstubasi === 'Iya' ? ekstubDates.filter(d => !!d) : [],
        tglTrakheostomi: formData.trakheostomi === 'Iya' ? trachDates.filter(d => !!d) : []
      };
      const colRef = collection(db, 'artifacts', appId, 'public', 'data', 'patients'); 
      if (editData && editData.id) await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'patients', editData.id), finalData); 
      else await addDoc(colRef, { ...finalData, createdAt: new Date().toISOString() }); 
      showNotify("Berhasil Tersimpan"); onSaved(); 
    } catch (e) { showNotify("Gagal Tersimpan", "error"); } finally { setLoading(false); } 
  };

  const handleArrayUpdate = (setter, idx, val) => {
    setter(prev => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
  };
  
  return (
    <div className="max-w-4xl mx-auto text-center">
      <div className="bg-white rounded-[2rem] shadow-2xl overflow-hidden border border-slate-100 relative text-left">
        <button onClick={onCancel} className="absolute top-6 right-6 p-2 text-slate-400 hover:text-red-500 transition-all z-20 text-center"><X size={20} /></button>
        <div className="bg-blue-600 p-8 text-white flex items-center gap-4 text-left"><div className="bg-white/20 p-3 rounded-2xl text-center"><UserPlus size={24} /></div><div className="text-left text-left"><h3 className="text-xl font-black uppercase tracking-tight leading-none">{editData && editData.id ? 'Edit Pasien' : 'Registrasi Pasien'}</h3></div></div>
        <form onSubmit={(e) => { e.preventDefault(); save(); }} className="p-8 space-y-8 text-left">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-center">
            <Field label="No. MR" required value={formData.mr} onChange={v => setFormData({...formData, mr: v})} />
            <Field label="Nama Pasien" required value={formData.nama} onChange={v => setFormData({...formData, nama: v})} />
            <div className="grid grid-cols-2 gap-3 text-center">
              <Field label="Tgl Lahir" type="date" required value={formData.tglLahir} onChange={v => setFormData({...formData, tglLahir: v})} />
              <Field label="Usia" disabled value={formData.usia} />
            </div>
            
            <Select label="Jenis Kelamin" required value={formData.jenisKelamin} onChange={v => setFormData({...formData, jenisKelamin: v})} options={['Laki-laki', 'Perempuan']} />
            <Select label="Agama" value={formData.agama} onChange={v => setFormData({...formData, agama: v})} options={['Islam', 'Kristen', 'Budha', 'Hindu']} />
            <Select label="Bed" required value={tempBaseBed} onChange={v => { setTempBaseBed(v); }} options={availableBeds} />
            <Select label="Unit" required value={tempUnitMain} onChange={v => { setTempUnitMain(v); if(v!=='ICU') setTempSubUnit(''); }} options={['ICU', 'HCU', 'PICU']} />
            {tempUnitMain === 'ICU' && (
              <Select label="Sub Unit ICU" required value={tempSubUnit} onChange={setTempSubUnit} options={['Neuro', 'Bedah', 'Metabolik / Paru']} />
            )}
            <Field label="Tgl Masuk ICU" type="datetime-local" required value={formData.tglJamMasuk} onChange={v => setFormData({...formData, tglJamMasuk: v})} />
            <Field label="Perawat Penerima" disabled value={formData.perawatPenerima} />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-center">
            <Select label="Asal Pasien" required value={formData.asalPasien} onChange={v => setFormData({...formData, asalPasien: v})} options={masters.locations} />
            <Select label="Jaminan" required value={formData.jaminan} onChange={e => setFormData({...formData, jaminan: e})} options={masters.jaminan} />
            <SearchableSelect label="Dokter DPJP" required value={formData.dpjp} onChange={v => setFormData({...formData, dpjp: v})} options={masters.dpjp} />
            <Select label="Oksigen Awal" required value={formData.oksigenAwal} onChange={v => setFormData({...formData, oksigenAwal: v})} options={masters.oxygen} />
            <Select label="Oksigen Skrg" required value={formData.oksigenSaatIni} onChange={v => setFormData({...formData, oksigenSaatIni: v})} options={masters.oxygen} />
            <SearchableMultiSelect label="Dokter Raber" selected={formData.raber} onChange={v => setFormData({...formData, raber: v})} options={masters.raber} />
          </div>

          <div className="grid grid-cols-1 gap-5 text-left">
            <div className="space-y-1 text-left">
              <label className="text-[9px] font-black text-slate-500 uppercase ml-1 tracking-widest">Diagnosa Medis *</label>
              <textarea 
                required
                placeholder="Ketik Diagnosa Medis Pasien..." 
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-xs h-32 uppercase outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-sm"
                value={formData.diagnosaMedis} 
                onChange={e => setFormData({...formData, diagnosaMedis: e.target.value})} 
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-center">
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3 text-center">
              <Select label="TRAKHEOSTOMI" value={formData.trakheostomi} onChange={v => setFormData({...formData, trakheostomi: v})} options={['Tidak', 'Iya']} />
              {formData.trakheostomi === 'Iya' && (
                 <div className="space-y-2 mt-3 pt-3 border-t border-slate-200 text-left">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Tgl & Jam Trakheo</span>
                    <button type="button" onClick={() => setTrachDates([...trachDates, ''])} className="p-1 bg-indigo-100 text-indigo-600 rounded-lg"><Plus size={14}/></button>
                  </div>
                  {trachDates.map((date, idx) => (
                    <div key={idx} className="flex gap-2 text-center">
                      <input type="datetime-local" className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-[10px] font-bold uppercase text-center" value={date} onChange={e => handleArrayUpdate(setTrachDates, idx, e.target.value)} />
                      {trachDates.length > 1 && <button type="button" onClick={() => setTrachDates(trachDates.filter((_, i) => i !== idx))} className="text-red-400 p-1 text-center"><Trash2 size={16}/></button>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3 text-center">
              <Select label="INTUBASI" value={formData.intubasi} onChange={v => setFormData({...formData, intubasi: v})} options={['Tidak', 'Iya']} />
              {formData.intubasi === 'Iya' && (
                <div className="space-y-2 mt-3 pt-3 border-t border-slate-200 text-left">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Tgl & Jam Intubasi</span>
                    <button type="button" onClick={() => setIntubDates([...intubDates, ''])} className="p-1 bg-blue-100 text-blue-600 rounded-lg"><Plus size={14}/></button>
                  </div>
                  {intubDates.map((date, idx) => (
                    <div key={idx} className="flex gap-2 text-center">
                      <input type="datetime-local" className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-[10px] font-bold uppercase text-center" value={date} onChange={e => handleArrayUpdate(setIntubDates, idx, e.target.value)} />
                      {intubDates.length > 1 && <button type="button" onClick={() => setIntubDates(intubDates.filter((_, i) => i !== idx))} className="text-red-400 p-1 text-center"><Trash2 size={16}/></button>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3 text-center">
              <Select label="EKSTUBASI" value={formData.ekstubasi} onChange={v => setFormData({...formData, ekstubasi: v})} options={['Tidak', 'Iya']} />
              {formData.ekstubasi === 'Iya' && (
                <div className="space-y-2 mt-3 pt-3 border-t border-slate-200 text-left">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Tgl & Jam Ekstubasi</span>
                    <button type="button" onClick={() => setEkstubDates([...ekstubDates, ''])} className="p-1 bg-emerald-100 text-emerald-600 rounded-lg"><Plus size={14}/></button>
                  </div>
                  {ekstubDates.map((date, idx) => (
                    <div key={idx} className="flex gap-2 text-center">
                      <input type="datetime-local" className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-[10px] font-bold uppercase text-center" value={date} onChange={e => handleArrayUpdate(setEkstubDates, idx, e.target.value)} />
                      {ekstubDates.length > 1 && <button type="button" onClick={() => setEkstubDates(ekstubDates.filter((_, i) => i !== idx))} className="text-red-400 p-1 text-center"><Trash2 size={16}/></button>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 text-left">
            <div className="space-y-1 text-left">
              <label className="text-[9px] font-black text-slate-500 uppercase ml-1 tracking-widest">Alamat Lengkap</label>
              <textarea 
                placeholder="Ketik Alamat Lengkap Pasien..." 
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-xs h-24 uppercase outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-sm"
                value={formData.alamat} 
                onChange={e => setFormData({...formData, alamat: e.target.value})} 
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-center">
              <Field label="No. Telpon" value={formData.noTelp} onChange={v => setFormData({...formData, noTelp: v})} />
              <Field label="Riwayat Vaksin" value={formData.riwayatVaksin} onChange={v => setFormData({...formData, riwayatVaksin: v})} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-center">
            <Field label="Tgl Keluar ICU" type="datetime-local" value={formData.tglJamKeluar} onChange={v => setFormData({...formData, tglJamKeluar: v})} />
            <Select label="Status Keluar" value={formData.statusKeluar} onChange={v => setFormData({...formData, statusKeluar: v})} options={masters.exitStatus} />
          </div>

          <div className="grid grid-cols-1 gap-5 text-left">
            <div className="space-y-1 text-left">
              <label className="text-[9px] font-black text-slate-500 uppercase ml-1 tracking-widest">Keterangan / Catatan Tambahan</label>
              <textarea 
                placeholder="Tambahkan Keterangan Jika Ada..." 
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-xs h-24 uppercase outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-sm"
                value={formData.keterangan} 
                onChange={e => setFormData({...formData, keterangan: e.target.value})} 
              />
            </div>
          </div>

          <div className="flex gap-4 pt-4 text-center"><button type="button" onClick={() => { setFormData({...INITIAL}); setIntubDates(['']); setEkstubDates(['']); setTrachDates(['']); setTempBaseBed(''); setTempUnitMain(''); setTempSubUnit(''); }} className="flex-1 py-4 bg-red-50 text-red-600 font-black rounded-2xl uppercase text-[10px] text-center">Reset Form</button><button type="submit" disabled={loading} className="flex-[2] py-4 bg-blue-600 text-white font-black rounded-2xl shadow-lg uppercase text-xs text-center">Simpan Data Pasien</button></div>
        </form>
      </div>
      <AnimatePresence>{duplicateModal && (<SmartDuplicateModal patient={duplicateModal} onConfirm={useDuplicateData} onCancel={() => setDuplicateModal(null)} />)}</AnimatePresence>
    </div>
  );
}

function PatientListUI({ patients, masters, showNotify, onEdit }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('Semua Status');
  const [filterAgama, setFilterAgama] = useState('Semua Agama');
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewPatient, setViewPatient] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [isLocked, setIsLocked] = useState(true);
  const [activeRowId, setActiveRowId] = useState(null);
  const [sortKey, setSortKey] = useState('tglJamMasuk'); 
  const [sortOrder, setSortOrder] = useState('asc'); 
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 100;

  const sortedPatients = useMemo(() => {
    let list = patients.filter(p => {
      const searchMatch = (p.nama || '').toLowerCase().includes(searchTerm.toLowerCase()) || (p.mr || '').includes(searchTerm);
      
      let statusMatch = true;
      if (filterStatus === 'Dirawat') statusMatch = !p.statusKeluar;
      else if (filterStatus === 'Pindah (ICU/Ranap)') statusMatch = p.statusKeluar && (p.statusKeluar.includes('Ranap') || p.statusKeluar === 'ICU1' || p.statusKeluar === 'HCU' || p.statusKeluar === 'OK');
      else if (filterStatus === 'Meninggal') statusMatch = p.statusKeluar === 'Meninggal';
      else if (filterStatus === 'Pulpak') statusMatch = p.statusKeluar === 'Pulpak';
      else if (filterStatus === 'Rujuk') statusMatch = p.statusKeluar === 'Rujuk';

      const agamaMatch = filterAgama === 'Semua Agama' || p.agama === filterAgama;

      return searchMatch && statusMatch && agamaMatch;
    });
    list.sort((a, b) => {
      let valA, valB;
      if (sortKey === 'tglJamMasuk') { valA = a.tglJamMasuk || ''; valB = b.tglJamMasuk || ''; } 
      else if (sortKey === 'statusKeluar') { valA = a.statusKeluar || 'DIRAWAT'; valB = b.statusKeluar || 'DIRAWAT'; } 
      else { valA = a.bed; valB = b.bed; }
      return sortOrder === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
    });
    return list;
  }, [patients, searchTerm, filterStatus, filterAgama, sortKey, sortOrder]);

  const monthsData = useMemo(() => {
    const groups = {}; sortedPatients.forEach(p => { const key = p.tglJamMasuk?.slice(0, 7) || 'Unknown'; if (!groups[key]) groups[key] = []; groups[key].push(p); });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0])); 
  }, [sortedPatients]);

  const defaultMonths = useMemo(() => monthsData.slice(0, 3), [monthsData]);
  const [activeMonthIdx, setActiveMonthIdx] = useState(0);
  const currentRows = monthsData[activeMonthIdx]?.[1] || [];
  const pagedRows = useMemo(() => currentRows.slice((currentPage - 1) * perPage, currentPage * perPage), [currentRows, currentPage]);
  const toggleSort = (key) => { if (sortKey === key) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortOrder('asc'); } setCurrentPage(1); };
  
  const handleConfirmDelete = async () => {
    try { 
      if(deleteModal.type==='single') await deleteDoc(doc(db,'artifacts',appId,'public', 'data','patients',deleteModal.id)); 
      else { const batch=writeBatch(db); selectedIds.forEach(id=>batch.delete(doc(db,'artifacts',appId,'public', 'data','patients',id))); await batch.commit(); setSelectedIds([]); }
      showNotify("Berhasil Hapus"); 
    } catch(e){ showNotify("Gagal Hapus","error"); } finally { setDeleteModal(null); }
  };

  const handleExportExcel = async (fileName, range) => {
    if (!window.XLSX) { showNotify("Library Excel Belum Siap", "error"); return; }
    try {
      const wb = window.XLSX.utils.book_new(); 
      const startMonth = new Date(range.start + "-01"); 
      const endMonth = new Date(range.end + "-01");
      const filteredMonths = monthsData.filter(([mKey]) => { const d = new Date(mKey + "-01"); return d >= startMonth && d <= endMonth; });
      if (filteredMonths.length === 0) { showNotify("Tidak ada data registrasi pada periode ini", "error"); return; }
      const monthNames = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
      filteredMonths.forEach(([mKey, monthPatients]) => {
        const dateObj = new Date(mKey + "-01"); const sheetName = `${monthNames[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
        const sheetData = monthPatients.map((p, idx) => ({ 
          'NO': idx + 1, 'NAMA PASIEN': p.nama?.toUpperCase(), 'NOMOR MR': p.mr, 'AGAMA': p.agama || '-', 'TGL LAHIR': p.tglLahir || '-', 'USIA / JK': `${p.usia || '-'} / ${p.jenisKelamin || '-'}`, 'BED': p.bed, 'TGL MASUK ICU': p.tglJamMasuk?.replace('T', ' '), 'ASAL PASIEN': p.asalPasien?.toUpperCase(), 'JAMINAN': p.jaminan?.toUpperCase(), 'DIAGNOSA MEDIS': p.diagnosaMedis?.toUpperCase(), 'DPJP': p.dpjp?.toUpperCase(), 'DOKTER RABER': Array.isArray(p.raber) ? p.raber.join(', ').toUpperCase() : (p.raber || '-').toUpperCase(), 'OKSIGEN AWAL': p.oksigenAwal?.toUpperCase() || '-', 'OKSIGEN SKRG': p.oksigenSaatIni?.toUpperCase() || '-', 'TRAKHEOSTOMI': (Array.isArray(p.tglTrakheostomi) ? p.tglTrakheostomi.join(', ') : (p.tglTrakheostomi || 'Tidak')), 'TGL TRAKHEO': (Array.isArray(p.tglTrakheostomi) ? p.tglTrakheostomi.join(', ') : '-'), 'INTUBASI': (p.tglIntubasi || []).join(', ') || '-', 'EKSTUBASI': (p.tglEkstubasi || []).join(', ') || '-', 'ALAMAT': p.alamat?.toUpperCase(), 'NO TELPON': p.noTelp || '-', 'RIWAYAT VAKSIN': p.riwayatVaksin?.toUpperCase(), 'TGL KELUAR ICU': p.tglJamKeluar?.replace('T', ' ') || '-', 'STATUS KELUAR': p.statusKeluar?.toUpperCase() || 'DIRAWAT', 'PERAWAT PENERIMA': p.perawatPenerima?.toUpperCase() || '-', 'KETERANGAN': p.keterangan?.toUpperCase() || '-'
        }));
        const ws = window.XLSX.utils.aoa_to_sheet([[fileName.toUpperCase()]]);
        window.XLSX.utils.sheet_add_json(ws, sheetData, { origin: 'A3' });
        applySmartExcelFormatting(ws, sheetData);
        window.XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
      });
      window.XLSX.writeFile(wb, `${fileName}.xlsx`); showNotify("Register Pasien Berhasil Diunduh"); setShowDownloadModal(false);
    } catch (e) { showNotify("Gagal Eksport Register", "error"); }
  };

  const handleExportPdf = async (fileName, range) => {
    if (!window.jspdf || !window.jspdf.jsPDF) {
      showNotify("Library PDF belum dimuat sepenuhnya, coba sebentar lagi.", "error");
      return;
    }
    try {
      const { jsPDF } = window.jspdf;
      const startMonth = new Date(`${range.start}-01T00:00:00`);
      const endMonthDate = new Date(`${range.end}-01T00:00:00`);
      endMonthDate.setMonth(endMonthDate.getMonth() + 1);
      const endDateTime = endMonthDate.getTime() - 1;

      const filtered = sortedPatients.filter(p => {
        if (!p.tglJamMasuk) return false;
        const ptTime = new Date(p.tglJamMasuk).getTime();
        return ptTime >= startMonth.getTime() && ptTime <= endDateTime;
      });

      if (filtered.length === 0) {
        showNotify("Tidak ada data registrasi pada rentang bulan ini.", "error");
        return;
      }

      // Group by month
      const groupedByMonth = {};
      filtered.forEach(p => {
        const dateObj = new Date(p.tglJamMasuk);
        const monthKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
        if (!groupedByMonth[monthKey]) groupedByMonth[monthKey] = [];
        groupedByMonth[monthKey].push(p);
      });

      const sortedMonths = Object.keys(groupedByMonth).sort();
      const monthNames = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

      // Initialize jsPDF: Landscape, 'pt' units, Legal size
      const doc = new jsPDF('landscape', 'pt', 'legal');
      
      sortedMonths.forEach((mKey, monthIndex) => {
        if (monthIndex > 0) {
          doc.addPage('legal', 'landscape');
        }

        const dateObj = new Date(mKey + "-01");
        const titleMonth = `${monthNames[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
        const monthPatients = groupedByMonth[mKey];

        // Title
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 58, 138); // blue-900
        doc.text(`LAPORAN REGISTRASI PASIEN ICU - RSUD PASAR MINGGU`, 40, 40);
        
        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 116, 139); // slate-500
        doc.text(`PERIODE: ${titleMonth.toUpperCase()}`, 40, 60);
        
        doc.setFontSize(9);
        doc.text(`Tanggal Cetak: ${new Date().toLocaleString('id-ID')}`, doc.internal.pageSize.getWidth() - 40, 40, { align: 'right' });

        // Prepare Table Data
        const tableBody = monthPatients.map((p, idx) => [
          idx + 1,
          `${p.nama?.toUpperCase() || '-'}\nMR: ${p.mr || '-'}`,
          `${p.tglLahir || '-'}\n${p.usia || '-'} / ${p.jenisKelamin?.charAt(0) || '-'}`,
          p.agama?.toUpperCase() || '-',
          p.bed || '-',
          p.tglJamMasuk?.replace('T', '\n') || '-',
          `${p.asalPasien?.toUpperCase() || '-'}\n${p.jaminan?.toUpperCase() || '-'}`,
          p.diagnosaMedis?.toUpperCase() || '-',
          `${p.dpjp?.toUpperCase() || '-'}\nR: ${Array.isArray(p.raber) ? p.raber.join(', ').toUpperCase() : (p.raber?.toUpperCase() || '-')}`,
          `AW: ${p.oksigenAwal?.toUpperCase() || '-'}\nSK: ${p.oksigenSaatIni?.toUpperCase() || '-'}`,
          p.trakheostomi === 'Iya' ? (Array.isArray(p.tglTrakheostomi) ? p.tglTrakheostomi.join('\n').replace(/T/g, ' ') : (p.tglTrakheostomi || 'IYA')) : '-',
          `IN: ${(p.tglIntubasi || []).join('\n').replace(/T/g, ' ') || '-'}\nEK: ${(p.tglEkstubasi || []).join('\n').replace(/T/g, ' ') || '-'}`,
          p.tglJamKeluar?.replace('T', '\n') || '-',
          p.statusKeluar?.toUpperCase() || 'DIRAWAT',
          p.keterangan?.toUpperCase() || '-'
        ]);

        const tableHeaders = [
          ['NO', 'NAMA & MR', 'IDENTITAS', 'AGAMA', 'BED', 'TGL MASUK', 'ASAL & JMN', 'DIAGNOSA MEDIS', 'DPJP & RABER', 'OKSIGEN', 'TRAKHEO', 'INTUBASI & EKSTUBASI', 'TGL KELUAR', 'STATUS', 'KETERANGAN']
        ];

        // Autotable configuration for professional look
        doc.autoTable({
          startY: 80,
          head: tableHeaders,
          body: tableBody,
          theme: 'grid',
          styles: {
            font: 'helvetica',
            fontSize: 7,
            cellPadding: 4,
            lineColor: [226, 232, 240], // slate-200
            lineWidth: 0.5,
            valign: 'middle',
          },
          headStyles: {
            fillColor: [248, 250, 252], // slate-50
            textColor: [71, 85, 105], // slate-600
            fontStyle: 'bold',
            halign: 'center',
            minCellHeight: 25,
          },
          columnStyles: {
            0: { halign: 'center', cellWidth: 20 }, // NO
            1: { cellWidth: 80 }, // NAMA & MR
            2: { cellWidth: 50 },  // IDENTITAS
            3: { halign: 'center', cellWidth: 35 },  // AGAMA
            4: { halign: 'center', cellWidth: 40 },  // BED
            5: { halign: 'center', cellWidth: 50 },  // MASUK
            6: { halign: 'center', cellWidth: 50 },  // ASAL JMN
            7: { cellWidth: 120 }, // DIAGNOSA
            8: { cellWidth: 80 }, // DOKTER
            9: { halign: 'center', cellWidth: 45 },  // O2
            10: { halign: 'center', cellWidth: 45 }, // TRACH
            11: { halign: 'center', cellWidth: 60 }, // IN/EK
            12: { halign: 'center', cellWidth: 50 }, // KELUAR
            13: { halign: 'center', cellWidth: 45 }, // STATUS
            14: { cellWidth: 100 }, // KETERANGAN
          },
          alternateRowStyles: {
            fillColor: [251, 252, 253] // Very light blue-ish grey
          },
          margin: { top: 40, left: 30, right: 30, bottom: 40 },
          didParseCell: function (data) {
            // Highlight specific statuses
            if (data.section === 'body' && data.column.index === 13) {
              const val = data.cell.raw;
              if (val === 'DIRAWAT') {
                data.cell.styles.textColor = [16, 185, 129]; // emerald-500
                data.cell.styles.fontStyle = 'bold';
              } else if (val === 'MENINGGAL') {
                data.cell.styles.textColor = [239, 68, 68]; // red-500
                data.cell.styles.fontStyle = 'bold';
              }
            }
          }
        });
      });

      doc.save(`${fileName}.pdf`);
      showNotify("PDF Register Pasien Berhasil Diunduh");
      setShowPdfModal(false);
    } catch (e) {
      console.error(e);
      showNotify("Gagal Eksport PDF", "error");
    }
  };

  return (
    <div className="space-y-4 text-center">
      <div className="flex flex-col md:flex-row gap-3 items-center justify-between text-left">
        <div className="flex flex-wrap items-center gap-3 w-full md:max-w-7xl text-left">
          <div className="relative flex-1 min-w-[200px] group text-left"><Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><input type="text" placeholder="Cari Nama / MR..." className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-2xl outline-none font-bold text-xs shadow-sm" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} /></div>
          <div className="flex bg-white p-1 rounded-xl border border-slate-100 shadow-sm items-center text-left">
            <Filter size={14} className="text-indigo-600 ml-2" />
            <select className="bg-transparent font-black text-[9px] uppercase outline-none text-slate-700 px-2 cursor-pointer h-8" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setCurrentPage(1); }}>
              <option value="Semua Status">Semua Status</option>
              <option value="Dirawat">Dirawat</option>
              <option value="Pindah (ICU/Ranap)">Pindah (ICU/Ranap)</option>
              <option value="Meninggal">Meninggal</option>
              <option value="Pulpak">Pulpak</option>
              <option value="Rujuk">Rujuk</option>
            </select>
          </div>
          <div className="flex bg-white p-1 rounded-xl border border-slate-100 shadow-sm items-center text-left">
            <span className="text-[9px] font-black text-slate-400 ml-2 uppercase">Agama</span>
            <select className="bg-transparent font-black text-[9px] uppercase outline-none text-slate-700 px-2 cursor-pointer h-8" value={filterAgama} onChange={e => { setFilterAgama(e.target.value); setCurrentPage(1); }}>
              <option value="Semua Agama">Semua Agama</option>
              <option value="Islam">Islam</option>
              <option value="Kristen">Kristen</option>
              <option value="Budha">Budha</option>
              <option value="Hindu">Hindu</option>
            </select>
          </div>
          <div className="flex bg-white p-1 rounded-xl border border-slate-100 shadow-sm text-center">
            <button onClick={() => toggleSort('tglJamMasuk')} className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all flex items-center gap-2 text-center ${sortKey === 'tglJamMasuk' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-50'}`}>
              <CalendarSearch size={12}/> Tgl Masuk {sortKey === 'tglJamMasuk' && (sortOrder === 'asc' ? <SortAsc size={10}/> : <SortDesc size={10}/>)}
            </button>
            <button onClick={() => toggleSort('statusKeluar')} className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all flex items-center gap-2 text-center ${sortKey === 'statusKeluar' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-50'}`}>
              <Activity size={12}/> Status Keluar {sortKey === 'statusKeluar' && (sortOrder === 'asc' ? <SortAsc size={10}/> : <SortDesc size={10}/>)}
            </button>
          </div>
          <button onClick={() => setShowDownloadModal(true)} className="p-3 bg-emerald-600 text-white rounded-2xl shadow-lg hover:bg-emerald-700 transition-all flex items-center gap-2 text-center"><FileSpreadsheet size={18} /><span className="text-[10px] font-black uppercase hidden sm:block">Excel</span></button>
          <button onClick={() => setShowPdfModal(true)} className="p-3 bg-red-600 text-white rounded-2xl shadow-lg hover:bg-red-700 transition-all flex items-center gap-2 text-center"><FileText size={18} /><span className="text-[10px] font-black uppercase hidden sm:block">PDF</span></button>
          <button onClick={() => setIsLocked(!isLocked)} className={`p-3 rounded-2xl transition-all shadow-sm flex items-center gap-2 text-center ${isLocked ? 'bg-blue-600 text-white' : 'bg-white text-slate-400 border border-slate-100'}`}>{isLocked ? <Lock size={18} /> : <Unlock size={18} />}<span className="text-[10px] font-black uppercase">{isLocked ? 'Locked' : 'Unlocked'}</span></button>
        </div>
        {selectedIds.length > 0 && (<button onClick={() => setDeleteModal({ type: 'bulk' })} className="px-4 py-3 bg-red-50 text-red-600 font-black rounded-xl flex items-center gap-1.5 text-[10px] uppercase border border-red-100 shadow-sm text-center"><Trash2 size={14} /> HAPUS ({selectedIds.length})</button>)}
      </div>
      <div className="flex gap-1 overflow-x-auto pb-1 no-scrollbar text-center">{defaultMonths.map(([m, data], idx) => (<button key={m} onClick={() => { setActiveMonthIdx(idx); setCurrentPage(1); }} className={`px-4 py-2 rounded-xl font-black text-[9px] border shrink-0 uppercase tracking-tighter transition-all ${activeMonthIdx === idx ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-slate-400 hover:bg-slate-50'} text-center`}>{m} <span className="ml-1 opacity-60">({data.length})</span></button>))}</div>
      <div className="bg-white rounded-[1.5rem] shadow-xl border border-slate-100 overflow-hidden overflow-x-auto custom-scrollbar relative text-center">
        <table className="w-full border-collapse table-auto min-w-[1500px] text-center">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10 text-[8px] font-black text-slate-400 uppercase text-center">
            <tr>{REGISTER_COL_DEFS.map((col) => (<th key={col.id} style={{ width: col.width }} className={`p-1.5 border-r border-slate-100 ${col.id === 'action' && isLocked ? 'sticky right-0 bg-slate-50 z-20 border-l border-slate-200 shadow-[-5px_0_10px_rgba(0,0,0,0.05)]' : ''}`}>{col.id === 'no' && <input type="checkbox" onChange={e => setSelectedIds(e.target.checked ? currentRows.map(p => p.id) : [])} checked={selectedIds.length > 0 && selectedIds.length === currentRows.length} />}{col.label}</th>))}</tr>
          </thead>
          <tbody className="divide-y divide-slate-50 text-[9px] text-center">
            {pagedRows.map((p, i) => (
              <tr key={p.id} onClick={() => setActiveRowId(p.id)} className={`transition-all duration-200 h-14 group cursor-pointer ${activeRowId === p.id ? 'bg-blue-200/90 outline outline-2 outline-blue-500 shadow-md relative z-10 font-semibold' : 'hover:bg-blue-50/40'}`}>
                <td className={`p-1 border-r border-slate-100 transition-all duration-200 ${activeRowId === p.id ? 'border-l-[6px] border-l-blue-600' : 'border-l-[6px] border-l-transparent group-hover:border-l-blue-300'}`}><input type="checkbox" className="cursor-pointer" checked={selectedIds.includes(p.id)} onChange={() => setSelectedIds(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])} /> {(currentPage-1)*perPage + i + 1}</td>
                <td className="p-1 border-r border-slate-100 font-black uppercase text-left pl-3">{p.nama}<br/><span className="text-blue-600">MR: {p.mr}</span></td>
                <td className="p-1 border-r border-slate-100 font-black">{p.usia || '-'} / {p.jenisKelamin?.charAt(0)}</td>
                <td className="p-1 border-r border-slate-100 font-bold uppercase">{p.agama || '-'}</td>
                <td className="p-1 border-r border-slate-100 font-black">{p.bed}</td><td className="p-1 border-r border-slate-100 font-bold whitespace-pre-wrap">{p.tglJamMasuk?.replace('T', '\n')}</td>
                <td className="p-1 border-r border-slate-100 font-black text-emerald-600 uppercase">{p.jaminan}</td><td className="p-1 border-r border-slate-100 uppercase px-1 h-14 line-clamp-2">{p.diagnosaMedis}</td>
                <td className="p-1 border-r border-slate-100 font-black">{p.dpjp}</td>
                <td className="p-1 border-r border-slate-100">
                  <div className="flex flex-col gap-0.5 items-center justify-center text-center">
                    <span className="px-1 py-0.5 bg-blue-50 text-blue-600 rounded text-[7px] font-black border border-blue-100 uppercase text-center">{p.oksigenAwal || '-'}</span>
                    <div className="h-2 w-px bg-slate-200 mx-auto"></div>
                    <span className="px-1 py-0.5 bg-indigo-50 text-indigo-600 rounded text-[7px] font-black border border-indigo-100 uppercase text-center">{p.oksigenSaatIni || '-'}</span>
                  </div>
                </td>
                <td className="p-1 border-r border-slate-100 font-bold text-[7px] text-center">
                  {Array.isArray(p.tglTrakheostomi) ? p.tglTrakheostomi.join(', ').replace(/T/g, ' ') : (p.tglTrakheostomi || '-')}
                </td>
                <td className="p-1 border-r border-slate-100 font-bold text-center">
                  <div className="flex flex-col gap-1 text-[7px] max-h-24 overflow-y-auto no-scrollbar text-center">
                    <div className="text-blue-600 font-black text-center">IN: {(p.tglIntubasi || []).join(', ').replace(/T/g, ' ') || '-'}</div>
                    <div className="text-emerald-600 font-black text-center">EK: {(p.tglEkstubasi || []).join(', ').replace(/T/g, ' ') || '-'}</div>
                  </div>
                </td>
                <td className="p-1 border-r border-slate-100 font-black whitespace-pre-wrap">{p.tglJamKeluar?.replace('T', '\n') || '-'}</td><td className="p-1 border-r border-slate-100 text-center"><span className={`px-1.5 py-0.5 rounded font-black uppercase text-[7px] border ${p.statusKeluar ? 'bg-red-50 text-red-500 border-red-100' : 'bg-emerald-50 text-emerald-500 border-emerald-400'}`}>{p.statusKeluar || 'DIRAWAT'}</span></td>
                <td className={`p-1 text-center bg-white group-hover:bg-slate-50 transition-colors ${isLocked ? 'sticky right-0 z-10 border-l border-slate-100 shadow-[-5px_0_10_rgba(0,0,0,0.03)]' : ''}`}><div className="flex gap-0.5 justify-center text-center"><button onClick={() => setViewPatient(p)} className="p-1 text-blue-600 text-center"><Eye size={12} /></button><button onClick={() => onEdit(p)} className="p-1 text-emerald-600 text-center"><Edit3 size={12} /></button><button onClick={() => setDeleteModal({ type: 'single', id: p.id })} className="p-1 text-red-600 text-center"><Trash2 size={12} /></button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination total={sortedPatients.length} perPage={perPage} current={currentPage} onChange={setCurrentPage} />
      <AnimatePresence>{deleteModal && (<SmartDeleteModal onConfirm={handleConfirmDelete} onCancel={() => setDeleteModal(null)} />)}</AnimatePresence>
      <AnimatePresence>{viewPatient && (<SmartViewModal p={viewPatient} onClose={() => setViewPatient(null)} />)}</AnimatePresence>
      <AnimatePresence>{showDownloadModal && (<SmartRegDownloadModal onExport={handleExportExcel} onClose={() => setShowDownloadModal(false)} />)}</AnimatePresence>
      <AnimatePresence>{showPdfModal && (<SmartRegPdfDownloadModal onExport={handleExportPdf} onClose={() => setShowPdfModal(false)} />)}</AnimatePresence>
    </div>
  );
}

function LaporanPasienSection({ user, patients, masters, showNotify }) {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [pjData, setPjData] = useState({ pagi: '', siang: '', malam: '' });
  const [nextPjData, setNextPjData] = useState({ pagi: '', siang: '', malam: '' });
  const [conditions, setConditions] = useState({});
  const [editModal, setEditModal] = useState(null); 
  const [viewModal, setViewModal] = useState(null);
  const [downloadModal, setDownloadModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [activeRowId, setActiveRowId] = useState(null);
  const [isLocked, setIsLocked] = useState(true);
  const [sortKey, setSortKey] = useState('bed'); 
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 50;
  const [tempOxygen, setTempOxygen] = useState('');

  useEffect(() => {
    if (!user) return;
    const pjRef = doc(db, 'artifacts', appId, 'public', 'data', 'shift_pj', date);
    const unsubPj = onSnapshot(pjRef, (d) => d.exists() ? setPjData(d.data()) : setPjData({ pagi: '', siang: '', malam: '' }), (err) => {
      if (err.code !== 'permission-denied') console.error("Firestore Shift PJ Error:", err);
    });

    const nextDateObj = new Date(date);
    nextDateObj.setDate(nextDateObj.getDate() + 1);
    const nextDateStr = nextDateObj.toISOString().split('T')[0];
    const nextPjRef = doc(db, 'artifacts', appId, 'public', 'data', 'shift_pj', nextDateStr);
    const unsubNextPj = onSnapshot(nextPjRef, (d) => d.exists() ? setNextPjData(d.data()) : setNextPjData({ pagi: '', siang: '', malam: '' }), (err) => {
      if (err.code !== 'permission-denied') console.error("Firestore Next Shift PJ Error:", err);
    });

    const condRef = query(collection(db, 'artifacts', appId, 'public', 'data', 'conds_' + date));
    const unsubCond = onSnapshot(condRef, (s) => { const c = {}; s.docs.forEach(d => { c[d.id] = d.data(); }); setConditions(c); }, (err) => {
      if (err.code !== 'permission-denied') console.error("Firestore Conditions Error:", err);
    });
    return () => { unsubPj(); unsubNextPj(); unsubCond(); };
  }, [date, user]);

  useEffect(() => {
    if (editModal && editModal.p) {
      setTempOxygen(editModal.p.oksigenSaatIni || '');
    }
  }, [editModal]);

  const activePatientsProcessed = useMemo(() => {
    let list = patients.filter(p => { const entry = p.tglJamMasuk?.split('T')[0]; const exit = p.tglJamKeluar?.split('T')[0]; return entry <= date && (!p.statusKeluar || exit >= date); });
    list.sort((a, b) => {
      let valA, valB;
      if (sortKey === 'bed') { 
        valA = a.bed; valB = b.bed; 
        return sortOrder === 'asc' ? naturalSortBeds(valA, valB) : naturalSortBeds(valB, valA);
      } 
      else if (sortKey === 'status') { valA = a.statusKeluar || 'DIRAWAT'; valB = b.statusKeluar || 'DIRAWAT'; return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA); }
      return sortOrder === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
    });
    return list;
  }, [patients, date, sortKey, sortOrder]);

  const pagedRows = useMemo(() => activePatientsProcessed.slice((currentPage - 1) * perPage, currentPage * perPage), [activePatientsProcessed, currentPage]);
  
  const handleNextPatient = () => {
    const idx = activePatientsProcessed.findIndex(p => p.id === viewModal.id);
    if (idx < activePatientsProcessed.length - 1) setViewModal(activePatientsProcessed[idx + 1]);
  };
  const handlePrevPatient = () => {
    const idx = activePatientsProcessed.findIndex(p => p.id === viewModal.id);
    if (idx > 0) setViewModal(activePatientsProcessed[idx - 1]);
  };

  const handleSaveAll = async () => {
    if (!editModal) return; const pId = editModal.p.id;
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'conds_' + date, pId), conditions[pId] || {}, { merge: true }); 
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'shift_pj', date), pjData, { merge: true });
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'patients', pId), { oksigenSaatIni: tempOxygen });
      showNotify("Data Berhasil Disimpan Permanen"); setEditModal(null); 
    } catch(e) { showNotify("Gagal Menyimpan Data Cloud", "error"); }
  };

  const handleExportExcel = async (fileName, range) => {
    if (!window.XLSX) { showNotify("Library Excel Belum Siap", "error"); return; }
    try {
      const wb = window.XLSX.utils.book_new();
      const dates = []; let curr = new Date(range.start); const end = new Date(range.end);
      while (curr <= end) { dates.push(new Date(curr).toISOString().split('T')[0]); curr.setDate(curr.getDate() + 1); }
      for (const dStr of dates) {
        const pjSnap = await getDoc(doc(db, 'artifacts', appId, 'public', 'data', 'shift_pj', dStr));
        const pjs = pjSnap.exists() ? pjSnap.data() : { pagi: '-', siang: '-', malam: '-' };

        const nextDateObj = new Date(dStr);
        nextDateObj.setDate(nextDateObj.getDate() + 1);
        const nextDateStr = nextDateObj.toISOString().split('T')[0];
        const nextPjSnap = await getDoc(doc(db, 'artifacts', appId, 'public', 'data', 'shift_pj', nextDateStr));
        const pjsNext = nextPjSnap.exists() ? nextPjSnap.data() : { pagi: '-', siang: '-', malam: '-' };

        const condSnap = await getDocs(collection(db, 'artifacts', appId, 'public', 'data', 'conds_' + dStr));
        const dayConds = {}; condSnap.docs.forEach(docItem => { dayConds[docItem.id] = docItem.data(); });
        const dailyPatients = patients.filter(p => { const entry = p.tglJamMasuk?.split('T')[0]; const exit = p.tglJamKeluar?.split('T')[0]; return entry <= dStr && (!p.statusKeluar || exit >= dStr); });
        dailyPatients.sort((a,b) => naturalSortBeds(a.bed, b.bed));
        const sheetData = dailyPatients.map((p, idx) => { 
          const c = dayConds[p.id] || {}; 
          return { 
            'NO': idx + 1, 
            'PASIEN, MR, BED & STATUS': `${(p.nama || '-').toUpperCase()}\nMR: ${p.mr || '-'}\nBED: ${p.bed || '-'}\nSTATUS: ${(p.statusKeluar || 'DIRAWAT').toUpperCase()}`, 
            'DIAGNOSA': (p.diagnosaMedis || '-').toUpperCase(), 
            'DPJP & RABER': `${(p.dpjp || '-').toUpperCase()}\n${Array.isArray(p.raber) ? p.raber.join(', ').toUpperCase() : (p.raber || '-').toUpperCase()}`, 
            'SHIFT PAGI': `PJ: ${(pjs.pagi || '-').toUpperCase()}\nPP: ${(c.pagi?.nrs || '-').toUpperCase()}\nKONDISI: ${c.pagi?.txt || '-'}`, 
            'SHIFT SIANG': `PJ: ${(pjs.siang || '-').toUpperCase()}\nPP: ${(c.siang?.nrs || '-').toUpperCase()}\nKONDISI: ${c.siang?.txt || '-'}`, 
            'SHIFT MALAM': `PJ: ${(pjs.malam || '-').toUpperCase()}\nPP: ${(c.malam?.nrs || '-').toUpperCase()}\nKONDISI: ${c.malam?.txt || '-'}` 
          }; 
        });
        
        if (sheetData.length > 0) { 
          sheetData.push({});
          sheetData.push({ 'NO': 'SERAH TERIMA LAPORAN SHIFT' });
          sheetData.push({
            'SHIFT PAGI': `Menyerahkan: ${pjs.pagi || '-'}\nMenerima: ${pjs.siang || '-'}`,
            'SHIFT SIANG': `Menyerahkan: ${pjs.siang || '-'}\nMenerima: ${pjs.malam || '-'}`,
            'SHIFT MALAM': `Menyerahkan: ${pjs.malam || '-'}\nMenerima: ${pjsNext.pagi || '.....'}`
          });

          const ws = window.XLSX.utils.json_to_sheet(sheetData, { origin: 'A3' });
          window.XLSX.utils.book_append_sheet(wb, ws, dStr); 
        }
      }
      if (wb.SheetNames.length === 0) { showNotify("Tidak ada data", "error"); return; }
      window.XLSX.writeFile(wb, `${fileName}.xlsx`); showNotify("Excel Berhasil Diunduh"); setDownloadModal(false);
    } catch (e) { showNotify("Gagal Eksport", "error"); }
  };

  const handleExportPdf = async (fileName, range) => {
    if (!window.jspdf || !window.jspdf.jsPDF) { showNotify("Library PDF belum siap", "error"); return; }
    try {
      const { jsPDF } = window.jspdf;
      const pdfDoc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'legal' });
      
      if (typeof pdfDoc.autoTable !== 'function') {
          showNotify("Library AutoTable belum siap, tunggu beberapa detik lalu coba lagi.", "error");
          return;
      }

      const dates = []; let curr = new Date(range.start); const end = new Date(range.end);
      while (curr <= end) { dates.push(new Date(curr).toISOString().split('T')[0]); curr.setDate(curr.getDate() + 1); }
      
      let pagesAdded = 0;
      
      for (const dStr of dates) {
        const pjSnap = await getDoc(doc(db, 'artifacts', appId, 'public', 'data', 'shift_pj', dStr));
        const pjs = pjSnap.exists() ? pjSnap.data() : { pagi: '-', siang: '-', malam: '-' };

        const nextDateObj = new Date(dStr);
        nextDateObj.setDate(nextDateObj.getDate() + 1);
        const nextDateStr = nextDateObj.toISOString().split('T')[0];
        const nextPjSnap = await getDoc(doc(db, 'artifacts', appId, 'public', 'data', 'shift_pj', nextDateStr));
        const pjsNext = nextPjSnap.exists() ? nextPjSnap.data() : { pagi: '-', siang: '-', malam: '-' };

        const condSnap = await getDocs(collection(db, 'artifacts', appId, 'public', 'data', 'conds_' + dStr));
        const dayConds = {}; condSnap.docs.forEach(docItem => { dayConds[docItem.id] = docItem.data(); });

        const dailyPatients = patients.filter(p => { const entry = p.tglJamMasuk?.split('T')[0]; const exit = p.tglJamKeluar?.split('T')[0]; return entry <= dStr && (!p.statusKeluar || exit >= dStr); });
        dailyPatients.sort((a,b) => naturalSortBeds(a.bed, b.bed));
        
        if (dailyPatients.length === 0) continue; 
        
        if (pagesAdded > 0) pdfDoc.addPage('legal', 'landscape');
        pagesAdded++;
        
        pdfDoc.setFontSize(14);
        pdfDoc.setFont("helvetica", "bold");
        pdfDoc.text(`LAPORAN PASIEN ICU - RSUD PASAR MINGGU`, 40, 40);
        pdfDoc.setFontSize(10);
        pdfDoc.setFont("helvetica", "normal");
        const dateObj = new Date(dStr);
        pdfDoc.text(`Tanggal Laporan: ${dateObj.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`, 40, 60);
        
        const tableBody = dailyPatients.map((p, idx) => {
          const c = dayConds[p.id] || {}; 
          return [
            idx + 1,
            `${(p.nama || '-').toUpperCase()}\nMR: ${p.mr || '-'}\nBED: ${p.bed || '-'}\nSTATUS: ${(p.statusKeluar || 'DIRAWAT').toUpperCase()}`,
            (p.diagnosaMedis || '-').toUpperCase(),
            `${(p.dpjp || '-').toUpperCase()}\nRABER: ${Array.isArray(p.raber) ? p.raber.join(', ').toUpperCase() : (p.raber || '-').toUpperCase()}`,
            `PJ: ${(pjs.pagi || '-').toUpperCase()}\nPP: ${(c.pagi?.nrs || '-').toUpperCase()}\nKondisi:\n${c.pagi?.txt || '-'}`,
            `PJ: ${(pjs.siang || '-').toUpperCase()}\nPP: ${(c.siang?.nrs || '-').toUpperCase()}\nKondisi:\n${c.siang?.txt || '-'}`,
            `PJ: ${(pjs.malam || '-').toUpperCase()}\nPP: ${(c.malam?.nrs || '-').toUpperCase()}\nKondisi:\n${c.malam?.txt || '-'}`
          ];
        });

        tableBody.push([
          { content: 'SERAH TERIMA LAPORAN SHIFT', colSpan: 4, styles: { halign: 'right', fillColor: [241, 245, 249], textColor: [71, 85, 105], fontStyle: 'bold' } },
          { content: `Menyerahkan:\n${pjs.pagi || '.....'}\n\nMenerima:\n${pjs.siang || '.....'}`, styles: { fillColor: [248, 250, 252], cellPadding: 4, valign: 'top' } },
          { content: `Menyerahkan:\n${pjs.siang || '.....'}\n\nMenerima:\n${pjs.malam || '.....'}`, styles: { fillColor: [248, 250, 252], cellPadding: 4, valign: 'top' } },
          { content: `Menyerahkan:\n${pjs.malam || '.....'}\n\nMenerima:\n${pjsNext.pagi || '.....'}`, styles: { fillColor: [248, 250, 252], cellPadding: 4, valign: 'top' } }
        ]);
        
        pdfDoc.autoTable({
          startY: 80,
          head: [['NO', 'PASIEN, MR, BED & STATUS', 'DIAGNOSA', 'DPJP & RABER', `SHIFT PAGI (${pjs.pagi || '-'})`, `SHIFT SIANG (${pjs.siang || '-'})`, `SHIFT MALAM (${pjs.malam || '-'})`]],
          body: tableBody,
          theme: 'grid',
          styles: { fontSize: 7, cellPadding: 3, valign: 'middle' },
          headStyles: { fillColor: [71, 85, 105], textColor: 255, halign: 'center' },
          columnStyles: {
            0: { cellWidth: 20, halign: 'center' },
            1: { cellWidth: 130 },
            2: { cellWidth: 100 },
            3: { cellWidth: 100 },
            4: { cellWidth: 190 },
            5: { cellWidth: 190 },
            6: { cellWidth: 190 }
          }
        });
      }
      
      if (pagesAdded === 0) {
        showNotify("Tidak ada data untuk dirender pada rentang tanggal ini", "error");
        return;
      }
      
      pdfDoc.save(`${fileName}.pdf`);
      showNotify("PDF Laporan Berhasil Diunduh");
      setShowPdfModal(false);
    } catch (e) {
      showNotify("Gagal Eksport PDF", "error");
      console.error(e);
    }
  };

  const toggleSort = (key) => { if (sortKey === key) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortOrder('asc'); } setCurrentPage(1); };
  return (
    <div className="space-y-6 text-center">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-left">
        <div className="flex flex-wrap items-center gap-3 text-left">
          <div className="flex items-center gap-3 bg-white p-2 rounded-2xl border border-slate-100 shadow-sm text-center"><History className="text-indigo-600 ml-2" size={18}/><input type="date" className="bg-transparent font-black text-xs text-slate-700 outline-none uppercase text-center" value={date} onChange={e => { setDate(e.target.value); setCurrentPage(1); }} /></div>
          <div className="flex bg-white p-1 rounded-xl border border-slate-100 shadow-sm text-center">
              <button onClick={() => toggleSort('bed')} className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all flex items-center gap-2 text-center ${sortKey === 'bed' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-50'}`}>{sortKey === 'bed' && (sortOrder === 'asc' ? <SortAsc size={10}/> : <SortDesc size={10}/>)} Bed</button>
              <button onClick={() => toggleSort('status')} className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all flex items-center gap-2 text-center ${sortKey === 'status' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-50'}`}>{sortKey === 'status' && (sortOrder === 'asc' ? <SortAsc size={10}/> : <SortDesc size={10}/>)} Status</button>
          </div>
          <button onClick={() => setDownloadModal(true)} className="p-3 bg-emerald-600 text-white rounded-2xl shadow-lg hover:bg-emerald-700 transition-all flex items-center gap-2 text-center"><FileSpreadsheet size={18} /><span className="text-[10px] font-black uppercase hidden sm:block">Excel</span></button>
          <button onClick={() => setShowPdfModal(true)} className="p-3 bg-red-600 text-white rounded-2xl shadow-lg hover:bg-red-700 transition-all flex items-center gap-2 text-center"><FileText size={18} /><span className="text-[10px] font-black uppercase hidden sm:block">PDF</span></button>
        </div>
        <button onClick={() => setIsLocked(!isLocked)} className={`p-3 rounded-2xl transition-all shadow-sm flex items-center gap-2 text-center ${isLocked ? 'bg-blue-600 text-white' : 'bg-white text-slate-400 border border-slate-100'}`}>{isLocked ? <Lock size={18} /> : <Unlock size={18} />}<span className="text-[10px] font-black uppercase">{isLocked ? 'Locked' : 'Unlocked'}</span></button>
      </div>
      <div className="bg-white rounded-[2rem] shadow-xl border border-slate-100 overflow-hidden overflow-x-auto custom-scrollbar relative text-center">
        <table className="w-full border-collapse table-auto min-w-[1400px] text-center">
          <thead className="bg-slate-50 border-b border-slate-200 text-[8px] font-black text-slate-400 uppercase tracking-tighter text-center">
            <tr><th className="p-2 w-[35px] border-r border-slate-100 text-center">NO</th><th className="p-2 w-[220px] border-r border-slate-100">PASIEN, MR, BED & STATUS</th><th className="p-2 w-[150px] border-r border-slate-100">DIAGNOSA</th><th className="p-2 w-[130px] border-r border-slate-100">DPJP & RABER</th><th className="p-2 w-[250px] border-r bg-blue-50/10 text-blue-600">SHIFT PAGI ({pjData.pagi || '-'})</th><th className="p-2 w-[250px] border-r bg-indigo-50/10 text-indigo-600">SHIFT SIANG ({pjData.siang || '-'})</th><th className="p-2 w-[250px] border-r bg-violet-50/10 text-violet-600">SHIFT MALAM ({pjData.malam || '-'})</th><th className={`p-2 w-[100px] bg-slate-50 z-20 ${isLocked ? 'sticky right-0 border-l border-slate-200 shadow-[-5px_0_10_rgba(0,0,0,0.05)]' : ''}`}>AKSI</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-50 text-[9px] text-center">
            {pagedRows.map((p, i) => { 
              const c = conditions[p.id] || {}; 
              return (<tr key={p.id} onClick={() => setActiveRowId(p.id)} className={`transition-all duration-200 group cursor-pointer ${activeRowId === p.id ? 'bg-blue-200/90 outline outline-2 outline-blue-500 shadow-md relative z-10 font-semibold' : 'hover:bg-slate-50/50'}`}><td className={`p-1 border-r border-slate-100 font-black text-slate-300 transition-all duration-200 ${activeRowId === p.id ? 'border-l-[6px] border-l-blue-600' : 'border-l-[6px] border-l-transparent group-hover:border-l-blue-300'}`}>{(currentPage-1)*perPage + i + 1}</td><td className="p-2 border-r border-slate-100 text-left pl-3"><p className="font-black text-slate-800 uppercase text-left">{p.nama}</p><p className="font-bold text-blue-600 text-[8px] text-left">MR: {p.mr} | BED: {p.bed}</p><span className={`mt-1 px-1.5 py-0.5 inline-block rounded font-black uppercase text-[7px] border ${p.statusKeluar ? 'bg-red-50 text-red-500 border-red-100' : 'bg-emerald-50 text-emerald-500 border-emerald-400'}`}>{p.statusKeluar || 'DIRAWAT'}</span></td><td className="p-1 border-r border-slate-100 text-center uppercase line-clamp-3 text-[8px] min-h-[50px]">{p.diagnosaMedis}</td><td className="p-1 border-r border-slate-100 text-center font-bold text-slate-800 uppercase">{p.dpjp}</td>{['pagi','siang','malam'].map(sKey => (<td key={sKey} className="p-1 border-r border-slate-100 cursor-pointer hover:bg-white text-center" onClick={() => setEditModal({ p, shift: sKey })}><p className="font-bold text-slate-700 uppercase leading-tight line-clamp-2 px-1 text-center">{c[sKey]?.txt || '-'}</p><p className="text-[7px] font-black text-indigo-500 uppercase mt-1 text-center">Pwt: {c[sKey]?.nrs || '-'}</p></td>))}<td className={`p-1 bg-white group-hover:bg-slate-50 transition-colors ${isLocked ? 'sticky right-0 z-10 border-l border-slate-100 shadow-[-5px_0_10_rgba(0,0,0,0.03)]' : ''}`}><div className="flex gap-1 justify-center text-center"><button onClick={() => setViewModal(p)} className="p-1.5 text-blue-600 bg-blue-50 rounded-lg border border-blue-100 text-center"><Eye size={13}/></button><button onClick={() => setEditModal({ p, shift: 'all' })} className="p-1.5 text-emerald-600 bg-emerald-50 rounded-lg border border-emerald-100 text-center"><Edit3 size={13}/></button></div></td></tr>);
            })}
          </tbody>
          <tfoot className="bg-slate-50/50 border-t border-slate-200">
            <tr>
              <td colSpan={4} className="p-4 text-[9px] font-black text-slate-400 uppercase tracking-widest border-r border-slate-200 text-left pl-10">SERAH TERIMA LAPORAN SHIFT</td>
              <td className="p-4 border-r border-slate-100 text-left align-top">
                  <div className="bg-white p-2 rounded-lg border border-slate-100 shadow-sm text-center">
                      <p className="text-[8px] font-black text-blue-500 uppercase leading-none mb-1 text-center">Menyerahkan (Pagi):</p>
                      <p className="text-[9px] font-bold text-slate-600 uppercase leading-tight italic mb-3 text-center">{pjData.pagi || '.....'}</p>
                      <p className="text-[8px] font-black text-indigo-500 uppercase leading-none mb-1 text-center">Menerima (Siang):</p>
                      <p className="text-[9px] font-bold text-slate-600 uppercase leading-tight italic text-center">{pjData.siang || '.....'}</p>
                  </div>
              </td>
              <td className="p-4 border-r border-slate-100 text-left align-top">
                  <div className="bg-white p-2 rounded-lg border border-slate-100 shadow-sm text-center">
                      <p className="text-[8px] font-black text-indigo-500 uppercase leading-none mb-1 text-center">Menyerahkan (Siang):</p>
                      <p className="text-[9px] font-bold text-slate-600 uppercase leading-tight italic mb-3 text-center">{pjData.siang || '.....'}</p>
                      <p className="text-[8px] font-black text-violet-500 uppercase leading-none mb-1 text-center">Menerima (Malam):</p>
                      <p className="text-[9px] font-bold text-slate-600 uppercase leading-tight italic text-center">{pjData.malam || '.....'}</p>
                  </div>
              </td>
              <td className="p-4 border-r border-slate-100 text-left align-top">
                  <div className="bg-white p-2 rounded-lg border border-slate-100 shadow-sm text-center">
                      <p className="text-[8px] font-black text-violet-500 uppercase leading-none mb-1 text-center">Menyerahkan (Malam):</p>
                      <p className="text-[9px] font-bold text-slate-600 uppercase leading-tight italic mb-3 text-center">{pjData.malam || '.....'}</p>
                      <p className="text-[8px] font-black text-blue-500 uppercase leading-none mb-1 text-center">Menerima (Pagi):</p>
                      <p className="text-[9px] font-bold text-slate-600 uppercase leading-tight italic text-center">{nextPjData?.pagi || '.....'}</p>
                  </div>
              </td>
              <td className={`p-4 bg-slate-50 z-20 ${isLocked ? 'sticky right-0 border-l border-slate-200 shadow-[-5px_0_10_rgba(0,0,0,0.05)]' : ''}`}></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <Pagination total={activePatientsProcessed.length} perPage={perPage} current={currentPage} onChange={setCurrentPage} />
      <AnimatePresence>{viewModal && (
        <SmartReportViewModal 
          p={viewModal} 
          conds={conditions[viewModal.id] || {}} 
          pj={pjData} date={date} 
          onClose={() => setViewModal(null)} 
          onNext={handleNextPatient}
          onPrev={handlePrevPatient}
          hasNext={activePatientsProcessed.findIndex(p => p.id === viewModal.id) < activePatientsProcessed.length - 1}
          hasPrev={activePatientsProcessed.findIndex(p => p.id === viewModal.id) > 0}
        />
      )}</AnimatePresence>
      <AnimatePresence>{editModal && (<div className="fixed inset-0 z-[500] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm text-center"><motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white w-full max-w-lg rounded-[2.5rem] p-8 shadow-2xl flex flex-col max-h-[90vh] text-center"><div className="flex justify-between items-center mb-6"><div className="text-left leading-none"><h4 className="text-lg font-black text-slate-800 uppercase tracking-tight">Smart Edit Laporan</h4><p className="text-[9px] font-black text-indigo-600 uppercase tracking-widest mt-1">{editModal.p.nama} | {date}</p></div><button onClick={() => setEditModal(null)} className="p-2 hover:bg-slate-100 rounded-xl text-center"><X size={20}/></button></div><div className="space-y-6 flex-1 overflow-y-auto custom-scrollbar pr-1 text-center">
          
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-3xl mb-4 text-center">
            <Select label="Update Oksigen Saat Ini" value={tempOxygen} options={masters.oxygen} onChange={setTempOxygen} />
            <p className="text-[7px] font-bold text-blue-400 mt-2 uppercase tracking-tighter text-center">* Mengupdate data utama pasien secara global</p>
          </div>

          {['pagi','siang','malam'].map(sKey => (<div key={sKey} className="p-4 bg-slate-50 border border-slate-200 rounded-3xl space-y-3 mb-4 text-center"><div className="flex items-center justify-between border-b border-slate-200 pb-2 mb-2"><span className="text-[9px] font-black text-indigo-600 uppercase tracking-widest">Shift {sKey.toUpperCase()}</span></div><SearchableSelect label="PJ Shift" value={pjData[sKey] || ''} onChange={v => setPjData({...pjData, [sKey]: v})} options={masters.nurses} /><div className="space-y-1 text-center"><label className="text-[8px] font-black text-slate-400 block ml-1 text-center">Kondisi</label><textarea className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-xs h-20 uppercase text-center" value={conditions[editModal.p.id]?.[sKey]?.txt || ''} onChange={e => setConditions({...conditions, [editModal.p.id]: {...(conditions[editModal.p.id]||{}), [sKey]: {...(conditions[editModal.p.id]?.[sKey]||{}), txt: e.target.value.toUpperCase()}}})} /></div><SearchableSelect label="Perawat Pelaksana" value={conditions[editModal.p.id]?.[sKey]?.nrs || ''} onChange={v => setConditions({...conditions, [editModal.p.id]: {...(conditions[editModal.p.id]||{}), [sKey]: {...(conditions[editModal.p.id]?.[sKey]||{}), nrs: v}}})} options={masters.nurses} /></div>))}</div><button onClick={handleSaveAll} className="w-full py-4 bg-blue-600 text-white font-black rounded-2xl text-[10px] uppercase shadow-lg mt-6 text-center">Simpan Perubahan Cloud</button></motion.div></div>)}</AnimatePresence>
      <AnimatePresence>{downloadModal && (<SmartDownloadModal onClose={() => setDownloadModal(false)} onExport={handleExportExcel} />)}</AnimatePresence>
      <AnimatePresence>{showPdfModal && (<SmartReportPdfDownloadModal onClose={() => setShowPdfModal(false)} onExport={handleExportPdf} />)}</AnimatePresence>
    </div>
  );
}

function PembagianTugasUI({ patients, masters, showNotify, user, initialContext = null, onFinished, onExit }) {
  const [selectedDate, setSelectedDate] = useState(initialContext?.date || new Date().toISOString().split('T')[0]);
  const [selectedShift, setSelectedShift] = useState(initialContext?.shift || getCurrentShift().id);
  const [workingNurses, setWorkingNurses] = useState([]);
  const [nurseSearch, setNurseSearch] = useState('');
  const [assignments, setAssignments] = useState({}); 
  const [pbPlans, setPbPlans] = useState(['']); 
  const [isSetup, setIsSetup] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      const docId = initialContext ? `${initialContext.date}_${initialContext.shift}` : `${selectedDate}_${selectedShift}`;
      const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'task_books', docId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const d = snap.data();
        setWorkingNurses(d.workingNurses || []);
        setAssignments(d.assignments || {});
        setPbPlans(d.pbPlans || ['']);
        if (initialContext) setIsSetup(true);
      } else if (!initialContext) {
        setWorkingNurses([]);
        setAssignments({});
        setPbPlans(['']);
      }
    };
    load();
  }, [initialContext, selectedDate, selectedShift]);

  const activePatients = useMemo(() => {
    const { start, end } = getShiftTimeRange(selectedDate, selectedShift);
    const list = patients.filter(p => {
      const masuk = new Date(p.tglJamMasuk);
      const keluar = p.tglJamKeluar ? new Date(p.tglJamKeluar) : null;
      return masuk < end && (!keluar || keluar >= start);
    });
    return list.sort((a, b) => naturalSortBeds(a.bed, b.bed));
  }, [patients, selectedDate, selectedShift]);

  const addNurseToTim = (nurse) => {
    if (!nurse) return;
    if (workingNurses.includes(nurse)) {
      showNotify("Perawat sudah ada dalam tim", "error");
      return;
    }
    setWorkingNurses([...workingNurses, nurse]);
    setNurseSearch('');
  };

  const removeNurseFromTim = (nurse) => {
    setWorkingNurses(workingNurses.filter(n => n !== nurse));
    setAssignments(prev => {
        const next = {...prev};
        delete next[nurse];
        return next;
    });
  };

  const handleManualAssign = (nurse, patientId) => {
    setAssignments(prev => {
      const next = { ...prev };
      if (!nurse) return next;
      if (!next[nurse]) next[nurse] = [];
      if (next[nurse].includes(patientId)) {
        next[nurse] = next[nurse].filter(id => id !== patientId);
      } else {
        next[nurse] = [...next[nurse], patientId];
      }
      return next;
    });
  };

  const handleRandomAssign = () => {
    if (workingNurses.length === 0) return;
    const unassignedPatients = activePatients.filter(p => !Object.values(assignments).flat().includes(p.id));
    if (unassignedPatients.length === 0) {
      showNotify("Semua pasien aktif sudah terbagi", "info");
      return;
    }
    const shuffled = [...unassignedPatients].sort(() => Math.random() - 0.5);
    const nextAssignments = { ...assignments };
    shuffled.forEach((p, idx) => {
      const nurse = workingNurses[idx % workingNurses.length];
      if (!nextAssignments[nurse]) nextAssignments[nurse] = [];
      nextAssignments[nurse].push(p.id);
    });
    setAssignments(nextAssignments);
    showNotify(`${shuffled.length} Pasien Baru Berhasil Dibagikan`);
  };

  const handleAddPb = () => setPbPlans([...pbPlans, '']);
  const handleRemovePb = (index) => {
    if (pbPlans.length === 1) { setPbPlans(['']); return; }
    setPbPlans(pbPlans.filter((_, i) => i !== index));
  };
  const handleUpdatePb = (index, val) => {
    const newPlans = [...pbPlans];
    newPlans[index] = val.toUpperCase();
    setPbPlans(newPlans);
  };

  const handleReset = () => {
    setAssignments({});
    setPbPlans(['']);
    showNotify("Daftar pembagian dikosongkan");
  };

  const handleSaveToCloud = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const docId = `${selectedDate}_${selectedShift}`;
      const taskBookRef = doc(db, 'artifacts', appId, 'public', 'data', 'task_books', docId);
      
      await setDoc(taskBookRef, {
        date: selectedDate,
        shift: selectedShift,
        workingNurses,
        assignments,
        pbPlans,
        updatedAt: new Date().toISOString(),
        createdBy: user.email
      });

      const batch = writeBatch(db);
      for (const p of activePatients) {
        const assignedNurses = workingNurses.filter(n => assignments[n]?.includes(p.id));
        const nurseStr = assignedNurses.join(', ');
        const condDocRef = doc(db, 'artifacts', appId, 'public', 'data', `conds_${selectedDate}`, p.id);
        batch.set(condDocRef, { [selectedShift]: { nrs: nurseStr } }, { merge: true });
      }
      await batch.commit();

      showNotify("Buku Tugas Berhasil Disinkronkan");
      onFinished();
    } catch (e) { showNotify("Gagal Menyimpan Sinkronisasi", "error"); } finally { setLoading(false); }
  };

  const getNursesForPatient = (pId) => workingNurses.filter(nurse => assignments[nurse]?.includes(pId));

  return (
    <div className="space-y-6 pb-10 text-center">
      {!isSetup ? (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ scale: 1, opacity: 1 }} className="max-w-2xl mx-auto bg-white rounded-[2.5rem] p-10 shadow-2xl border border-slate-100 text-center">
          <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-inner text-center"><Users size={40} className="mx-auto" /></div>
          <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight mb-2 text-center">Penentuan Tim Shift</h3>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-8 text-center">Tentukan perawat yang bertugas pada hari dan shift ini.</p>
          
          <div className="grid grid-cols-2 gap-4 mb-8 text-center">
            <Field label="Pilih Tanggal" type="date" value={selectedDate} onChange={setSelectedDate} />
            <Select label="Pilih Shift" value={selectedShift} options={['pagi', 'siang', 'malam']} onChange={setSelectedShift} />
          </div>

          <div className="space-y-4 text-left mb-8 text-left">
            <SearchableSelect label="Cari Nama Perawat" placeholder="Ketik untuk mencari perawat..." options={masters.nurses} value={nurseSearch} onChange={addNurseToTim} />
            <div className="space-y-2 text-left">
                <label className="text-[9px] font-black text-slate-500 uppercase ml-2 tracking-widest text-left">Tim Terpilih ({workingNurses.length})</label>
                <div className="flex flex-wrap gap-2 p-3 border border-slate-100 rounded-2xl bg-slate-50 min-h-[60px] text-left">
                    {workingNurses.map(nurse => (
                        <div key={nurse} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-[9px] font-black uppercase flex items-center gap-2 shadow-sm text-left">
                            {nurse}
                            <button onClick={() => removeNurseFromTim(nurse)} className="hover:text-red-200 transition-colors text-center"><X size={12}/></button>
                        </div>
                    ))}
                    {workingNurses.length === 0 && <p className="text-[9px] font-bold text-slate-300 uppercase italic m-auto w-full text-center">Belum ada perawat terpilih</p>}
                </div>
            </div>
          </div>

          <div className="flex gap-3 text-center"><button onClick={onExit} className="flex-1 py-4 bg-slate-100 text-slate-600 font-black rounded-2xl text-[11px] uppercase flex items-center justify-center gap-2 text-center"> <DoorOpen size={16}/> Exit </button><button disabled={workingNurses.length === 0} onClick={() => setIsSetup(true)} className="flex-[2] py-4 bg-blue-600 text-white font-black rounded-2xl text-[11px] uppercase shadow-lg shadow-blue-100 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 text-center">Lanjut ke Pembagian Pasien</button></div>
        </motion.div>
      ) : (
        <div className="space-y-6 text-center">
          <div className="flex flex-col md:flex-row md:items-center justify-between bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm gap-4 text-left">
            <div className="flex items-center gap-4 text-left">
              <button onClick={() => setIsSetup(false)} className="p-2 hover:bg-slate-50 rounded-xl text-slate-400 text-center"><ChevronLeft size={24}/></button>
              <div className="leading-none text-left">
                <h4 className="text-lg font-black text-slate-800 uppercase tracking-tight">Pembagian Pasien ICU</h4>
                <p className="text-[9px] font-bold text-blue-600 uppercase tracking-widest mt-1">Shift {selectedShift} | {selectedDate}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-center">
              <button onClick={handleReset} className="px-5 py-3 bg-red-50 text-red-600 font-black rounded-xl text-[10px] uppercase flex items-center gap-2 hover:bg-red-100 transition-all border border-red-100 text-center"> <RotateCcw size={14} /> Reset Data</button>
              <button onClick={handleRandomAssign} className="px-5 py-3 bg-indigo-600 text-white font-black rounded-xl text-[10px] uppercase flex items-center gap-2 shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all text-center"> <Shuffle size={14} /> Acak Pasien</button>
              <button onClick={onExit} className="px-5 py-3 bg-slate-100 text-slate-600 font-black rounded-xl text-[10px] uppercase flex items-center gap-2 hover:bg-slate-200 transition-all text-center"> <DoorOpen size={14} /> Exit</button>
              <button disabled={loading} onClick={handleSaveToCloud} className="px-5 py-3 bg-emerald-600 text-white font-black rounded-xl text-[10px] uppercase flex items-center gap-2 shadow-lg shadow-emerald-100 hover:bg-emerald-700 transition-all disabled:opacity-50 text-center">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {loading ? 'MENYIMPAN...' : 'SIMPAN & SINKRON'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-center">
            <div className="lg:col-span-1 bg-slate-100/50 p-6 rounded-[2.5rem] border border-slate-200 border-dashed text-left">
              <div className="flex items-center justify-between mb-6 text-left">
                <div className="flex items-center gap-2 text-slate-500 text-left">
                  <Bed size={18}/>
                  <h5 className="text-[11px] font-black uppercase tracking-widest">Daftar Pasien Aktif</h5>
                </div>
                <span className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded-md text-[9px] font-black">{activePatients.length}</span>
              </div>
              <div className="space-y-3 text-left">
                {activePatients.map(p => {
                  const assignedNurses = getNursesForPatient(p.id);
                  const exitTime = p.statusKeluar ? new Date(p.tglJamKeluar) : null;
                  const shiftRange = getShiftTimeRange(selectedDate, selectedShift);
                  const exitDateOnly = p.tglJamKeluar?.split('T')[0];
                  const exitedThisShift = exitTime && exitTime >= shiftRange.start && exitTime < shiftRange.end && exitDateOnly === selectedDate;
                  
                  return (
                    <motion.div layout key={p.id} className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col gap-3 group transition-all hover:border-blue-200 text-left">
                      <div className="flex items-center justify-between text-left">
                        <div className="text-left leading-tight text-left">
                          <p className="text-[10px] font-black text-slate-800 uppercase text-left">{p.nama}</p>
                          <p className="text-[8px] font-bold text-blue-600 uppercase mt-0.5 text-left">MR: {p.mr} | {p.bed}</p>
                          <div className="flex items-center gap-1 mt-1 text-left">
                            <Wind size={10} className="text-blue-500" />
                            <span className="text-[7px] text-blue-600 uppercase font-black text-left">{p.oksigenSaatIni || '-'}</span>
                          </div>
                          {exitedThisShift && <span className="text-[7px] font-black text-red-500 uppercase block mt-1 text-left">STATUS: {p.statusKeluar}</span>}
                        </div>
                        <select className="p-1.5 bg-slate-50 border border-slate-100 rounded-lg text-[8px] font-black uppercase text-slate-500 outline-none cursor-pointer hover:border-blue-300" onChange={(e) => handleManualAssign(e.target.value, p.id)} value="">
                          <option value="">Tambah PP...</option>
                          {workingNurses.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                      {assignedNurses.length > 0 && (
                        <div className="flex flex-wrap gap-1 border-t border-slate-50 pt-2 text-center">
                          {assignedNurses.map(nurse => (
                            <span key={nurse} className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded text-[7px] font-black uppercase flex items-center gap-1 border border-indigo-100 text-center">
                              {nurse}
                              <button onClick={() => handleManualAssign(nurse, p.id)} className="hover:text-red-500 text-center"><X size={8}/></button>
                            </span>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>

            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 content-start text-center">
              {workingNurses.map(nurse => (
                <div key={nurse} className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm flex flex-col min-h-[200px] text-left">
                  <div className="flex items-center justify-between mb-6 text-left">
                    <div className="flex items-center gap-3 text-left">
                      <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center font-black text-xs shadow-inner uppercase text-center">{nurse.charAt(0)}</div>
                      <div className="text-left leading-none text-left">
                        <p className="text-[11px] font-black text-slate-800 uppercase tracking-tight">{nurse}</p>
                        <p className="text-[8px] font-bold text-emerald-500 uppercase mt-1">Perawat Pelaksana</p>
                      </div>
                    </div>
                    <span className="px-2 py-0.5 bg-blue-600 text-white rounded-md text-[9px] font-black">{assignments[nurse]?.length || 0}</span>
                  </div>
                  <div className="space-y-2 flex-1 text-left">
                    {(assignments[nurse] || []).map(pId => {
                      const p = activePatients.find(x => x.id === pId);
                      if (!p) return null;
                      return (
                        <div key={p.id} className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex items-center justify-between text-left">
                          <div className="text-left leading-tight text-left">
                             <p className="text-[10px] font-bold text-slate-700 uppercase text-left">{p.bed} - {p.nama}</p>
                          </div>
                          <button onClick={() => handleManualAssign(nurse, p.id)} className="text-slate-300 hover:text-red-500 transition-colors p-1 text-center"><X size={14}/></button>
                        </div>
                      );
                    })}
                    {(assignments[nurse] || []).length === 0 && (
                      <div className="h-full flex flex-col items-center justify-center opacity-30 italic py-10 text-center">
                        <p className="text-[9px] font-bold uppercase text-center">Belum Ada Pasien</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 bg-indigo-50/50 rounded-[2.5rem] p-8 border border-indigo-100 shadow-inner text-center">
            <div className="flex items-center justify-between mb-6 text-left">
                <div className="flex items-center gap-3 text-left">
                    <div className="p-2 bg-indigo-600 text-white rounded-xl shadow-md text-center"><MessageSquarePlus size={20}/></div>
                    <div className="text-left leading-none text-left">
                        <h5 className="text-sm font-black text-slate-800 uppercase tracking-widest">Rencana Pasien Baru (PB)</h5>
                        <p className="text-[8px] font-bold text-indigo-600 uppercase mt-1">Monitoring rencana pasien yang akan datang</p>
                    </div>
                </div>
                <button onClick={handleAddPb} className="px-4 py-2 bg-indigo-600 text-white text-[9px] font-black rounded-xl uppercase flex items-center gap-2 hover:bg-indigo-700 transition-all shadow-md text-center">
                    <PlusCircle size={14}/> Tambah Rencana
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
                {pbPlans.map((plan, index) => (
                    <div key={index} className="flex gap-2 items-start group text-left">
                        <div className="flex-1 text-left">
                            <label className="text-[8px] font-black text-indigo-400 uppercase ml-2 mb-1 block text-left">PB {index + 1}</label>
                            <textarea className="w-full p-4 bg-white border border-slate-200 rounded-2xl text-[10px] font-bold uppercase outline-none focus:ring-4 focus:ring-indigo-200 transition-all h-20 placeholder:text-slate-300 shadow-sm" placeholder="Contoh: NY. X OK BED 1 NEURO" value={plan} onChange={(e) => handleUpdatePb(index, e.target.value)} />
                        </div>
                        <button onClick={() => handleRemovePb(index)} className="mt-6 p-2 text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 text-center"><MinusCircle size={20}/></button>
                    </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BukuTugasUI({ patients, masters, showNotify, onEditRequest }) {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [shiftData, setShiftData] = useState({ pagi: null, siang: null, malam: null });
  const [activeRowId, setActiveRowId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);

  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'task_books'), (snap) => {
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const dateRecords = data.filter(tb => tb.date === selectedDate);
        setShiftData({
            pagi: dateRecords.find(r => r.shift === 'pagi'),
            siang: dateRecords.find(r => r.shift === 'siang'),
            malam: dateRecords.find(r => r.shift === 'malam'),
        });
        setLoading(false);
    }, (err) => { setLoading(false); });
    return () => unsub();
  }, [selectedDate]);

  const findNursesForPatient = (shiftKey, patientId, recordsData = null) => {
    const record = recordsData ? recordsData[shiftKey] : shiftData[shiftKey];
    if (!record || !record.assignments) return '-';
    const nurses = [];
    for (const [nurse, patientIds] of Object.entries(record.assignments)) {
        if (patientIds.includes(patientId)) nurses.push(nurse);
    }
    return nurses.length > 0 ? nurses.join(', ') : '-';
  };

  const handleExportExcel = async (fileName, range) => {
    if (!window.XLSX) { showNotify("Library Excel Belum Siap", "error"); return; }
    try {
      const wb = window.XLSX.utils.book_new();
      const dates = []; let curr = new Date(range.start); const end = new Date(range.end);
      while (curr <= end) { dates.push(new Date(curr).toISOString().split('T')[0]); curr.setDate(curr.getDate() + 1); }
      const taskBooksSnap = await getDocs(collection(db, 'artifacts', appId, 'public', 'data', 'task_books'));
      const allTaskBooks = taskBooksSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      for (const dStr of dates) {
        const dateRecords = allTaskBooks.filter(r => r.date === dStr);
        const dayShifts = {
          pagi: dateRecords.find(r => r.shift === 'pagi'),
          siang: dateRecords.find(r => r.shift === 'siang'),
          malam: dateRecords.find(r => r.shift === 'malam'),
        };

        const dailyPatients = patients.filter(p => {
          const masukDate = p.tglJamMasuk?.split('T')[0];
          const keluarDate = p.tglJamKeluar?.split('T')[0];
          return masukDate <= dStr && (!p.statusKeluar || keluarDate >= dStr);
        });
        dailyPatients.sort((a, b) => naturalSortBeds(a.bed, b.bed));

        const sheetData = dailyPatients.map((p, idx) => {
          const shiftRanges = {
            pagi: getShiftTimeRange(dStr, 'pagi'),
            siang: getShiftTimeRange(dStr, 'siang'),
            malam: getShiftTimeRange(dStr, 'malam')
          };

          const pExitTime = p.statusKeluar ? new Date(p.tglJamKeluar) : null;
          const exitDateOnly = p.tglJamKeluar?.split('T')[0];
          
          const statusTags = {};
          ['pagi', 'siang', 'malam'].forEach(s => {
            const range = shiftRanges[s];
            if (pExitTime && pExitTime >= range.start && pExitTime < range.end && dStr === exitDateOnly) {
              statusTags[s] = `[${p.statusKeluar.toUpperCase()}]`;
            } else if (pExitTime && pExitTime < range.start) {
              statusTags[s] = 'OUT';
            } else {
              statusTags[s] = '';
            }
          });

          return {
            'NO': idx + 1, 
            'NAMA PASIEN': p.nama?.toUpperCase() + (p.statusKeluar && dStr === exitDateOnly ? ` [${p.statusKeluar.toUpperCase()}]` : ''), 
            'NOMOR MR': p.mr, 
            'BED': p.bed, 
            'OKSIGEN': p.oksigenSaatIni || '-', 
            'SHIFT PAGI (PP)': findNursesForPatient('pagi', p.id, dayShifts) + (statusTags.pagi ? ` ${statusTags.pagi}` : ''), 
            'SHIFT SIANG (PP)': findNursesForPatient('siang', p.id, dayShifts) + (statusTags.siang ? ` ${statusTags.siang}` : ''), 
            'SHIFT MALAM (PP)': findNursesForPatient('malam', p.id, dayShifts) + (statusTags.malam ? ` ${statusTags.malam}` : '')
          };
        });

        sheetData.push({});
        sheetData.push({ 'NO': 'RENCANA PASIEN BARU / PB' });

        const maxPlans = Math.max(
          dayShifts.pagi?.pbPlans?.length || 0,
          dayShifts.siang?.pbPlans?.length || 0,
          dayShifts.malam?.pbPlans?.length || 0
        );

        if (maxPlans > 0) {
          for (let i = 0; i < maxPlans; i++) {
            sheetData.push({
              'SHIFT PAGI (PP)': dayShifts.pagi?.pbPlans?.[i] ? `PB ${i+1}: ${dayShifts.pagi.pbPlans[i]}` : '',
              'SHIFT SIANG (PP)': dayShifts.siang?.pbPlans?.[i] ? `PB ${i+1}: ${dayShifts.siang.pbPlans[i]}` : '',
              'SHIFT MALAM (PP)': dayShifts.malam?.pbPlans?.[i] ? `PB ${i+1}: ${dayShifts.malam.pbPlans[i]}` : ''
            });
          }
        }

        if (sheetData.length > 0) {
          const ws = window.XLSX.utils.json_to_sheet(sheetData, { origin: 'A3' });
          window.XLSX.utils.book_append_sheet(wb, ws, dStr);
        }
      }

      if (wb.SheetNames.length === 0) { showNotify("Tidak ada data pembagian tugas pada rentang ini", "error"); return; }
      window.XLSX.writeFile(wb, `${fileName}.xlsx`); showNotify("Buku Tugas Berhasil Diunduh"); setShowDownloadModal(false);
    } catch (e) { showNotify("Gagal Eksport Buku Tugas", "error"); }
  };

  const handleExportPdf = async (fileName, range) => {
    if (!window.jspdf || !window.jspdf.jsPDF) { showNotify("Library PDF Belum Siap", "error"); return; }
    try {
      const { jsPDF } = window.jspdf;
      const pdfDoc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'legal' });
      
      if (typeof pdfDoc.autoTable !== 'function') {
          showNotify("Library AutoTable belum siap, tunggu beberapa detik.", "error");
          return;
      }

      const dates = []; let curr = new Date(range.start); const end = new Date(range.end);
      while (curr <= end) { dates.push(new Date(curr).toISOString().split('T')[0]); curr.setDate(curr.getDate() + 1); }
      
      const taskBooksSnap = await getDocs(collection(db, 'artifacts', appId, 'public', 'data', 'task_books'));
      const allTaskBooks = taskBooksSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      let pagesAdded = 0;

      for (const dStr of dates) {
        const dateRecords = allTaskBooks.filter(r => r.date === dStr);
        const dayShifts = {
          pagi: dateRecords.find(r => r.shift === 'pagi'),
          siang: dateRecords.find(r => r.shift === 'siang'),
          malam: dateRecords.find(r => r.shift === 'malam'),
        };

        const dailyPatients = patients.filter(p => {
          const masukDate = p.tglJamMasuk?.split('T')[0];
          const keluarDate = p.tglJamKeluar?.split('T')[0];
          return masukDate <= dStr && (!p.statusKeluar || keluarDate >= dStr);
        });
        dailyPatients.sort((a, b) => naturalSortBeds(a.bed, b.bed));
        
        if (dailyPatients.length === 0 && !dayShifts.pagi && !dayShifts.siang && !dayShifts.malam) continue;

        if (pagesAdded > 0) pdfDoc.addPage('legal', 'landscape');
        pagesAdded++;

        pdfDoc.setFontSize(16);
        pdfDoc.setFont("helvetica", "bold");
        pdfDoc.setTextColor(30, 58, 138);
        pdfDoc.text(`LAPORAN BUKU TUGAS ICU - RSUD PASAR MINGGU`, 40, 40);
        
        pdfDoc.setFontSize(11);
        pdfDoc.setFont("helvetica", "normal");
        pdfDoc.setTextColor(100, 116, 139);
        const dateObj = new Date(dStr);
        pdfDoc.text(`TANGGAL: ${dateObj.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }).toUpperCase()}`, 40, 60);

        const tableBody = dailyPatients.map((p, idx) => {
          const shiftRanges = {
            pagi: getShiftTimeRange(dStr, 'pagi'),
            siang: getShiftTimeRange(dStr, 'siang'),
            malam: getShiftTimeRange(dStr, 'malam')
          };

          const pExitTime = p.statusKeluar ? new Date(p.tglJamKeluar) : null;
          const exitDateOnly = p.tglJamKeluar?.split('T')[0];
          
          const statusTags = {};
          ['pagi', 'siang', 'malam'].forEach(s => {
            const range = shiftRanges[s];
            if (pExitTime && pExitTime >= range.start && pExitTime < range.end && dStr === exitDateOnly) {
              statusTags[s] = `\n[${p.statusKeluar.toUpperCase()}]`;
            } else if (pExitTime && pExitTime < range.start) {
              statusTags[s] = '\nOUT';
            } else {
              statusTags[s] = '';
            }
          });

          return [
            idx + 1,
            `${p.nama?.toUpperCase() || '-'}\nMR: ${p.mr || '-'}${p.statusKeluar && dStr === exitDateOnly ? `\n[KELUAR: ${p.statusKeluar.toUpperCase()}]` : ''}`,
            `${p.bed || '-'}\nO2: ${p.oksigenSaatIni || '-'}`,
            `${findNursesForPatient('pagi', p.id, dayShifts)}${statusTags.pagi}`,
            `${findNursesForPatient('siang', p.id, dayShifts)}${statusTags.siang}`,
            `${findNursesForPatient('malam', p.id, dayShifts)}${statusTags.malam}`
          ];
        });

        const maxPlans = Math.max(
          dayShifts.pagi?.pbPlans?.length || 0,
          dayShifts.siang?.pbPlans?.length || 0,
          dayShifts.malam?.pbPlans?.length || 0
        );

        const pbRow = [
          { content: 'RENCANA PASIEN BARU / PB', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold', fillColor: [248, 250, 252], textColor: [71, 85, 105] } },
          { content: '', styles: { fillColor: [255, 255, 255] } },
          { content: '', styles: { fillColor: [255, 255, 255] } },
          { content: '', styles: { fillColor: [255, 255, 255] } }
        ];
        
        let pbContentPagi = '';
        let pbContentSiang = '';
        let pbContentMalam = '';

        if (maxPlans > 0) {
            for(let i=0; i<maxPlans; i++) {
                if (dayShifts.pagi?.pbPlans?.[i]) pbContentPagi += `PB ${i+1}: ${dayShifts.pagi.pbPlans[i]}\n`;
                if (dayShifts.siang?.pbPlans?.[i]) pbContentSiang += `PB ${i+1}: ${dayShifts.siang.pbPlans[i]}\n`;
                if (dayShifts.malam?.pbPlans?.[i]) pbContentMalam += `PB ${i+1}: ${dayShifts.malam.pbPlans[i]}\n`;
            }
        } else {
            pbContentPagi = 'Tidak ada rencana';
            pbContentSiang = 'Tidak ada rencana';
            pbContentMalam = 'Tidak ada rencana';
        }

        pbRow[1].content = pbContentPagi.trim();
        pbRow[2].content = pbContentSiang.trim();
        pbRow[3].content = pbContentMalam.trim();

        tableBody.push(pbRow);

        pdfDoc.autoTable({
          startY: 80,
          head: [['NO', 'NAMA PASIEN & MR', 'BED / OKSIGEN', 'SHIFT PAGI (PP)', 'SHIFT SIANG (PP)', 'SHIFT MALAM (PP)']],
          body: tableBody,
          theme: 'grid',
          styles: { fontSize: 8, cellPadding: 5, valign: 'middle', lineColor: [226, 232, 240], lineWidth: 0.5 },
          headStyles: { fillColor: [71, 85, 105], textColor: 255, fontStyle: 'bold', halign: 'center' },
          columnStyles: {
            0: { halign: 'center', cellWidth: 30 },
            1: { cellWidth: 200 },
            2: { halign: 'center', cellWidth: 120 },
            3: { halign: 'center', cellWidth: 180 },
            4: { halign: 'center', cellWidth: 180 },
            5: { halign: 'center', cellWidth: 180 }
          },
          margin: { left: 40, right: 40, bottom: 40 }
        });
      }

      if (pagesAdded === 0) { showNotify("Tidak ada data untuk dirender pada rentang tanggal ini", "error"); return; }
      
      pdfDoc.save(`${fileName}.pdf`);
      showNotify("PDF Buku Tugas Berhasil Diunduh");
      setShowPdfModal(false);
    } catch (e) {
      showNotify("Gagal Eksport PDF", "error");
      console.error(e);
    }
  };

  return (
    <div className="space-y-6 pb-10 text-center">
      <div className="flex flex-col md:flex-row md:items-center justify-between bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm gap-4 text-left">
        <div className="flex items-center gap-3 text-left">
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl text-center"><ClipboardList size={24}/></div>
            <div className="text-left leading-none text-left">
                <h4 className="text-lg font-black text-slate-800 uppercase tracking-tight">Monitoring Buku Tugas</h4>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">Status pembagian perawat & monitoring PB</p>
            </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-center">
            <div className="flex items-center gap-3 bg-slate-50 p-2 rounded-2xl border border-slate-200 text-center">
                <History className="text-indigo-600 ml-2" size={18}/>
                <input type="date" className="bg-transparent font-black text-xs text-slate-700 outline-none uppercase text-center" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />
            </div>
            <button onClick={() => setShowDownloadModal(true)} className="p-3 bg-emerald-600 text-white rounded-2xl shadow-lg hover:bg-emerald-700 transition-all flex items-center gap-2 text-center">
                <FileSpreadsheet size={18} />
                <span className="text-[10px] font-black uppercase hidden sm:block">Export Excel</span>
            </button>
            <button onClick={() => setShowPdfModal(true)} className="p-3 bg-red-600 text-white rounded-2xl shadow-lg hover:bg-red-700 transition-all flex items-center gap-2 text-center">
                <FileText size={18} />
                <span className="text-[10px] font-black uppercase hidden sm:block">Export PDF</span>
            </button>
        </div>
      </div>
      <div className="bg-white rounded-[2rem] shadow-xl border border-slate-100 overflow-hidden overflow-x-auto custom-scrollbar relative text-center">
        {loading ? (
            <div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-blue-600 text-center" size={32}/></div>
        ) : (
            <table className="w-full border-collapse table-auto min-w-[1000px] text-center">
                <thead className="bg-slate-50 border-b border-slate-200 text-[8px] font-black text-slate-400 uppercase tracking-tighter text-center">
                    <tr>
                        <th className="p-3 w-[50px] border-r border-slate-100">NO</th>
                        <th className="p-3 w-[180px] border-r border-slate-100 text-left pl-6">NAMA PASIEN</th>
                        <th className="p-3 w-[120px] border-r border-slate-100">BED / OKSIGEN</th>
                        {['pagi', 'siang', 'malam'].map(s => (
                            <th key={s} className={`p-3 w-[200px] border-r ${s === 'pagi' ? 'bg-blue-50/10 text-blue-600' : s === 'siang' ? 'bg-indigo-50/10 text-indigo-600' : 'bg-violet-50/10 text-violet-600'}`}>
                                <div className="flex items-center justify-center gap-2 text-center">
                                    SHIFT {s.toUpperCase()}
                                    <button onClick={() => onEditRequest(selectedDate, s)} className="p-1 hover:bg-white rounded shadow-sm text-slate-400 hover:text-blue-600 transition-all text-center"><Edit3 size={10}/></button>
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 text-[10px] text-center">
                    {patients.filter(p => {
                      const masuk = p.tglJamMasuk?.split('T')[0];
                      const keluar = p.tglJamKeluar?.split('T')[0];
                      return masuk <= selectedDate && (!p.statusKeluar || keluar >= selectedDate);
                    })
                    .sort((a, b) => naturalSortBeds(a.bed, b.bed))
                    .map((p, i) => {
                        const shiftRanges = {
                          pagi: getShiftTimeRange(selectedDate, 'pagi'),
                          siang: getShiftTimeRange(selectedDate, 'siang'),
                          malam: getShiftTimeRange(selectedDate, 'malam')
                        };
                        const pExitTime = p.statusKeluar ? new Date(p.tglJamKeluar) : null;
                        const exitDateOnly = p.tglJamKeluar?.split('T')[0];

                        return (
                          <tr key={p.id} onClick={() => setActiveRowId(p.id)} className={`transition-all duration-200 h-14 group cursor-pointer ${activeRowId === p.id ? 'bg-blue-200/90 outline outline-2 outline-blue-500 shadow-md relative z-10 font-semibold' : 'hover:bg-slate-50/50'}`}>
                              <td className={`p-3 border-r border-slate-100 font-black text-slate-300 text-center transition-all duration-200 ${activeRowId === p.id ? 'border-l-[6px] border-l-blue-600' : 'border-l-[6px] border-l-transparent group-hover:border-l-blue-300'}`}>{i + 1}</td>
                              <td className="p-3 border-r border-slate-100 text-left pl-6 text-left">
                                  <p className="font-black text-slate-800 uppercase text-left">{p.nama}</p>
                                  <p className="font-bold text-blue-500 text-[8px] text-left">MR: {p.mr}</p>
                                  {p.statusKeluar && exitDateOnly === selectedDate && (
                                    <span className="text-[7px] font-black text-red-500 uppercase block text-left">KELUAR: {p.statusKeluar}</span>
                                  )}
                              </td>
                              <td className="p-3 border-r border-slate-100 font-black text-slate-600 uppercase text-center">
                                <p className="text-slate-800 text-center">{p.bed}</p>
                                <div className="flex items-center justify-center gap-1 mt-1 text-center">
                                  <Wind size={10} className="text-blue-500" />
                                  <span className="text-[7px] text-blue-600 font-black uppercase text-center">{p.oksigenSaatIni || '-'}</span>
                                </div>
                              </td>
                              {['pagi', 'siang', 'malam'].map(s => {
                                const range = shiftRanges[s];
                                const isGoneBeforeThisShift = pExitTime && pExitTime < range.start;
                                const exitedThisShift = pExitTime && pExitTime >= range.start && pExitTime < range.end && exitDateOnly === selectedDate;

                                return (
                                  <td key={s} className="p-3 border-r border-slate-100 font-bold text-slate-700 uppercase whitespace-pre-wrap text-center">
                                      {isGoneBeforeThisShift ? (
                                        <span className="text-slate-300 opacity-50 italic text-center">OUT</span>
                                      ) : (
                                        <>
                                          {findNursesForPatient(s, p.id, shiftData)}
                                          {exitedThisShift && <div className="text-[7px] text-red-500 mt-1 font-black text-center">[{p.statusKeluar.toUpperCase()}]</div>}
                                        </>
                                      )}
                                  </td>
                                );
                              })}
                          </tr>
                        );
                    })}
                </tbody>
                <tfoot className="bg-slate-50/50 border-t border-slate-200">
                    <tr>
                        <td colSpan={3} className="p-4 text-[9px] font-black text-slate-400 uppercase tracking-widest border-r border-slate-200 text-left pl-10 text-left">RENCANA PASIEN BARU / PB</td>
                        {['pagi', 'siang', 'malam'].map(s => (
                            <td key={`plan-${s}`} className="p-4 border-r border-slate-100 text-left align-top text-left">
                                {shiftData[s]?.pbPlans?.map((plan, idx) => (
                                    plan && (
                                        <div key={idx} className="mb-2 last:mb-0 bg-white p-2 rounded-lg border border-slate-100 shadow-sm text-center">
                                            <p className="text-[8px] font-black text-indigo-500 uppercase leading-none mb-1 text-center">PB {idx + 1}:</p>
                                            <p className="text-[9px] font-bold text-slate-600 uppercase leading-tight italic text-center">{plan}</p>
                                        </div>
                                    )
                                ))}
                                {(!shiftData[s]?.pbPlans || shiftData[s].pbPlans.every(v => !v)) && (
                                    <p className="text-[8px] font-bold text-slate-300 uppercase italic text-center py-2 text-center">Tidak ada rencana</p>
                                )}
                            </td>
                        ))}
                    </tr>
                </tfoot>
            </table>
        )}
      </div>
      <AnimatePresence>
        {showDownloadModal && (
          <SmartDownloadModal 
            onClose={() => setShowDownloadModal(false)} 
            onExport={handleExportExcel} 
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showPdfModal && (
          <SmartBukuTugasPdfDownloadModal 
            onClose={() => setShowPdfModal(false)} 
            onExport={handleExportPdf} 
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function MasterDataManagement({ masters, onUpdate, user, showNotify }) {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);

  const categories = [ 
    { id: 'beds', label: 'Daftar Bed ICU', icon: Bed },
    { id: 'unit_categories', label: 'Daftar Unit', icon: LayoutDashboard },
    { id: 'locations', label: 'Asal Pasien / Lokasi', icon: Activity }, 
    { id: 'jaminan', label: 'Jenis Jaminan', icon: FileText }, 
    { id: 'dpjp', label: 'Dokter DPJP', icon: UserCog }, 
    { id: 'raber', label: 'Dokter Raber', icon: UserCog }, 
    { id: 'oxygen', label: 'Opsi Pemakaian Oksigen', icon: Activity }, 
    { id: 'exitStatus', label: 'Status Keluar ICU', icon: ArrowUpCircle }, 
    { id: 'nurses', label: 'Perawat Pelaksana', icon: UserCheck } 
  ];
  
  const [activeCat, setActiveCat] = useState(categories[0].id);
  const [newItem, setNewItem] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!password) return;
    setVerifying(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      setIsUnlocked(true);
      showNotify("Akses Master Terbuka");
    } catch (err) {
      showNotify("Password Akun Salah", "error");
    } finally {
      setVerifying(false);
    }
  };

  if (!isUnlocked) {
    return (
      <div className="max-w-md mx-auto py-20 text-center">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white p-10 rounded-[3rem] shadow-2xl border border-slate-100 text-center">
          <div className="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner text-center">
            <Lock size={40} className="mx-auto" />
          </div>
          <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight mb-2 text-center">Akses Terkunci</h3>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-8 text-center">Masukkan password akun Anda untuk mengelola data master.</p>
          <form onSubmit={handleUnlock} className="space-y-4 text-center">
            <div className="relative group text-center">
              <KeyRound size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-500 transition-colors" />
              <input 
                type="password" 
                placeholder="Password Akun..." 
                className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-100 font-bold text-sm transition-all text-center"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>
            <button 
              disabled={verifying}
              type="submit" 
              className="w-full py-4 bg-indigo-600 text-white font-black rounded-2xl text-[11px] uppercase shadow-lg shadow-indigo-100 flex items-center justify-center gap-2 hover:bg-indigo-700 active:scale-95 transition-all text-center"
            >
              {verifying ? <Loader2 size={16} className="animate-spin" /> : <Unlock size={16} />}
              {verifying ? 'VERIFIKASI...' : 'BUKA AKSES MASTER'}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  const currentItems = Array.isArray(masters[activeCat]) ? masters[activeCat] : [];
  const addItem = (e) => { e.preventDefault(); if (!newItem.trim() || currentItems.includes(newItem.trim())) return; onUpdate(activeCat, [...currentItems, newItem.trim()]); setNewItem(''); };
  const removeItem = (item) => { onUpdate(activeCat, currentItems.filter(i => i !== item)); };
  const saveEditing = () => { if (editingIndex === null) return; const nextItems = [...currentItems]; nextItems[editingIndex] = editingValue.trim(); onUpdate(activeCat, nextItems); setEditingIndex(null); };
  const moveItem = (idx, direction) => { const nextIdx = direction === 'up' ? idx - 1 : idx + 1; if (nextIdx < 0 || nextIdx >= currentItems.length) return; const nextItems = [...currentItems]; [nextItems[idx], nextItems[nextIdx]] = [nextItems[nextIdx], nextItems[idx]]; onUpdate(activeCat, nextItems); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 pb-10 text-center">
      <div className="lg:col-span-1 space-y-2 text-center">{categories.map(cat => (<button key={cat.id} onClick={() => { setActiveCat(cat.id); setEditingIndex(null); }} className={`w-full flex items-center gap-3 p-4 rounded-2xl transition-all border text-left ${activeCat === cat.id ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-100' : 'bg-white text-slate-500 border-slate-100'} text-center`}><cat.icon size={18} className="mx-auto" /><div className="leading-none text-left"><p className="text-[10px] font-black uppercase tracking-tight text-center">{cat.label}</p></div></button>))}</div>
      <div className="lg:col-span-3 space-y-6 text-center"><div className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm text-center"><div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 text-center"><h3 className="text-lg font-black text-slate-800 uppercase text-center w-full">{categories.find(c => c.id === activeCat)?.label}</h3><form onSubmit={addItem} className="flex gap-2 w-full md:w-auto text-center"><input type="text" placeholder="Tambah Baru..." className="flex-1 md:w-64 px-5 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-xs text-center" value={newItem} onChange={e => setNewItem(e.target.value)} /><button type="submit" className="p-3 bg-blue-600 text-white rounded-xl text-center"><Plus size={20}/></button></form></div>
          <div className="grid grid-cols-1 gap-3 text-center">{currentItems.map((item, idx) => (<motion.div layout key={idx} className="group flex items-center justify-between p-4 bg-slate-50 border border-slate-100 rounded-2xl hover:bg-white transition-all text-center">{editingIndex === idx ? (<div className="flex-1 flex gap-2 mr-4 text-center"><input autoFocus className="flex-1 px-3 py-1 bg-white border border-blue-400 rounded-lg text-xs font-black uppercase outline-none text-center" value={editingValue} onChange={e => setEditingValue(e.target.value)} /><button onClick={saveEditing} className="p-2 bg-emerald-500 text-white rounded-lg text-center"><Save size={14}/></button><button onClick={() => setEditingIndex(null)} className="p-2 bg-slate-200 text-slate-600 rounded-lg text-center"><X size={14}/></button></div>) : (<span className="text-xs font-bold text-slate-700 uppercase text-center w-full">{String(item)}</span>)}<div className="flex items-center gap-1 text-center"><div className="flex flex-col gap-1 mr-4 opacity-0 group-hover:opacity-100 transition-opacity text-center"><button onClick={() => moveItem(idx, 'up')} disabled={idx === 0} className="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30 text-center"><ArrowUp size={14}/></button><button onClick={() => moveItem(idx, 'down')} disabled={idx === currentItems.length - 1} className="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30 text-center"><ArrowDown size={14}/></button></div><button onClick={() => { setEditingIndex(idx); setEditingValue(String(item)); }} className="p-2 text-slate-300 hover:text-emerald-500 opacity-0 group-hover:opacity-100 transition-all text-center"><Edit3 size={16}/></button><button onClick={() => removeItem(item)} className="p-2 text-slate-300 hover:text-red-500 text-center"><Trash2 size={16} /></button></div></motion.div>))}</div>
        </div></div></div>
  );
}

function BackupRestore({ patients, bookings, infoNotes, masters, showNotify }) { 
  const [progress, setProgress] = useState(0); 
  const [processing, setProcessing] = useState(false); 
  const [importData, setImportData] = useState(null); 
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const fileInputRef = useRef(null);
  
  const secureData = (data) => btoa(unescape(encodeURIComponent(JSON.stringify(data))));
  const unsecureData = (str) => JSON.parse(decodeURIComponent(escape(atob(str))));
  
  const handleBackup = async () => {
    setProcessing(true); setProgress(10);
    try {
      const pjSnap = await getDocs(collection(db, 'artifacts', appId, 'public', 'data', 'shift_pj')); 
      const shiftPj = pjSnap.docs.map(d => ({id: d.id, ...d.data()})); 
      setProgress(20);
      const tbSnap = await getDocs(collection(db, 'artifacts', appId, 'public', 'data', 'task_books'));
      const taskBooks = tbSnap.docs.map(d => ({id: d.id, ...d.data()}));
      setProgress(30);
      const datesToScan = new Set(); 
      patients.forEach(p => { if(p.tglJamMasuk) datesToScan.add(p.tglJamMasuk.split('T')[0]); }); 
      datesToScan.add(new Date().toISOString().split('T')[0]);
      const conditionData = []; 
      const dateArray = Array.from(datesToScan);
      for (let i = 0; i < dateArray.length; i++) { 
        const dateStr = dateArray[i]; 
        const condSnap = await getDocs(collection(db, 'artifacts', appId, 'public', 'data', 'conds_' + dateStr)); 
        if (!condSnap.empty) { 
          conditionData.push({ date: dateStr, docs: condSnap.docs.map(d => ({ patientId: d.id, data: d.data() })) }); 
        } 
        setProgress(30 + Math.floor(((i + 1) / dateArray.length) * 50)); 
      }
      const fullBackup = { 
        version: '15.8', timestamp: new Date().toISOString(), patients, bookings, infoNotes, masters, shiftPj, taskBooks, conditions: conditionData 
      };
      const a = document.createElement('a'); 
      a.href = URL.createObjectURL(new Blob([secureData(fullBackup)], {type: 'text/plain'})); 
      a.download = `ICU_SMART_BACKUP_${new Date().toLocaleDateString().replace(/\//g,'-')}.pmb`; 
      a.click(); 
      setProgress(100); 
      showNotify("Seluruh Paket Data Terbackup");
    } catch (e) { showNotify("Gagal Backup", "error"); } finally { setProcessing(false); }
  };

  const handleRestore = async (overwrite) => {
    if(!importData) return; setProcessing(true); setProgress(0);
    try {
      const { patients: bPats, bookings: bBooks, infoNotes: bNotes, masters: bMasters, shiftPj: bPjs, conditions: bConds, taskBooks: bTaskBooks } = importData;
      if (bMasters) await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'master_config', 'settings'), bMasters);
      if (bPats) { 
        const colRef = collection(db, 'artifacts', appId, 'public', 'data', 'patients'); 
        if (overwrite) { const s = await getDocs(colRef); for (const d of s.docs) await deleteDoc(d.ref); } 
        for (const p of bPats) { await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'patients', p.id || crypto.randomUUID()), p); } 
      }
      if (bBooks) { 
        const colRef = collection(db, 'artifacts', appId, 'public', 'data', 'booking_patients'); 
        if (overwrite) { const s = await getDocs(colRef); for (const d of s.docs) await deleteDoc(d.ref); } 
        for (const b of bBooks) { await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'booking_patients', b.id || crypto.randomUUID()), b); } 
      }
      if (bNotes) { 
        const colRef = collection(db, 'artifacts', appId, 'public', 'data', 'info_notes'); 
        if (overwrite) { const s = await getDocs(colRef); for (const d of s.docs) await deleteDoc(d.ref); } 
        for (const n of bNotes) { await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'info_notes', n.id || crypto.randomUUID()), n); } 
      }
      if (bPjs) { for (const pj of bPjs) { await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'shift_pj', pj.id), pj); } }
      if (bTaskBooks) { for (const tb of bTaskBooks) { await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'task_books', tb.id), tb); } }
      if (bConds) { for (const day of bConds) { for (const docItem of day.docs) { await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'conds_' + day.date, docItem.patientId), docItem.data, { merge: true }); } } }
      setProgress(100); setImportData(null); showNotify("Restore Paket Sempurna");
    } catch(e) { showNotify("Gagal Restore", "error"); } finally { setProcessing(false); }
  };

  const handleResetToDefault = async () => {
    setProcessing(true); setProgress(0);
    try {
      const collectionsToWipe = ['patients', 'booking_patients', 'info_notes', 'wa_reports', 'shift_pj', 'task_books'];
      for (const collName of collectionsToWipe) {
        const snap = await getDocs(collection(db, 'artifacts', appId, 'public', 'data', collName));
        for (const d of snap.docs) await deleteDoc(d.ref);
      }
      const defaultMasters = {
        beds: DEFAULT_BEDS, unit_categories: DEFAULT_UNIT_CATEGORIES, locations: DEFAULT_LOCATIONS, jaminan: ['BPJS', 'UMUM', 'ASURANSI'], dpjp: DEFAULT_DPJP, raber: DEFAULT_RABER, oxygen: DEFAULT_OXYGEN, exitStatus: DEFAULT_EXIT_STATUS, nurses: DEFAULT_NURSES
      };
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'master_config', 'settings'), defaultMasters);
      setProgress(100); showNotify("Sistem Berhasil Direset ke Default"); setShowResetConfirm(false);
      setTimeout(() => window.location.reload(), 1000);
    } catch (e) { showNotify("Gagal Reset", "error"); } finally { setProcessing(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pb-10 text-center">
      <div className="bg-white p-10 rounded-[2rem] border border-slate-100 text-center shadow-sm">
        <div className="bg-blue-50 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-blue-100 shadow-inner text-center"><Download className="text-blue-600 mx-auto" size={40} /></div>
        <h5 className="text-md font-black uppercase text-center">Smart Export Full</h5>
        <p className="text-[10px] text-slate-400 mt-2 uppercase font-bold tracking-widest leading-relaxed text-center">Mencadangkan Pasien, Booking, Info Board, Buku Tugas & Laporan.</p>
        <button onClick={handleBackup} disabled={processing} className="w-full mt-8 py-4 bg-blue-600 text-white font-black rounded-2xl text-[10px] uppercase shadow-lg text-center">GENERATE SMART BACKUP</button>
      </div>
      <div className="bg-white p-10 rounded-[2rem] border border-slate-100 text-center shadow-sm">
        <div className="bg-emerald-50 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-emerald-100 shadow-inner text-center"><Upload className="text-emerald-600 mx-auto" size={40} /></div>
        <h5 className="text-md font-black uppercase text-center">Smart Restore Full</h5>
        <p className="text-[10px] text-slate-400 mt-2 uppercase font-bold tracking-widest leading-relaxed text-center">Mengembalikan Seluruh Paket Data Secara Utuh Melalui File.</p>
        <input type="file" accept=".pmb" className="hidden" ref={fileInputRef} onChange={(e) => { const file = e.target.files[0]; if(!file) return; const reader = new FileReader(); reader.onload = (ev) => { try { setImportData(unsecureData(ev.target.result)); } catch(err) { showNotify("Format File Tidak Valid", "error"); } }; reader.readAsText(file); }} />
        <button onClick={() => fileInputRef.current?.click()} className="w-full mt-8 py-4 bg-emerald-600 text-white font-black rounded-2xl text-[10px] uppercase shadow-lg text-center">UPLOAD FILE BACKUP</button>
      </div>
      <div className="bg-white p-10 rounded-[2rem] border border-slate-100 text-center shadow-sm">
        <div className="bg-red-50 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-red-100 shadow-inner text-center"><RotateCw className="text-red-600 mx-auto" size={40} /></div>
        <h5 className="text-md font-black uppercase text-center">Reset Sistem Default</h5>
        <p className="text-[10px] text-slate-400 mt-2 uppercase font-bold tracking-widest leading-relaxed text-center">Mereset semua data transaksi dan mengembalikan Master Data.</p>
        <button onClick={() => setShowResetConfirm(true)} disabled={processing} className="w-full mt-8 py-4 bg-red-600 text-white font-black rounded-2xl text-[10px] uppercase shadow-lg text-center">RESET KE PENGATURAN AWAL</button>
      </div>
      <AnimatePresence>{importData && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm text-center"><motion.div initial={{ y:20, opacity: 0 }} animate={{ y:0, opacity: 1 }} className="bg-white w-full max-w-sm rounded-[2rem] p-8 border border-slate-100 shadow-2xl text-center"><div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-emerald-100 shadow-inner text-center"><ShieldCheck className="text-emerald-500 mx-auto" size={32} /></div><h5 className="text-lg font-black text-slate-800 uppercase tracking-tight">Verifikasi Paket Restore</h5><p className="text-slate-400 text-[10px] mt-2 mb-8 uppercase font-bold tracking-widest">Data Paket Ditemukan: <br/><span className="text-blue-600">[{importData.patients?.length || 0}] Pasien, [{importData.bookings?.length || 0}] Booking</span></p><div className="flex flex-col gap-3 text-center"><button onClick={() => handleRestore(true)} className="w-full py-4 bg-red-600 text-white font-black rounded-xl text-[10px] uppercase shadow-md text-center">BERSIHKAN & TIMPA SEMUA</button><button onClick={() => handleRestore(false)} className="w-full py-4 bg-blue-600 text-white font-black rounded-xl text-[10px] uppercase shadow-md text-center">GABUNGKAN DATA</button><button onClick={() => setImportData(null)} className="w-full py-4 bg-slate-100 text-slate-600 font-black rounded-xl text-[10px] uppercase text-center">BATAL</button></div></motion.div></div>
      )}</AnimatePresence>
      <AnimatePresence>{showResetConfirm && (
        <SmartDeleteModal title="RESET KE DEFAULT?" description="Seluruh data pasien, laporan, dan pengaturan master akan dihapus permanen dan kembali ke awal. Tindakan ini tidak bisa dibatalkan." onConfirm={handleResetToDefault} onCancel={() => setShowResetConfirm(false)} />
      )}</AnimatePresence>
      {processing && (<div className="fixed inset-0 z-[600] bg-slate-900/80 backdrop-blur-md flex flex-col items-center justify-center text-white text-center"><motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full mb-6 shadow-[0_0_20px_rgba(59,130,246,0.5)] mx-auto text-center" /><h4 className="text-3xl font-black uppercase tracking-tighter animate-pulse text-center">PROSES PAKET {progress}%</h4></div>)}
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null); 
  const [loading, setLoading] = useState(true); 
  const [activeTab, setActiveTab] = useState('dashboard'); 
  const [isSidebarOpen, setSidebarOpen] = useState(window.innerWidth > 1024); 
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [patients, setPatients] = useState([]); 
  const [bookingPatients, setBookingPatients] = useState([]); 
  const [infoNotes, setInfoNotes] = useState([]); 
  const [notification, setNotification] = useState(null); 
  const [editPatient, setEditPatient] = useState(null); 
  const [editBooking, setEditBooking] = useState(null);
  const [pembagianContext, setPembagianContext] = useState(null);
  const [showReceiverModal, setShowReceiverModal] = useState(false); 
  const [tempReceiver, setTempReceiver] = useState(null);
  const [masters, setMasters] = useState({ 
    beds: DEFAULT_BEDS, unit_categories: DEFAULT_UNIT_CATEGORIES, locations: DEFAULT_LOCATIONS, jaminan: ['BPJS', 'UMUM', 'ASURANSI'], dpjp: DEFAULT_DPJP, raber: DEFAULT_RABER, oxygen: DEFAULT_OXYGEN, exitStatus: DEFAULT_EXIT_STATUS, nurses: DEFAULT_NURSES 
  });
  
  useEffect(() => {
    const script = document.createElement('script'); 
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"; 
    script.async = true; 
    document.head.appendChild(script);

    const pdfScript = document.createElement('script');
    pdfScript.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    pdfScript.async = true;
    document.head.appendChild(pdfScript);

    pdfScript.onload = () => {
      const autoTableScript = document.createElement('script');
      autoTableScript.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js";
      autoTableScript.async = true;
      document.head.appendChild(autoTableScript);
    };

    const initAuth = async () => {
      if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
        try { await signInWithCustomToken(auth, __initial_auth_token); } catch (e) {}
      }
      setLoading(false);
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, (usr) => {
      setUser(usr);
    });
    return () => { 
      unsubscribe(); 
      try { document.head.removeChild(script); } catch(e) {} 
    };
  }, []);

  useEffect(() => {
    if (!user) { 
      setPatients([]); setBookingPatients([]); setInfoNotes([]); 
      return; 
    }
    const unsubPatients = onSnapshot(query(collection(db, 'artifacts', appId, 'public', 'data', 'patients')), 
      (s) => setPatients(s.docs.map(d => ({ id: d.id, ...d.data() }))),
      (err) => { if (err.code !== 'permission-denied') console.error("Patients Sync Error:", err); }
    );
    const unsubBooking = onSnapshot(query(collection(db, 'artifacts', appId, 'public', 'data', 'booking_patients')), 
      (s) => setBookingPatients(s.docs.map(d => ({ id: d.id, ...d.data() }))),
      (err) => { if (err.code !== 'permission-denied') console.error("Booking Sync Error:", err); }
    );
    const unsubInfo = onSnapshot(query(collection(db, 'artifacts', appId, 'public', 'data', 'info_notes')), 
      (s) => setInfoNotes(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt?.localeCompare(a.createdAt))),
      (err) => { if (err.code !== 'permission-denied') console.error("Info Sync Error:", err); }
    );
    const unsubMasters = onSnapshot(doc(db, 'artifacts', appId, 'public', 'data', 'master_config', 'settings'), 
      (docSnap) => { 
        if (docSnap.exists()) { 
          const data = docSnap.data();
          if (data.beds && data.beds.some(b => typeof b === 'string' && b.includes('ICU '))) {
            data.beds = DEFAULT_BEDS;
            setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'master_config', 'settings'), data, { merge: true });
          }
          setMasters(prev => ({ ...prev, ...data })); 
        } 
        else { setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'master_config', 'settings'), masters); } 
      },
      (err) => { if (err.code !== 'permission-denied') console.error("Masters Sync Error:", err); }
    );
    return () => { 
      unsubPatients(); unsubBooking(); unsubMasters(); unsubInfo(); 
    };
  }, [user]);

  const handleTabChange = (tabId) => { 
    if (tabId === 'registrasiPasien' && !editPatient) { setShowReceiverModal(true); } 
    else { 
      if (tabId !== 'pembagianTugas') setPembagianContext(null);
      setActiveTab(tabId); 
    } 
  };

  const handleLogout = async () => {
    try {
      setPatients([]); setBookingPatients([]); setInfoNotes([]); setActiveTab('dashboard');
      await signOut(auth);
      showNotify("Berhasil Keluar");
    } catch (err) { showNotify("Gagal Keluar", "error"); }
  };

  const showNotify = (msg, type = 'success') => { setNotification({ msg, type }); setTimeout(() => setNotification(null), 4000); };
  const updateMaster = async (category, newList) => { if (!user) return; try { await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'master_config', 'settings'), { [category]: newList }); showNotify(`Master ${category} diperbarui`); } catch (e) { showNotify("Gagal memperbarui", "error"); } };
  const handleBookingToRegistration = (bookingData) => { 
    const prefilledData = { 
      nama: bookingData.nama || '', mr: bookingData.mr || '', asalPasien: bookingData.asalPasien || '', diagnosaMedis: bookingData.diagnosa || '', dpjp: bookingData.dokterMerawat || '', oksigenSaatIni: bookingData.oksigenSaatIni || '', raber: [], unit: bookingData.unit || '', intubasi: '', ekstubasi: '', agama: ''
    }; 
    setEditPatient(prefilledData); setShowReceiverModal(true); showNotify("Data Booking Diimpor"); 
  };
  const handleEditBukuTugas = (date, shift) => { setPembagianContext({ date, shift }); setActiveTab('pembagianTugas'); };

  if (loading) return (
    <div className="h-screen w-full flex flex-col items-center justify-center bg-slate-50 text-blue-600 gap-4 text-center">
      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} className="w-10 h-10 border-4 border-current border-t-transparent rounded-full mx-auto text-center" />
      <p className="text-[10px] font-black uppercase tracking-widest animate-pulse text-center">Menghubungkan ke Hub Cloud...</p>
    </div>
  );
  
  if (!user) return <LoginUI auth={auth} showNotify={showNotify} />;

  return (
    <div className="flex h-screen bg-[#F8FAFC] overflow-hidden font-sans text-center">
      <SidebarUI isOpen={isSidebarOpen} setOpen={setSidebarOpen} isCollapsed={isSidebarCollapsed} setCollapsed={setSidebarCollapsed} activeTab={activeTab} setActiveTab={handleTabChange} />
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden relative text-center">
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-10 shadow-sm text-center">
          <div className="flex items-center gap-3 text-center">
            <button onClick={() => setSidebarOpen(!isSidebarOpen)} className="p-1.5 hover:bg-slate-100 rounded-lg lg:hidden text-center text-center">
              <Menu size={18} className="text-slate-600" />
            </button>
            <h1 className="text-sm font-bold text-slate-800 capitalize flex items-center gap-2 uppercase text-center">
              <span className="w-1.5 h-6 bg-blue-600 rounded-full hidden sm:block"></span>
              {activeTab === 'registrasiPasien' && editPatient ? 'Edit Registrasi' : activeTab === 'bookingPasien' && editBooking ? 'Edit Booking' : activeTab.replace(/([A-Z])/g, ' $1').trim()}
            </h1>
          </div>
          <div className="flex items-center gap-4 text-center">
            <div className="hidden md:flex flex-col text-right leading-none text-right">
              <span className="text-[10px] font-black text-slate-700 truncate max-w-[120px] uppercase">{user?.email || 'User'}</span>
              <span className="text-[8px] text-blue-600 font-black uppercase tracking-widest mt-0.5">RSUD PASAR MINGGU</span>
            </div>
            <button onClick={handleLogout} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors text-center text-center">
              <LogOut size={18} />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-3 md:p-6 custom-scrollbar bg-[#F8FAFC] text-center">
          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }} transition={{ duration: 0.2 }}>
              {activeTab === 'dashboard' && <DashboardUI patients={patients} infoNotes={infoNotes} showNotify={showNotify} masters={masters} />}
              {activeTab === 'bookingPasien' && (<BookingFormUI showNotify={showNotify} editData={editBooking} masters={masters} onCancel={() => { setEditBooking(null); setActiveTab('dashboard'); }} onSaved={() => { setEditBooking(null); setActiveTab('dataBookingPasien'); }} onRedirectToReg={handleBookingToRegistration} patients={patients} bookingPatients={bookingPatients} />)}
              {activeTab === 'dataBookingPasien' && (<BookingListUI bookings={bookingPatients} patients={patients} showNotify={showNotify} onEdit={(b) => { setEditBooking(b); setActiveTab('bookingPasien'); }} onRegistrasi={handleBookingToRegistration} />)}
              {activeTab === 'registrasiPasien' && (<RegistrationFormUI showNotify={showNotify} patients={patients} bookingPatients={bookingPatients} editData={editPatient} masters={masters} receiverNurse={tempReceiver} onCancel={() => { setEditPatient(null); setTempReceiver(null); setActiveTab('dashboard'); }} onSaved={() => { setEditPatient(null); setTempReceiver(null); setActiveTab('dataRegisterPasien'); }} />)}
              {activeTab === 'dataRegisterPasien' && <PatientListUI patients={patients} masters={masters} showNotify={showNotify} onEdit={(p) => { setEditPatient(p); setActiveTab('registrasiPasien'); }} />}
              {activeTab === 'laporanPasien' && <LaporanPasienSection user={user} patients={patients} masters={masters} showNotify={showNotify} />}
              {activeTab === 'laporanWhatsApp' && <LaporanWhatsAppUI patients={patients} bookingPatients={bookingPatients} masters={masters} showNotify={showNotify} />}
              {activeTab === 'pembagianTugas' && <PembagianTugasUI patients={patients} masters={masters} showNotify={showNotify} user={user} initialContext={pembagianContext} onFinished={() => { setPembagianContext(null); setActiveTab('bukuTugas'); }} onExit={() => { setPembagianContext(null); setActiveTab('bukuTugas'); }} />}
              {activeTab === 'bukuTugas' && <BukuTugasUI patients={patients} masters={masters} showNotify={showNotify} onEditRequest={handleEditBukuTugas} />}
              {activeTab === 'smartBackupRestore' && <BackupRestore patients={patients} bookings={bookingPatients} infoNotes={infoNotes} masters={masters} showNotify={showNotify} />}
              {activeTab === 'pengaturanMaster' && <MasterDataManagement masters={masters} onUpdate={updateMaster} user={user} showNotify={showNotify} />}
            </motion.div>
          </AnimatePresence>
        </div>
        <AnimatePresence>{showReceiverModal && (<SmartReceiverModal nurses={masters.nurses} onCancel={() => { setShowReceiverModal(false); setEditPatient(null); }} onConfirm={(nurse) => { setTempReceiver(nurse); setShowReceiverModal(false); setActiveTab('registrasiPasien'); }} />)}</AnimatePresence>
        <AnimatePresence>{notification && (<motion.div initial={{ opacity: 0, x: 100 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 100 }} className={`fixed bottom-4 right-4 p-4 rounded-xl shadow-2xl flex items-start gap-3 z-[300] border max-w-sm ${notification.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}><div className="shrink-0">{notification.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}</div><div className="flex flex-col gap-1 text-left"><span className="text-xs font-black uppercase leading-none">{notification.type === 'success' ? 'Berhasil' : 'Sistem'}</span><p className="text-[10px] font-bold leading-tight uppercase text-left">{notification.msg}</p></div></motion.div>)}</AnimatePresence>
      </main>
    </div>
  );
}
